import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import mermaid from 'mermaid';
import { getAuth } from 'firebase/auth';
import {
    ArrowLeft, Bell, Bot, Check, FolderPlus, Send, ChevronDown, CircleHelp, Clapperboard, Contrast, Crop, Download, FileText, Film,
    FlipHorizontal2, Image as ImageIcon, LoaderCircle, Lock, MapPin, MessageSquare, Music, Palette,
    Pause, Pencil, Phone, Play, Plus, RefreshCw, SkipBack, Sparkles, Square, Sticker, Trash2,
    TriangleAlert, Type, Undo2, UserRound, X, ZoomIn,
} from 'lucide-react';
import { 
    CreateVideoWorkspace, 
    GetVideoWorkspaceList,
    GetVideoWorkspaceEditorList,
    UpdateVideoWorkspace, 
    DeleteVideoWorkspace,
    GetAnalysisById,
    GetVideoWorkspaceAssetUrl,
    ListVideoWorkspaceAssets,
    GenerateVideoWorkspaceAsset,
    UploadFile,
    GetProcessStatus,
    QueueAudioAlignment,
    GetAudioAlignment,
    SaveVideoWorkspaceTimeline,
    GetVideoWorkspaceTimeline,
    GetVideoWorkspaceOutputUrl,
    GetVideoWorkspaceOutputUploadUrl,
    FinalizeVideoWorkspaceVersion,
    FinalizeVideoWorkspaceUpload,
    AddUpdateVideoWorkspaceComment,
    GetVideoWorkspaceComments,
    DeleteVideoWorkspaceComment,
    QueueVideoWorkspaceReview,
    GetVideoWorkspaceReviewStatus,
    createVideoWorkspaceStreamingConnection,
    createVideoWorkspaceAIConnection,
    GetVideoGeneration
} from '../../Client/ManagementServiceClient';
import { formatDateTimeFromTicks } from '../../utils/formatters';
import { useAuth } from '../../hooks/useAuth';
import { useWallet } from '../../hooks/useWallet';
import StructuredAnalysisView from '../VideoAnalysis/AnalysisDetails/StructuredAnalysisView';
import { GenerationStudio } from '../VideoGeneration';
import { GenerationFork } from '../VideoGeneration/Fork';
import TextBlockEditor from './EffectBlocks/TextBlockEditor';
import BoxBlockEditor from './EffectBlocks/BoxBlockEditor';
import CropBlockEditor from './EffectBlocks/CropBlockEditor';
import ZoomBlockEditor from './EffectBlocks/ZoomBlockEditor';
import StickerBlockEditor from './EffectBlocks/StickerBlockEditor';
import OverlayBlockEditor from './EffectBlocks/OverlayBlockEditor';
import AssetUploadStatus, { useUploads, describeUpload } from './AssetUploadStatus';
import { startUpload, dismissUpload, cancelUpload, uploadKey, UPLOAD_UPLOADED, UPLOAD_ERROR, UPLOAD_CANCELLED } from './uploadManager';
import FadeBlockEditor from './EffectBlocks/FadeBlockEditor';
import FlipBlockEditor from './EffectBlocks/FlipBlockEditor';
import LookBlockEditor from './EffectBlocks/LookBlockEditor';
import SuggestPanel from './EffectBlocks/SuggestPanel';
import {
    Editor,
    buildScriptFromTimeline,
    createManualTimelineGraph,
    migrateGraph,
    deepClone,
    isVideoAssetType,
    isAudioAssetType,
    isImageAssetType,
    getAssetLayerKind,
    setAssetDragPayload,
    readAssetDragPayload,
    hasAssetDragOfKind,
    hasFileDrag,
    getLayerKind,
    getLayerByKind,
    formatSeconds,
    toSafeNumber,
    sanitizeStreamName,
} from './editor';
import { FloatingChatDock } from '../FloatingChat';
import './styles.css';
import './VideoWorkspace.css';
import './ai-chat.css';

// Supported file formats constants
const SUPPORTED_FORMATS = {
    VIDEO: ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v', '.3gp', '.ogv'],
    AUDIO: ['.mp3', '.wav', '.aac', '.ogg', '.wma', '.flac', '.m4a', '.opus', '.aiff', '.au'],
    IMAGE: ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp', '.svg', '.ico', '.heic', '.heif'],
    DOCUMENT: ['.pdf', '.doc', '.docx', '.txt', '.rtf']
};

const ALL_SUPPORTED_FORMATS = [
    ...SUPPORTED_FORMATS.VIDEO,
    ...SUPPORTED_FORMATS.AUDIO,
    ...SUPPORTED_FORMATS.IMAGE,
    ...SUPPORTED_FORMATS.DOCUMENT
].join(',');

const SUPPORTED_FORMATS_TEXT = {
    VIDEO: 'MP4, AVI, MOV, WMV, FLV, WebM, MKV, M4V, 3GP, OGV',
    AUDIO: 'MP3, WAV, AAC, OGG, WMA, FLAC, M4A, OPUS, AIFF, AU',
    IMAGE: 'JPG, PNG, GIF, BMP, TIFF, WebP, SVG, ICO, HEIC, HEIF',
    DOCUMENT: 'PDF, DOC, DOCX, TXT, RTF',
    ALL: 'Video: MP4, AVI, MOV, WMV, FLV, WebM, MKV, M4V, 3GP, OGV | Audio: MP3, WAV, AAC, OGG, WMA, FLAC, M4A, OPUS, AIFF, AU | Images: JPG, PNG, GIF, BMP, TIFF, WebP, SVG, ICO, HEIC, HEIF | Documents: PDF, DOC, DOCX, TXT, RTF'
};

// AI editing is on by default everywhere (backend planner runs Claude Opus);
// REACT_APP_DISABLE_AI_AGENT=true is the build-time kill switch.
const IS_AI_AGENT_ENABLED =
    String(process.env.REACT_APP_DISABLE_AI_AGENT || '').toLowerCase() !== 'true';

const MAX_AI_RECONNECT_ATTEMPTS = 5;

/* 1000 is a normal close and 1008/4xxx are the server's policy refusals. Only
   the genuinely transient closes -- a dropped network, an instance recycling --
   are worth another socket. */
const isRetryableAiCloseCode = (code) =>
    code !== 1000 && code !== 1008 && !(code >= 4000 && code <= 4999);

let isMermaidInitialized = false;

const getPlanSteps = (plan) => {
    if (!plan || typeof plan !== 'object') return [];
    const steps = plan.video_edit_steps || plan.steps || [];
    return Array.isArray(steps) ? steps : [];
};

const getPlanInputBindings = (plan) => {
    if (!plan || typeof plan !== 'object') return [];
    const bindings = plan.input_bindings || plan.inputs || [];
    return Array.isArray(bindings) ? bindings : [];
};

// Angle brackets are stripped as well as quotes: mermaid is initialized with
// securityLevel 'strict', but the rendered svg is still handed to innerHTML, so
// label text must not be able to carry markup on its own.
const sanitizeMermaidText = (value) => String(value || '')
    .replace(/[<>]/g, '')
    .replace(/"/g, '\\"')
    .replace(/[\r\n]+/g, ' ')
    .trim();

const normalizeStreamValue = (value) => {
    if (typeof value === 'string') {
        const stream = value.trim();
        return stream ? [stream] : [];
    }
    if (Array.isArray(value)) {
        return value
            .filter(item => typeof item === 'string')
            .map(item => item.trim())
            .filter(Boolean);
    }
    return [];
};

const extractStepInputRefs = (step) => {
    const refs = [];
    if (!step || typeof step !== 'object' || !step.inputs || typeof step.inputs !== 'object') {
        return refs;
    }

    Object.entries(step.inputs).forEach(([inputKey, inputValue]) => {
        const streams = normalizeStreamValue(inputValue);
        streams.forEach((stream, index) => {
            refs.push({
                stream,
                inputKey,
                order: index + 1,
                isOrderedList: Array.isArray(inputValue)
            });
        });
    });

    return refs;
};

const getStepOutputStreams = (step) => {
    if (!step || typeof step !== 'object' || !step.outputs || typeof step.outputs !== 'object') {
        return [];
    }

    const outputStreams = [];
    Object.values(step.outputs).forEach((value) => {
        normalizeStreamValue(value).forEach(stream => outputStreams.push(stream));
    });
    return outputStreams;
};

const collectExistingStreams = (plan) => {
    const streams = new Set();
    const bindings = getPlanInputBindings(plan);
    bindings.forEach((binding) => {
        const stream = String(binding?.stream_name || binding?.stream || '').trim();
        if (stream) streams.add(stream);
    });

    getPlanSteps(plan).forEach((step) => {
        extractStepInputRefs(step).forEach(ref => streams.add(ref.stream));
        getStepOutputStreams(step).forEach(stream => streams.add(stream));
    });

    return streams;
};

const createUniqueStreamName = (baseName, existingStreams) => {
    const safeBase = sanitizeStreamName(baseName) || 'stream';
    if (!existingStreams.has(safeBase)) {
        existingStreams.add(safeBase);
        return safeBase;
    }

    let index = 2;
    while (existingStreams.has(`${safeBase}_${index}`)) {
        index += 1;
    }
    const nextName = `${safeBase}_${index}`;
    existingStreams.add(nextName);
    return nextName;
};

const buildEditedPlanFromTimeline = (basePlan, editedGraph, availableAssets = []) => {
    if (!basePlan || typeof basePlan !== 'object') return basePlan;

    const nextPlan = deepClone(basePlan) || {};
    const nextSteps = getPlanSteps(nextPlan).map(step => ({ ...step }));
    const nextBindings = getPlanInputBindings(nextPlan).map(binding => ({ ...binding }));
    const existingStreams = collectExistingStreams(nextPlan);

    const videoLayer = getLayerByKind(editedGraph, 'video', 'layer 1');
    const audioLayer = getLayerByKind(editedGraph, 'audio', 'layer 2');
    const effectsLayer = getLayerByKind(editedGraph, 'effects', 'layer 0');

    const indexAssetByFileName = new Map();
    (Array.isArray(availableAssets) ? availableAssets : []).forEach((asset) => {
        if (asset?.FileName) indexAssetByFileName.set(String(asset.FileName), asset);
    });

    const ensureBinding = (item, preferredKind) => {
        const explicitStream = String(item?.stream || '').trim();
        if (explicitStream) {
            const existingBinding = nextBindings.find(binding => String(binding?.stream_name || binding?.stream || '') === explicitStream);
            if (!existingBinding) {
                const filename = String(item?.asset_filename || item?.filename || item?.label || `generated_${preferredKind}`);
                const duration = toSafeNumber(item?.duration);
                const payload = { filename, stream_name: explicitStream };
                if (duration !== null && duration > 0) payload.t = duration;
                nextBindings.push(payload);
            }
            existingStreams.add(explicitStream);
            return explicitStream;
        }

        const fileName = String(item?.asset_filename || item?.filename || '').trim();
        const existingBinding = nextBindings.find(binding => String(binding?.filename || '').trim() === fileName);
        if (existingBinding) {
            const stream = String(existingBinding?.stream_name || existingBinding?.stream || '').trim();
            if (stream) {
                existingStreams.add(stream);
                return stream;
            }
        }

        const asset = indexAssetByFileName.get(fileName);
        const base = asset?.FileAlias || fileName.replace(/\.[^.]+$/, '') || `${preferredKind}_clip`;
        const streamName = createUniqueStreamName(`${base}_${preferredKind}`, existingStreams);
        const duration = toSafeNumber(item?.duration);
        const payload = { filename: fileName || `${base}.mp4`, stream_name: streamName };
        if (duration !== null && duration > 0) payload.t = duration;
        nextBindings.push(payload);
        return streamName;
    };

    const videoItems = Array.isArray(videoLayer?.items) ? videoLayer.items : [];
    const videoStreams = videoItems
        .map(item => ensureBinding(item, 'video'))
        .filter(Boolean);

    const audioItems = Array.isArray(audioLayer?.items) ? audioLayer.items : [];
    const audioStreams = audioItems
        .map(item => ensureBinding(item, 'audio'))
        .filter(Boolean);

    const concatStep = nextSteps.find(step => step?.tool_name === 'concat_tool');
    if (concatStep && videoStreams.length >= 2) {
        concatStep.inputs = { ...(concatStep.inputs || {}), stream_names: videoStreams };
        concatStep.parameters = { ...(concatStep.parameters || {}), stream_names: videoStreams };
    }

    const amixStep = nextSteps.find(step => step?.tool_name === 'amix_tool');
    if (amixStep && audioStreams.length >= 2) {
        amixStep.inputs = { ...(amixStep.inputs || {}), input_streams: audioStreams };
        amixStep.parameters = { ...(amixStep.parameters || {}), input_streams: audioStreams };
        const amixOutputStream = String(amixStep?.outputs?.output_stream || '').trim();
        if (amixOutputStream) {
            nextPlan.planned_final_audio_stream = amixOutputStream;
        }
    } else if (audioStreams.length === 1) {
        nextPlan.planned_final_audio_stream = audioStreams[0];
    }

    const effectItems = Array.isArray(effectsLayer?.items) ? effectsLayer.items : [];
    const effectByStepId = new Map();
    effectItems.forEach((item) => {
        const stepId = String(item?.step_id || '').trim();
        if (stepId) {
            effectByStepId.set(stepId, item);
        }
    });

    nextSteps.forEach((step) => {
        const stepId = String(step?.step_id || '').trim();
        if (!stepId || !effectByStepId.has(stepId)) return;

        const item = effectByStepId.get(stepId);
        const start = toSafeNumber(item?.start);
        const end = toSafeNumber(item?.end);
        const duration = toSafeNumber(item?.duration);

        step.parameters = { ...(step.parameters || {}) };
        if (start !== null) step.parameters.start = start;
        if (end !== null) step.parameters.end = end;
        if (duration !== null) step.parameters.duration = duration;

        if (step?.tool_name === 'drawtext_tool') {
            const text = String(item?.text_content || '').trim();
            const textX = String(item?.text_x ?? '').trim();
            const textY = String(item?.text_y ?? '').trim();

            if (text) {
                step.parameters.text = text;
            }
            if (textX) {
                step.parameters.x = textX;
            }
            if (textY) {
                step.parameters.y = textY;
            }
        }
    });

    nextPlan.input_bindings = nextBindings;
    nextPlan.video_edit_steps = nextSteps;
    if (Array.isArray(nextPlan.steps)) {
        nextPlan.steps = nextSteps;
    }

    return nextPlan;
};

const buildPlanGraphDefinition = (plan) => {
    const steps = getPlanSteps(plan);
    const inputBindings = getPlanInputBindings(plan);
    if (steps.length === 0 && inputBindings.length === 0) return '';

    const lines = ['flowchart TD'];
    const stepNodeMap = new Map();
    const streamProducerMap = new Map();
    const fallbackAssetNode = 'asset_unknown';

    if (inputBindings.length > 0) {
        inputBindings.forEach((binding, index) => {
            const nodeId = `asset_${index + 1}`;
            const filename = binding?.filename || binding?.asset_name || binding?.name || `asset_${index + 1}`;
            const streamName = binding?.stream_name || binding?.stream || '';
            const label = streamName
                ? sanitizeMermaidText(`Asset ${index + 1}: ${filename} [${streamName}]`)
                : sanitizeMermaidText(`Asset ${index + 1}: ${filename}`);

            lines.push(`${nodeId}["${label}"]`);

            if (streamName) {
                streamProducerMap.set(String(streamName), nodeId);
            }
        });
    } else {
        lines.push(`${fallbackAssetNode}["Input Assets"]`);
    }

    steps.forEach((step, index) => {
        const nodeId = `step_${index + 1}`;
        const stepId = step?.step_id != null ? String(step.step_id) : `${index + 1}`;
        const rawTool = step?.tool_name || step?.step_type || 'operation';
        const tool = String(rawTool).replace(/_tool$/i, '').replace(/_/g, ' ');
        const label = sanitizeMermaidText(`${stepId}: ${tool}`);
        lines.push(`${nodeId}["${label}"]`);
        stepNodeMap.set(stepId, nodeId);

        const outputStreams = getStepOutputStreams(step);
        outputStreams.forEach((stream) => {
            streamProducerMap.set(String(stream), nodeId);
        });
    });

    steps.forEach((step, index) => {
        const targetNode = `step_${index + 1}`;
        const inputRefs = extractStepInputRefs(step);
        const isConcatStep = String(step?.tool_name || '').toLowerCase().includes('concat');
        let connectedFromInputs = false;

        inputRefs.forEach((ref) => {
            const sourceNode = streamProducerMap.get(String(ref.stream));
            if (!sourceNode) return;

            connectedFromInputs = true;
            if (isConcatStep && ref.isOrderedList) {
                lines.push(`${sourceNode} -->|${ref.order}| ${targetNode}`);
            } else {
                lines.push(`${sourceNode} --> ${targetNode}`);
            }
        });

        const dependencies = Array.isArray(step?.dependencies) ? step.dependencies : [];
        dependencies
            .map(dep => stepNodeMap.get(String(dep)))
            .filter(Boolean)
            .forEach((sourceNode) => {
                lines.push(`${sourceNode} -.-> ${targetNode}`);
            });

        if (!connectedFromInputs) {
            if (index === 0) {
                lines.push(`${fallbackAssetNode} --> ${targetNode}`);
            } else {
                lines.push(`step_${index} --> ${targetNode}`);
            }
        }
    });

    const finalOutput = plan?.planned_final_output_stream || plan?.final_output_stream || 'output';
    const finalNodeLabel = sanitizeMermaidText(`Final Output: ${finalOutput}`);
    lines.push(`final_output["${finalNodeLabel}"]`);

    const finalProducer = streamProducerMap.get(String(finalOutput));
    if (finalProducer) {
        lines.push(`${finalProducer} --> final_output`);
    } else if (steps.length > 0) {
        lines.push(`step_${steps.length} --> final_output`);
    } else {
        lines.push(`${fallbackAssetNode} --> final_output`);
    }

    return lines.join('\n');
};

const PlanGraph = ({ plan, messageId }) => {
    const graphRef = useRef(null);
    const [renderError, setRenderError] = useState('');
    const graphDefinition = useMemo(() => buildPlanGraphDefinition(plan), [plan]);

    useEffect(() => {
        let isCancelled = false;

        const renderGraph = async () => {
            if (!graphRef.current || !graphDefinition) return;

            try {
                if (!isMermaidInitialized) {
                    // 'strict' keeps mermaid's own sanitizer in the path and
                    // renders labels as text rather than raw HTML. The rendered
                    // svg is assigned via innerHTML below, so a label carrying
                    // markup would otherwise execute -- and labels are built
                    // from plan data (asset names, tool names) that originates
                    // outside this component.
                    mermaid.initialize({
                        startOnLoad: false,
                        securityLevel: 'strict',
                        theme: 'neutral'
                    });
                    isMermaidInitialized = true;
                }

                const graphId = `video-plan-${String(messageId).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                const { svg } = await mermaid.render(graphId, graphDefinition);

                if (!isCancelled && graphRef.current) {
                    graphRef.current.innerHTML = svg;
                    setRenderError('');
                }
            } catch (error) {
                if (!isCancelled) {
                    setRenderError('Unable to render plan graph.');
                }
            }
        };

        renderGraph();

        return () => {
            isCancelled = true;
        };
    }, [graphDefinition, messageId]);

    if (!graphDefinition) return null;

    return (
        <div className="plan-graph-wrapper">
            <div className="plan-graph-title">Planned edit</div>
            {renderError && <div className="plan-graph-error">{renderError}</div>}
            <div className="plan-graph" ref={graphRef} />
        </div>
    );
};

/*
 * Approximate in-browser preview of a plan timeline.
 * Plays the source assets back-to-back following the video/audio layer timing
 * and overlays planned text effects. It does not apply filters (fades, crops,
 * etc.) — those only exist in the final render.
 */
const PlanPreviewPlayer = ({ layers, totalDuration, availableAssets, resolveAssetUrl, onPlayheadChange }) => {
    const videoRef = useRef(null);
    const audioRef = useRef(null);
    const animationRef = useRef(null);
    const clockRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playhead, setPlayhead] = useState(0);
    const [assetUrls, setAssetUrls] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [previewError, setPreviewError] = useState('');

    const findLayer = (kind, fallbackName) => (Array.isArray(layers) ? layers : []).find(layer =>
        String(layer?.kind || '').toLowerCase() === kind ||
        String(layer?.name || '').toLowerCase() === fallbackName
    );

    const videoLayer = findLayer('video', 'layer 1');
    const audioLayer = findLayer('audio', 'layer 2');
    const effectsLayer = findLayer('effects', 'layer 0');

    const assetByFileName = useMemo(() => {
        const map = new Map();
        (Array.isArray(availableAssets) ? availableAssets : []).forEach((asset) => {
            if (asset?.FileName) map.set(asset.FileName, asset);
        });
        return map;
    }, [availableAssets]);

    const resolveClipAsset = useCallback((item) => {
        if (!item) return null;
        if (item.asset_filename && assetByFileName.has(item.asset_filename)) {
            return assetByFileName.get(item.asset_filename);
        }
        // Backend labels clips as "<filename> (<stream>)"; fall back to matching that.
        const label = String(item.label || '');
        for (const [fileName, asset] of assetByFileName.entries()) {
            if (label === fileName || label.startsWith(`${fileName} (`)) return asset;
        }
        return null;
    }, [assetByFileName]);

    const collectClips = useCallback((layer) => (
        (layer?.normalizedItems || [])
            .map(item => ({ item, asset: resolveClipAsset(item) }))
            .filter(entry => (entry.item._duration ?? 0) > 0)
            .sort((a, b) => (a.item._start || 0) - (b.item._start || 0))
    ), [resolveClipAsset]);

    const videoClips = useMemo(() => collectClips(videoLayer), [collectClips, videoLayer]);
    const audioClips = useMemo(() => collectClips(audioLayer), [collectClips, audioLayer]);

    const textOverlays = useMemo(() => (
        (effectsLayer?.normalizedItems || []).filter(item =>
            String(item?.tool_name || '').toLowerCase() === 'drawtext_tool'
            && String(item?.text_content || '').trim()
        )
    ), [effectsLayer]);

    const activeVideo = videoClips.find(({ item }) => playhead >= item._start && playhead < item._end)
        || (playhead >= totalDuration ? videoClips[videoClips.length - 1] : null);
    const activeAudio = audioClips.find(({ item }) => playhead >= item._start && playhead < item._end) || null;
    const activeTexts = textOverlays.filter((item) => {
        const start = item._start ?? 0;
        const end = item._end ?? start;
        if (Math.abs(end - start) < 1e-6) return playhead >= start;
        return playhead >= start && playhead < end;
    });

    const stopPlayback = useCallback(() => {
        setIsPlaying(false);
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
        clockRef.current = null;
        if (videoRef.current) videoRef.current.pause();
        if (audioRef.current) audioRef.current.pause();
    }, []);

    useEffect(() => () => stopPlayback(), [stopPlayback]);

    useEffect(() => {
        if (onPlayheadChange) onPlayheadChange(assetUrls ? playhead : null);
    }, [playhead, assetUrls, onPlayheadChange]);

    useEffect(() => {
        if (!isPlaying) return undefined;
        const startPlayhead = playhead >= totalDuration ? 0 : playhead;
        clockRef.current = { startedAt: performance.now(), startPlayhead };

        const tick = () => {
            if (!clockRef.current) return;
            const elapsed = (performance.now() - clockRef.current.startedAt) / 1000;
            const nextPlayhead = clockRef.current.startPlayhead + elapsed;
            if (nextPlayhead >= totalDuration) {
                setPlayhead(totalDuration);
                stopPlayback();
                return;
            }
            setPlayhead(nextPlayhead);
            animationRef.current = requestAnimationFrame(tick);
        };
        animationRef.current = requestAnimationFrame(tick);
        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlaying]);

    // Keep the media elements aligned with the playhead
    useEffect(() => {
        if (!assetUrls) return;

        const videoElement = videoRef.current;
        const isVideoClip = activeVideo && isVideoAssetType(activeVideo.asset?.FileType);
        if (videoElement) {
            if (isVideoClip) {
                const url = assetUrls[activeVideo.asset.FileName];
                if (url && videoElement.dataset.previewSrc !== url) {
                    videoElement.dataset.previewSrc = url;
                    videoElement.src = url;
                }
                const offset = Math.max(0, playhead - activeVideo.item._start);
                if (Math.abs((videoElement.currentTime || 0) - offset) > 0.4) {
                    try { videoElement.currentTime = offset; } catch { /* seek not ready yet */ }
                }
                if (isPlaying && videoElement.paused) videoElement.play().catch(() => {});
                if (!isPlaying && !videoElement.paused) videoElement.pause();
            } else if (!videoElement.paused) {
                videoElement.pause();
            }
        }

        const audioElement = audioRef.current;
        if (audioElement) {
            if (activeAudio && assetUrls[activeAudio.asset?.FileName]) {
                const url = assetUrls[activeAudio.asset.FileName];
                if (audioElement.dataset.previewSrc !== url) {
                    audioElement.dataset.previewSrc = url;
                    audioElement.src = url;
                }
                const offset = Math.max(0, playhead - activeAudio.item._start);
                if (Math.abs((audioElement.currentTime || 0) - offset) > 0.4) {
                    try { audioElement.currentTime = offset; } catch { /* seek not ready yet */ }
                }
                if (isPlaying && audioElement.paused) audioElement.play().catch(() => {});
                if (!isPlaying && !audioElement.paused) audioElement.pause();
            } else if (!audioElement.paused) {
                audioElement.pause();
            }
        }
    }, [assetUrls, activeVideo, activeAudio, isPlaying, playhead]);

    const loadPreview = async () => {
        setIsLoading(true);
        setPreviewError('');
        try {
            const fileNames = new Set();
            [...videoClips, ...audioClips].forEach(({ asset }) => {
                if (asset?.FileName) fileNames.add(asset.FileName);
            });
            if (fileNames.size === 0) {
                setPreviewError('No previewable clips found in this plan.');
                return;
            }
            const urls = {};
            for (const fileName of fileNames) {
                const url = await resolveAssetUrl(fileName);
                if (url) urls[fileName] = url;
            }
            if (Object.keys(urls).length === 0) {
                setPreviewError('Could not load preview media for this plan.');
                return;
            }
            setAssetUrls(urls);
            setPlayhead(0);
        } catch (error) {
            setPreviewError(`Failed to load preview: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSeek = (event) => {
        const nextValue = Number(event.target.value);
        setPlayhead(nextValue);
        if (clockRef.current) {
            clockRef.current = { startedAt: performance.now(), startPlayhead: nextValue };
        }
    };

    if ((videoClips.length === 0 && audioClips.length === 0) || typeof resolveAssetUrl !== 'function') {
        return null;
    }

    const activeImageUrl = activeVideo && isImageAssetType(activeVideo.asset?.FileType) && assetUrls
        ? assetUrls[activeVideo.asset.FileName]
        : null;
    const showVideoElement = Boolean(activeVideo && isVideoAssetType(activeVideo.asset?.FileType));

    return (
        <div className="plan-preview-player">
            <div className="plan-preview-toolbar">
                {!assetUrls ? (
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={loadPreview}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Loading preview…' : <><Film size={16} aria-hidden="true" /> Preview plan</>}
                    </button>
                ) : (
                    <>
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => (isPlaying ? stopPlayback() : setIsPlaying(true))}
                        >
                            {isPlaying ? <><Pause size={16} aria-hidden="true" /> Pause</> : <><Play size={16} aria-hidden="true" /> Play</>}
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => { stopPlayback(); setPlayhead(0); }}
                        >
                            <SkipBack size={16} aria-hidden="true" /> Restart
                        </button>
                        <span className="plan-preview-time">
                            {formatSeconds(playhead)} / {formatSeconds(totalDuration)}
                        </span>
                        <span className="plan-preview-note">
                            Approximate preview — fades, crops and other effects appear only in the final render.
                        </span>
                    </>
                )}
                {previewError && <span className="plan-preview-error">{previewError}</span>}
            </div>

            {assetUrls && (
                <>
                    <div className="plan-preview-stage">
                        <video
                            ref={videoRef}
                            muted={audioClips.length > 0}
                            playsInline
                            preload="auto"
                            className="plan-preview-video"
                            style={{ display: showVideoElement ? 'block' : 'none' }}
                        />
                        {activeImageUrl && (
                            <img className="plan-preview-image" src={activeImageUrl} alt="Planned clip" />
                        )}
                        {!activeVideo && (
                            <div className="plan-preview-blank">No clip at this point in the plan</div>
                        )}
                        {activeTexts.map((textItem, index) => (
                            <div key={`overlay-${index}`} className="plan-preview-text-overlay">
                                {textItem.text_content}
                            </div>
                        ))}
                        <audio ref={audioRef} preload="auto" />
                    </div>
                    <input
                        type="range"
                        className="plan-preview-scrubber"
                        min={0}
                        max={totalDuration}
                        step={0.05}
                        value={Math.min(playhead, totalDuration)}
                        onChange={handleSeek}
                    />
                </>
            )}
        </div>
    );
};

const LayeredPlanGraph = ({ graph, editable = false, availableAssets = [], onGraphChange, resolveAssetUrl }) => {
    const [editableGraph, setEditableGraph] = useState(() => deepClone(graph));
    const [layerAssetSelection, setLayerAssetSelection] = useState({});
    const [selectedTextEffect, setSelectedTextEffect] = useState({});
    const [trimState, setTrimState] = useState(null);
    const [previewPlayhead, setPreviewPlayhead] = useState(null);
    const [assetDropTarget, setAssetDropTarget] = useState(null);
    const dragStateRef = useRef(null);

    useEffect(() => {
        setEditableGraph(deepClone(graph));
    }, [graph]);

    const formatTime = (value) => {
        if (value === undefined || value === null || Number.isNaN(Number(value))) return '';
        const numeric = Number(value);
        if (Math.abs(numeric - Math.round(numeric)) < 1e-6) return `${Math.round(numeric)}s`;
        return `${numeric.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}s`;
    };

    const toNumber = (value) => {
        if (value === undefined || value === null || value === '') return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const updateGraph = useCallback((mutator) => {
        if (!editable) return;
        setEditableGraph((prev) => {
            const next = deepClone(prev) || { format: 'layered_timeline_v1', layers: [] };
            mutator(next);
            if (onGraphChange) {
                onGraphChange(next);
            }
            return next;
        });
    }, [editable, onGraphChange]);

    useEffect(() => {
        if (!trimState) return undefined;

        const minDuration = 0.5;

        const handleMouseMove = (event) => {
            const deltaPx = event.clientX - trimState.startX;
            const deltaSeconds = (deltaPx / Math.max(1, trimState.trackWidth)) * trimState.totalDuration;

            updateGraph((nextGraph) => {
                const targetLayer = Array.isArray(nextGraph.layers) ? nextGraph.layers[trimState.layerIndex] : null;
                if (!targetLayer || !Array.isArray(targetLayer.items)) return;
                const item = targetLayer.items[trimState.itemIndex];
                if (!item) return;

                const originalStart = trimState.initialStart;
                const originalEnd = trimState.initialEnd;
                let nextStart = originalStart;
                let nextEnd = originalEnd;

                if (trimState.edge === 'left') {
                    nextStart = Math.max(0, Math.min(originalEnd - minDuration, originalStart + deltaSeconds));
                } else {
                    nextEnd = Math.max(originalStart + minDuration, originalEnd + deltaSeconds);
                }

                item.start = nextStart;
                item.end = nextEnd;
                item.duration = Math.max(minDuration, nextEnd - nextStart);
                // Distinguishes "the user shortened this clip" from "this clip
                // sits later on the track", which the script emitter needs and
                // start/end alone cannot express.
                item.trimmed = true;
            });
        };

        const handleMouseUp = () => {
            setTrimState(null);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [trimState, updateGraph]);

    const activeGraph = editable ? editableGraph : graph;
    if (!activeGraph || typeof activeGraph !== 'object') return null;

    const totalDurationFromGraph = toNumber(activeGraph.total_duration);
    const layers = Array.isArray(activeGraph?.layers) ? activeGraph.layers : [];
    const hasItems = layers.some(layer => Array.isArray(layer?.items) && layer.items.length > 0);
    // An empty read-only graph is noise, but an empty editable one is where a
    // manual edit starts — it still has to render its "add a clip" toolbars.
    if (!hasItems && !editable) return null;

    const normalizeLayerItems = (items) => {
        if (!Array.isArray(items) || items.length === 0) return [];

        const prepared = items.map((item, index) => {
            const start = toNumber(item?.start);
            const end = toNumber(item?.end);
            const duration = toNumber(item?.duration);
            return {
                ...item,
                _index: index,
                _start: start,
                _end: end,
                _duration: duration
            };
        });

        const hasTimelineData = prepared.some(item => item._start !== null || item._end !== null || item._duration !== null);
        if (hasTimelineData) {
            return prepared.map((item) => {
                const start = item._start ?? 0;
                const duration = item._duration ?? (item._end !== null ? Math.max(0, item._end - start) : 1);
                const end = item._end ?? (start + duration);
                return {
                    ...item,
                    _start: start,
                    _duration: duration,
                    _end: end
                };
            });
        }

        let cursor = 0;
        return prepared.map((item) => {
            const start = cursor;
            const duration = 1;
            const end = start + duration;
            cursor = end;
            return {
                ...item,
                _start: start,
                _duration: duration,
                _end: end
            };
        });
    };

    const normalizedLayers = layers.map(layer => ({
        ...layer,
        normalizedItems: normalizeLayerItems(Array.isArray(layer?.items) ? layer.items : [])
    }));

    const computedTotalDuration = normalizedLayers.reduce((maxDuration, layer) => {
        const layerEnd = layer.normalizedItems.reduce((maxEnd, item) => Math.max(maxEnd, item._end ?? 0), 0);
        return Math.max(maxDuration, layerEnd);
    }, 0);

    const totalDuration = Math.max(totalDurationFromGraph || 0, computedTotalDuration || 0, 1);

    const buildTicks = (duration) => {
        if (duration <= 10) return 1;
        if (duration <= 60) return 5;
        if (duration <= 180) return 10;
        return 30;
    };

    const tickStep = buildTicks(totalDuration);
    const rulerTicks = [];
    for (let tick = 0; tick <= totalDuration + 0.0001; tick += tickStep) {
        const position = (tick / totalDuration) * 100;
        rulerTicks.push({ tick, position });
    }

    const formatClipLabel = (item) => {
        const label = item?.label || 'Step';
        const start = formatTime(item?._start);
        const end = formatTime(item?._end);
        if (start && end) return `${label} (${start}→${end})`;
        return label;
    };

    const effectLayer = normalizedLayers.find(layer =>
        String(layer?.kind || '').toLowerCase() === 'effects' ||
        String(layer?.name || '').toLowerCase() === 'layer 0'
    );
    const effectItems = Array.isArray(effectLayer?.normalizedItems) ? effectLayer.normalizedItems : [];

    const getEffectsForClip = (clipItem) => {
        const clipStart = clipItem?._start ?? 0;
        const clipEnd = clipItem?._end ?? clipStart;

        return effectItems.filter((effectItem) => {
            const effectStart = effectItem?._start ?? clipStart;
            const effectEndRaw = effectItem?._end;
            const effectDuration = effectItem?._duration;
            const effectEnd = effectEndRaw ?? (
                effectDuration !== null && effectDuration !== undefined
                    ? effectStart + effectDuration
                    : effectStart
            );

            const isPointEffect = Math.abs((effectEnd ?? 0) - (effectStart ?? 0)) < 1e-6;
            if (isPointEffect) {
                return effectStart >= clipStart && effectStart <= clipEnd;
            }
            return effectStart < clipEnd && effectEnd > clipStart;
        });
    };

    const getDurationHintForAsset = (asset, layerKind) => {
        const duration = toNumber(asset?.Duration || asset?.duration || asset?.T || asset?.t);
        if (duration !== null && duration > 0) return duration;
        return layerKind === 'audio' ? 8 : 4;
    };

    const getAssetOptionsForLayer = (layerKind) => {
        const options = Array.isArray(availableAssets) ? availableAssets : [];
        if (layerKind === 'video') return options.filter(asset => isVideoAssetType(asset?.FileType));
        if (layerKind === 'audio') return options.filter(asset => isAudioAssetType(asset?.FileType));
        return [];
    };

    const normalizeSequentialTiming = (items) => {
        let cursor = 0;
        return items.map((entry) => {
            const duration = Math.max(0.5, toSafeNumber(entry?.duration) || (toSafeNumber(entry?.end) !== null
                ? Math.max(0.5, (toSafeNumber(entry?.end) || 0) - (toSafeNumber(entry?.start) || 0))
                : 1));
            const next = { ...entry };
            next.start = cursor;
            next.duration = duration;
            next.end = cursor + duration;
            cursor = next.end;
            return next;
        });
    };

    const insertAssetIntoLayer = (layerIndex, layerKind, asset, insertIndex = null) => {
        if (!asset) return;

        updateGraph((nextGraph) => {
            const targetLayer = Array.isArray(nextGraph.layers) ? nextGraph.layers[layerIndex] : null;
            if (!targetLayer) return;
            if (!Array.isArray(targetLayer.items)) targetLayer.items = [];

            const items = targetLayer.items;
            const lastEnd = items.reduce((maxEnd, item) => {
                const start = toSafeNumber(item?.start) || 0;
                const end = toSafeNumber(item?.end);
                const duration = toSafeNumber(item?.duration) || 0;
                return Math.max(maxEnd, end !== null ? end : start + duration);
            }, 0);

            const duration = getDurationHintForAsset(asset, layerKind);
            const base = String(asset?.FileAlias || asset?.FileName || layerKind || 'asset').replace(/\.[^.]+$/, '');
            const streamName = sanitizeStreamName(`${base}_${layerKind}_${Date.now()}`);
            const label = `${asset.FileName} (${streamName})`;

            const position = Number.isInteger(insertIndex)
                ? Math.max(0, Math.min(items.length, insertIndex))
                : items.length;
            const appendsToEnd = position >= items.length;
            const start = appendsToEnd ? lastEnd : 0;

            items.splice(position, 0, {
                label,
                stream: streamName,
                asset_filename: asset.FileName,
                start,
                duration,
                end: start + duration,
            });

            // Appending leaves the clips already on the track exactly where they
            // are; dropping between two of them has to push the rest along, the
            // same reflow a drag-reorder does.
            if (!appendsToEnd) {
                targetLayer.items = normalizeSequentialTiming(items);
            }
        });
    };

    const addAssetToLayer = (layerIndex, layerKind) => {
        const selectedFileName = layerAssetSelection[layerIndex];
        if (!selectedFileName) return;
        const asset = (Array.isArray(availableAssets) ? availableAssets : []).find(item => String(item?.FileName || '') === selectedFileName);
        insertAssetIntoLayer(layerIndex, layerKind, asset);
    };

    const deleteClip = (layerIndex, itemIndex) => {
        updateGraph((nextGraph) => {
            const targetLayer = Array.isArray(nextGraph.layers) ? nextGraph.layers[layerIndex] : null;
            if (!targetLayer || !Array.isArray(targetLayer.items)) return;
            if (!targetLayer.items[itemIndex]) return;

            targetLayer.items.splice(itemIndex, 1);
            targetLayer.items = normalizeSequentialTiming(targetLayer.items);
        });
    };

    const updateDrawTextItem = (layerIndex, itemIndex, field, value) => {
        updateGraph((nextGraph) => {
            const targetLayer = Array.isArray(nextGraph.layers) ? nextGraph.layers[layerIndex] : null;
            if (!targetLayer || !Array.isArray(targetLayer.items)) return;
            const item = targetLayer.items[itemIndex];
            if (!item) return;

            item[field] = value;
            if (field === 'text_content') {
                item.label = value || item.label;
            }
        });
    };

    const handleDragStart = (layerIndex, itemIndex, event) => {
        if (!editable) return;
        dragStateRef.current = { layerIndex, itemIndex };
        if (event?.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', `${layerIndex}:${itemIndex}`);
        }
    };

    const reorderItems = (items, fromIndex, toIndex) => {
        const nextItems = [...items];
        const [moved] = nextItems.splice(fromIndex, 1);
        nextItems.splice(toIndex, 0, moved);
        return normalizeSequentialTiming(nextItems);
    };

    const handleDropOnClip = (layerIndex, itemIndex) => {
        if (!editable) return;
        const dragState = dragStateRef.current;
        dragStateRef.current = null;
        if (!dragState || dragState.layerIndex !== layerIndex || dragState.itemIndex === itemIndex) return;

        updateGraph((nextGraph) => {
            const targetLayer = Array.isArray(nextGraph.layers) ? nextGraph.layers[layerIndex] : null;
            if (!targetLayer || !Array.isArray(targetLayer.items)) return;
            targetLayer.items = reorderItems(targetLayer.items, dragState.itemIndex, itemIndex);
        });
    };

    // Where along the track the pointer is, expressed as the clip index the
    // dropped asset would take: everything from here on shifts right.
    const getDropInsertion = (layerIndex, event) => {
        const track = event.currentTarget;
        const rect = track?.getBoundingClientRect?.();
        const items = normalizedLayers[layerIndex]?.normalizedItems || [];
        if (!rect || rect.width <= 0) return { index: items.length, position: 100 };

        const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
        const seconds = ratio * totalDuration;

        let index = items.length;
        for (let i = 0; i < items.length; i += 1) {
            const midpoint = ((items[i]._start || 0) + (items[i]._end || 0)) / 2;
            if (seconds < midpoint) {
                index = i;
                break;
            }
        }

        const edgeSeconds = index < items.length
            ? (items[index]._start || 0)
            : (items.length > 0 ? (items[items.length - 1]._end || 0) : 0);
        const position = Math.min(100, Math.max(0, (edgeSeconds / totalDuration) * 100));
        return { index, position };
    };

    const handleTrackDragOver = (layerIndex, layerKind, event) => {
        if (!editable) return;

        if (hasAssetDragOfKind(event, layerKind)) {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
            const { index, position } = getDropInsertion(layerIndex, event);
            // dragover fires continuously; only a caret that actually moved is
            // worth re-rendering the timeline for.
            setAssetDropTarget((prev) => (
                prev?.layerIndex === layerIndex && prev?.index === index
                    ? prev
                    : { layerIndex, index, position }
            ));
            return;
        }

        // A clip being dragged within its own track still reorders as before.
        if (dragStateRef.current) event.preventDefault();
    };

    const handleDropOnTrack = (layerIndex, layerKind, event) => {
        if (!editable) return;
        setAssetDropTarget(null);

        const payload = readAssetDragPayload(event);
        if (payload) {
            event.preventDefault();
            dragStateRef.current = null;
            if (payload.kind !== layerKind) return;
            const asset = (Array.isArray(availableAssets) ? availableAssets : [])
                .find(item => String(item?.FileName || '') === payload.fileName);
            if (!asset) return;
            const { index } = getDropInsertion(layerIndex, event);
            insertAssetIntoLayer(layerIndex, layerKind, asset, index);
            return;
        }

        const dragState = dragStateRef.current;
        dragStateRef.current = null;
        if (!dragState || dragState.layerIndex !== layerIndex) return;

        updateGraph((nextGraph) => {
            const targetLayer = Array.isArray(nextGraph.layers) ? nextGraph.layers[layerIndex] : null;
            if (!targetLayer || !Array.isArray(targetLayer.items)) return;
            targetLayer.items = reorderItems(targetLayer.items, dragState.itemIndex, targetLayer.items.length - 1);
        });
    };

    const startTrim = (event, layerIndex, itemIndex, edge) => {
        if (!editable) return;
        event.preventDefault();
        event.stopPropagation();

        const trackElement = event.currentTarget.closest('.timeline-track');
        if (!trackElement) return;

        const trackRect = trackElement.getBoundingClientRect();
        const item = normalizedLayers[layerIndex]?.normalizedItems?.[itemIndex];
        if (!item) return;

        setTrimState({
            layerIndex,
            itemIndex,
            edge,
            startX: event.clientX,
            initialStart: item._start || 0,
            initialEnd: item._end || ((item._start || 0) + (item._duration || 1)),
            trackLeft: trackRect.left,
            trackWidth: trackRect.width,
            totalDuration,
        });
    };

    return (
        <div className="plan-graph-wrapper">
            <div className="plan-graph-title">Planned layers {editable ? '(Editable)' : ''}</div>
            <PlanPreviewPlayer
                layers={normalizedLayers}
                totalDuration={totalDuration}
                availableAssets={availableAssets}
                resolveAssetUrl={resolveAssetUrl}
                onPlayheadChange={setPreviewPlayhead}
            />
            <div className="layered-plan-graph nle-timeline">
                <div className="timeline-ruler-row">
                    <div className="layered-plan-label" />
                    <div className="timeline-ruler">
                        {rulerTicks.map((tickMark, index) => (
                            <div
                                key={`tick-${index}`}
                                className="timeline-ruler-tick"
                                style={{ left: `${tickMark.position}%` }}
                            >
                                <span className="timeline-ruler-label">{formatTime(tickMark.tick)}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {normalizedLayers.map((layer, index) => {
                    const items = layer.normalizedItems;
                    const layerKind = getLayerKind(layer);
                    const isEffectLayer = layerKind === 'effects';
                    const isVideoLayer = layerKind === 'video';
                    const isAudioLayer = layerKind === 'audio';
                    const dropTarget = assetDropTarget?.layerIndex === index ? assetDropTarget : null;
                    const assetOptions = getAssetOptionsForLayer(layerKind);
                    const textItems = items.filter(item => String(item?.tool_name || '').toLowerCase() === 'drawtext_tool');
                    const selectedTextIndex = selectedTextEffect[index] ?? (textItems.length > 0 ? items.findIndex(item => item === textItems[0]) : -1);
                    const selectedTextItem = selectedTextIndex >= 0 ? items[selectedTextIndex] : null;

                    return (
                        <div key={`${layer?.name || 'layer'}-${index}`} className="layered-plan-row">
                            <div className="layered-plan-label">{layer?.name || `Layer ${index}`}</div>
                            <div className="layered-plan-track timeline-track-wrapper">
                                {editable && (isVideoLayer || isAudioLayer) && (
                                    <div className="timeline-layer-toolbar">
                                        <select
                                            className="timeline-asset-select"
                                            value={layerAssetSelection[index] || ''}
                                            onChange={(event) => setLayerAssetSelection(prev => ({
                                                ...prev,
                                                [index]: event.target.value
                                            }))}
                                        >
                                            <option value="">Select {(isVideoLayer ? 'video' : 'audio')} asset</option>
                                            {assetOptions.map((asset) => (
                                                <option key={`${asset.FileName}-${asset.FileType}`} value={asset.FileName}>
                                                    {asset.FileAlias || asset.FileName}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => addAssetToLayer(index, isVideoLayer ? 'video' : 'audio')}
                                            disabled={!layerAssetSelection[index]}
                                        >
                                            <Plus size={14} aria-hidden="true" /> Add
                                        </button>
                                    </div>
                                )}

                                {editable && isEffectLayer && textItems.length > 0 && (
                                    <div className="timeline-layer-toolbar text-editor-toolbar">
                                        <label className="timeline-editor-label">Text:</label>
                                        <select
                                            className="timeline-asset-select"
                                            value={selectedTextIndex}
                                            onChange={(event) => {
                                                const nextValue = Number(event.target.value);
                                                setSelectedTextEffect(prev => ({ ...prev, [index]: nextValue }));
                                            }}
                                        >
                                            {textItems.map((textItem) => {
                                                const itemIndex = items.findIndex(item => item === textItem);
                                                return (
                                                    <option key={`${textItem?.step_id || itemIndex}`} value={itemIndex}>
                                                        {textItem?.text_content || textItem?.label || `Text ${itemIndex + 1}`}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        <input
                                            className="timeline-text-input"
                                            type="text"
                                            value={selectedTextItem?.text_content || ''}
                                            onChange={(event) => updateDrawTextItem(index, selectedTextIndex, 'text_content', event.target.value)}
                                            placeholder="Text content"
                                        />
                                        <input
                                            className="timeline-small-input"
                                            type="text"
                                            value={selectedTextItem?.text_x || ''}
                                            onChange={(event) => updateDrawTextItem(index, selectedTextIndex, 'text_x', event.target.value)}
                                            placeholder="x"
                                        />
                                        <input
                                            className="timeline-small-input"
                                            type="text"
                                            value={selectedTextItem?.text_y || ''}
                                            onChange={(event) => updateDrawTextItem(index, selectedTextIndex, 'text_y', event.target.value)}
                                            placeholder="y"
                                        />
                                    </div>
                                )}

                                <div
                                    className={`timeline-track ${dropTarget ? 'drop-active' : ''}`}
                                    onDragOver={(event) => handleTrackDragOver(index, layerKind, event)}
                                    onDragLeave={(event) => {
                                        // Moving between clips fires dragleave on the way; only a
                                        // pointer that actually left the track should clear the caret.
                                        if (!event.currentTarget.contains(event.relatedTarget)) {
                                            setAssetDropTarget((prev) => (prev?.layerIndex === index ? null : prev));
                                        }
                                    }}
                                    onDrop={(event) => handleDropOnTrack(index, layerKind, event)}
                                >
                                    {dropTarget && (
                                        <div className="timeline-drop-caret" style={{ left: `${dropTarget.position}%` }} />
                                    )}
                                    {previewPlayhead !== null && totalDuration > 0 && (
                                        <div
                                            className="timeline-playhead"
                                            style={{ left: `${Math.min(100, Math.max(0, (previewPlayhead / totalDuration) * 100))}%` }}
                                        />
                                    )}
                                    {items.length === 0 ? (
                                        <span className="layered-plan-empty">No items</span>
                                    ) : (
                                        items.map((item, itemIndex) => {
                                            const effects = isVideoLayer ? getEffectsForClip(item) : [];
                                            const effectNames = effects.map(effect => effect?.label).filter(Boolean);
                                            const tooltipLines = [formatClipLabel(item)];
                                            if (effectNames.length > 0) {
                                                tooltipLines.push(`Effects: ${effectNames.join(' | ')}`);
                                            }
                                            if (String(item?.tool_name || '').toLowerCase() === 'drawtext_tool') {
                                                tooltipLines.push(`text='${item?.text_content || ''}' x=${item?.text_x || ''} y=${item?.text_y || ''}`);
                                            }
                                            const tooltipText = tooltipLines.join(' | ');

                                            return (
                                                <div
                                                    key={`${layer?.name || index}-${itemIndex}`}
                                                    className="layered-plan-item timeline-clip"
                                                    draggable={editable}
                                                    onDragStart={(event) => handleDragStart(index, itemIndex, event)}
                                                    onDragOver={(event) => {
                                                        // An asset drag is positioned by the track underneath,
                                                        // which this event bubbles up to.
                                                        if (editable && dragStateRef.current) event.preventDefault();
                                                    }}
                                                    onDrop={() => handleDropOnClip(index, itemIndex)}
                                                    style={{
                                                        left: `${Math.max(0, (item._start / totalDuration) * 100)}%`,
                                                        width: `${Math.max(5, (item._duration / totalDuration) * 100)}%`
                                                    }}
                                                    title={tooltipText}
                                                >
                                                    {editable && (
                                                        <button
                                                            type="button"
                                                            className="timeline-clip-delete"
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                deleteClip(index, itemIndex);
                                                            }}
                                                            title="Delete clip"
                                                            aria-label="Delete clip"
                                                        >
                                                            <X size={14} aria-hidden="true" />
                                                        </button>
                                                    )}
                                                    {editable && (
                                                        <span
                                                            className="timeline-clip-trim trim-left"
                                                            onMouseDown={(event) => startTrim(event, index, itemIndex, 'left')}
                                                            title="Trim start"
                                                        />
                                                    )}

                                                    <span className="timeline-clip-label">{formatClipLabel(item)}</span>
                                                    {effectNames.length > 0 && (
                                                        <span className="timeline-clip-effects" title={`Effects: ${effectNames.join(' | ')}`}>
                                                            <Sparkles size={12} aria-hidden="true" /> {effectNames.length}
                                                        </span>
                                                    )}
                                                    <span className="timeline-clip-tooltip">{tooltipText}</span>

                                                    {editable && (
                                                        <span
                                                            className="timeline-clip-trim trim-right"
                                                            onMouseDown={(event) => startTrim(event, index, itemIndex, 'right')}
                                                            title="Trim end"
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/*
 * VideoWorkspace Component - Performance Optimizations Applied:
 * 
 * 1. Video Event Throttling: Time update events are throttled to every 100ms to reduce frequency
 * 2. Video Preloading: All video elements use preload="metadata" for faster loading 
 * 3. URL Caching: Video URLs are cached for 4 minutes to avoid redundant API calls
 * 4. Video Sync Tolerance: Increased sync tolerance from 0.1s to 0.2s to reduce sync operations
 * 5. Proper Resource Cleanup: Video elements are properly cleaned up when modals close
 * 6. Memoized Components: Asset preview components use keys to prevent unnecessary re-renders
 * 7. Cache Management: URL cache is cleared when workspaces reload to prevent memory leaks
 */

// `projectName` comes from the workflow, which holds the only name in the
// project. A workspace opened outside a project flow simply has none, and the
// headings below fall back to a generic label rather than inventing one.
/* Render status polling. Bounded, because a worker that dies without ever
   writing a terminal status would otherwise leave a tab polling every three
   seconds for as long as it stays open; an hour matches the render's own
   ceiling. Module scope keeps them out of every hook's dependency list. */
const POLL_INTERVAL_MS = 3000;
const POLL_LIMIT_MS = 60 * 60 * 1000;

/* How long the timeline has to sit still before it is saved. Long enough that
   a drag is one write rather than sixty, short enough that a closed tab loses
   at most the gesture in progress. */
const TIMELINE_SAVE_DEBOUNCE_MS = 1200;

/* Lining cameras up decodes a window of audio from each file on a worker, so
   it takes as long as the download does. Bounded because a worker that died
   without writing a terminal status must not leave someone watching a spinner
   for the rest of the session. */
const ALIGN_POLL_MS = 2000;
const ALIGN_LIMIT_MS = 10 * 60 * 1000;

/* How long a signed media URL is reused before another is asked for.
   Held well inside the lifetime the server signs them for (StorageUtils.
   SIGNED_URL_MINUTES), so a URL handed to a <video> element cannot expire
   while someone is still watching through it -- an edit session is measured in
   hours, and the old four minutes here meant the cache missed on essentially
   every use while the link itself outlived it many times over. */
const ASSET_URL_TTL_MS = 6 * 60 * 60 * 1000;

const formatUsd = (value) => `$${Number(value || 0).toFixed(2)}`;

/**
 * What rendering this timeline will cost, said as precisely as it is known.
 *
 * Rendering a timeline runs no model: the approved plan is compiled from the
 * script and executed, so the charge is entirely measured render time at a
 * published rate. Quoting the video_edit history here would be wrong twice
 * over -- that history blends AI planner runs (tokens AND render time) with
 * manual renders (render time only), so it overstates this button, and it
 * dresses a blended average as if it were a figure about this edit.
 *
 * So the rate is stated instead. It is the one number that is exactly true:
 * the settlement multiplies measured minutes by it. What is NOT knowable in
 * advance is the minutes -- render time is ffmpeg wall clock (x264 preset
 * medium, and slower than real time on a worker), which depends on the source
 * resolution and the filters, not on how long the cut is. Predicting it would
 * take measuring render seconds per edit, which nothing records yet.
 */
const describeRenderCost = (quote) => {
    const rate = Number(quote?.runtime_usd_per_minute || 0);
    if (rate > 0) {
        return {
            figure: `${formatUsd(rate)} per minute of render time`,
            basis: 'no AI cost — a manual render runs no model',
        };
    }
    // No rate published: fall back to saying what the flow has cost, and say
    // that is what it is.
    const samples = Number(quote?.basis_samples || 0);
    if (quote?.basis === 'user' && samples > 0) {
        return {
            figure: `About ${formatUsd(quote.estimated_usd)}`,
            basis: `based on your last ${samples} edit${samples === 1 ? '' : 's'}`,
        };
    }
    if (quote?.basis === 'global' && samples > 0) {
        return { figure: `About ${formatUsd(quote.estimated_usd)}`, basis: 'based on similar edits' };
    }
    return {
        figure: `About ${formatUsd(quote?.estimated_usd)}`,
        basis: 'estimated — no history for this yet',
    };
};

/** Nothing on any track — a timeline with no cut in it yet to lose. */
const isEmptyTimeline = (graph) => !(Array.isArray(graph?.layers) ? graph.layers : [])
    .some(layer => (Array.isArray(layer?.items) ? layer.items : []).length > 0);

function VideoWorkspace({ forcedWorkspaceId = '', ideaId = '', projectName = '', hideWorkflowNav = false, hideHeader = false, hideSidebar = false, inWorkflow = false }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [workspaces, setWorkspaces] = useState([]);
    const [selectedWorkspace, setSelectedWorkspace] = useState(null);
    const [assets, setAssets] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // Files dragged in from the desktop: which panel is under the pointer, and
    // how the upload those files kicked off is going.
    const [fileDropZone, setFileDropZone] = useState(null);
    const [dropUploadStatus, setDropUploadStatus] = useState('');
    const fileDropDepthRef = useRef(0);
    const [mediaBinQuery, setMediaBinQuery] = useState('');
    // The generate-or-edit fork: which run exists (if any), and whether the
    // guided generation flow is currently open in place of the editor.
    const [generationSummary, setGenerationSummary] = useState(null);
    const [showGenerationStudio, setShowGenerationStudio] = useState(false);
    // Which of the three phases of the work is on screen: gather media, cut it,
    // publish it. Everything used to render at once on one very long page.
    const [editorTab, setEditorTab] = useState('media');
    const requestedWorkspaceId = useMemo(() => {
        const forcedId = String(forcedWorkspaceId || '').trim();
        if (forcedId) {
            return forcedId;
        }

        const params = new URLSearchParams(location.search || '');
        return String(params.get('workspaceId') || '').trim();
    }, [forcedWorkspaceId, location.search]);
    const isSingleWorkspaceMode = Boolean(requestedWorkspaceId);
    const visibleWorkspaces = useMemo(() => {
        if (!isSingleWorkspaceMode) {
            return workspaces;
        }

        return workspaces.filter(ws => ws.VideoWorkspaceId === requestedWorkspaceId);
    }, [isSingleWorkspaceMode, requestedWorkspaceId, workspaces]);

    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAssetModal, setShowAssetModal] = useState(false);
    const [showProcessModal, setShowProcessModal] = useState(false);
    const [editingWorkspace, setEditingWorkspace] = useState({ Prompt: '' });
    const [newAsset, setNewAsset] = useState({ file: null, alias: '' });
    const [uploadProgress, setUploadProgress] = useState(null);
    const [previewAsset, setPreviewAsset] = useState(null);
    const [textEditorAsset, setTextEditorAsset] = useState(null);
    const [boxEditorAsset, setBoxEditorAsset] = useState(null);
    const [cropEditorAsset, setCropEditorAsset] = useState(null);
    const [zoomEditorAsset, setZoomEditorAsset] = useState(null);
    const [stickerEditorAsset, setStickerEditorAsset] = useState(null);
    const [fadeEditorAsset, setFadeEditorAsset] = useState(null);
    const [flipEditorAsset, setFlipEditorAsset] = useState(null);
    const [lookEditorAsset, setLookEditorAsset] = useState(null);
    // Generated-graphic overlays; holds the asset plus which template opened it.
    const [overlayEditorAsset, setOverlayEditorAsset] = useState(null);
    // AI "Suggest effects" panel (Effect Blocks phase 4).
    const [suggestEditorAsset, setSuggestEditorAsset] = useState(null);

    // Multi-file upload state variables
    const [uploadMode, setUploadMode] = useState('single'); // 'single' or 'multiple'
    const [multiFileStep, setMultiFileStep] = useState(1); // 1: file selection, 2: file configuration, 3: upload
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [fileConfigurations, setFileConfigurations] = useState([]); // Array of {file, alias, description, tags}
    const [bulkUploadProgress, setBulkUploadProgress] = useState(null);
    const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
    
    // Asset generation state variables
    const [assetMode, setAssetMode] = useState('upload'); // 'upload' or 'generate'
    const [generateAsset, setGenerateAsset] = useState({ 
        prompt: '', 
        assetType: 'image/jpeg', 
        aspectRatio: '16:9', 
        alias: '' 
    });
    const [isGeneratingAsset, setIsGeneratingAsset] = useState(false);
    
    const [processPrompt, setProcessPrompt] = useState('');
    const [processingStatus, setProcessingStatus] = useState(null);
    const [showOutputModal, setShowOutputModal] = useState(false);
    const [outputFile, setOutputFile] = useState(null);
    const [isUploadingOutput, setIsUploadingOutput] = useState(false);
    const [outputUploadProgress, setOutputUploadProgress] = useState(null);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [isAnalyzingWorkspace, setIsAnalyzingWorkspace] = useState(false);
    const [showVersionModal, setShowVersionModal] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState('');
    const [showDiffModal, setShowDiffModal] = useState(false);
    const [selectedFromVersion, setSelectedFromVersion] = useState('');
    const [selectedToVersion, setSelectedToVersion] = useState('');
    const [versionComparison, setVersionComparison] = useState(null);
    const [syncedCurrentTime, setSyncedCurrentTime] = useState(0);
    const [videoDuration, setVideoDuration] = useState(0);
    const [isPlayingSynced, setIsPlayingSynced] = useState(false);
    const [videoRefs, setVideoRefs] = useState({ fromVideo: null, toVideo: null });
    const [changeMarkers, setChangeMarkers] = useState([]);
    const [diffViewMode, setDiffViewMode] = useState('side-by-side'); // 'side-by-side' or 'timeline-diff'
    
    // WebSocket streaming states
    const [isStreamingComparison, setIsStreamingComparison] = useState(false);
    const [streamingProgress, setStreamingProgress] = useState(0);
    const [streamingStatus, setStreamingStatus] = useState('');
    const [streamedChanges, setStreamedChanges] = useState([]);
    const [websocketRef, setWebsocketRef] = useState(null);
    
    // Performance optimization states
    const [lastTimeUpdate, setLastTimeUpdate] = useState(0);
    const [timeUpdateThrottle] = useState(100); // Throttle time updates to every 100ms
    const [videoUrlCache, setVideoUrlCache] = useState(new Map()); // Cache video URLs
    
    // Comments state variables
    const [comments, setComments] = useState([]);
    const [showCommentsModal, setShowCommentsModal] = useState(false);
    const [newComment, setNewComment] = useState({ comment: '', videoTimestamp: 0, videoEndTimestamp: 0 });
    const [isLoadingComments, setIsLoadingComments] = useState(false);
    const [editingComment, setEditingComment] = useState(null);
    
    // Timeline selection state variables
    const [timelineSelection, setTimelineSelection] = useState({ start: null, end: null, isSelecting: false });
    const timelineRef = useRef(null);
    
    // Video preview state for comments
    const [commentVideoUrl, setCommentVideoUrl] = useState(null);
    const [commentVideoRef, setCommentVideoRef] = useState(null);
    const [isLoadingCommentVideo, setIsLoadingCommentVideo] = useState(false);
    const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState(null);
    const [isLoadingUploadedPreview, setIsLoadingUploadedPreview] = useState(false);
    const [uploadedPreviewError, setUploadedPreviewError] = useState('');
    const [commentVideoCurrentTime, setCommentVideoCurrentTime] = useState(0);
    const [commentVideoDuration, setCommentVideoDuration] = useState(0);
    const [isCommentVideoPlaying, setIsCommentVideoPlaying] = useState(false);
    const [workspaceAnalysisById, setWorkspaceAnalysisById] = useState({});

    // Backend workspace review state
    const [, setReviewStatus] = useState(null);
    const reviewPollingRef = useRef(null);
    
    // Time format state for manual input
    const [timeInputFormat, setTimeInputFormat] = useState({ start: '0:00.00', end: '0:00.00' });

    // Agentic AI Chat states
    const [showAIChat, setShowAIChat] = useState(false);
    const [aiChatMessages, setAiChatMessages] = useState([]);
    const [aiChatInput, setAiChatInput] = useState('');
    const [isAiConnected, setIsAiConnected] = useState(false);
    const [aiWebSocket, setAiWebSocket] = useState(null);
    const [isAiProcessing, setIsAiProcessing] = useState(false);
    const [aiConnectionStatus, setAiConnectionStatus] = useState('disconnected');
    const [aiChatError, setAiChatError] = useState('');
    const [editableApprovalPayloads, setEditableApprovalPayloads] = useState({});
    const aiChatRef = useRef(null);
    const aiInputRef = useRef(null);
    const aiWebSocketRef = useRef(null);
    // Payloads composed before the socket finished opening; flushed on open.
    const pendingAiSendsRef = useRef([]);
    /* Reconnect bookkeeping. `isAiConnected` cannot guard the opening socket --
       it only flips in onopen, so every render during the token fetch used to
       start another socket. These refs track the attempt itself, and give the
       retry timer an owner that can cancel it. */
    const aiConnectingRef = useRef(false);
    const aiReconnectTimerRef = useRef(null);
    const aiReconnectAttemptsRef = useRef(0);
    // Whether the chat still wants a socket. Set false by teardown so an
    // in-flight open closes itself instead of outliving the panel.
    const aiWantsConnectionRef = useRef(false);
    // The user's own timeline, kept aside while an AI proposal occupies the
    // editor, so discarding the proposal is not the same as losing their work.
    const preProposalGraphRef = useRef(null);

    // One timeline, two ways to change it. The user cuts it by hand, and the AI
    // proposes into the *same* graph rather than a parallel one — an AI proposal
    // is a starting point you keep editing, not a separate mode you switch to.
    const [manualGraph, setManualGraph] = useState(() => createManualTimelineGraph());
    const [manualError, setManualError] = useState('');
    const [manualNotice, setManualNotice] = useState('');
    // Whether the cut on screen is safely stored: '' (nothing to say yet),
    // 'saving', 'saved' or 'error'.
    const [timelineSaveState, setTimelineSaveState] = useState('');
    // What was last read from or written to the server, serialized, so an
    // autosave fires on a real change rather than on every re-render.
    const savedTimelineRef = useRef('');
    // The workspace whose saved timeline has already been fetched. Until this
    // matches, there is nothing worth saving over.
    const timelineLoadedForRef = useRef('');
    // The workspace the timeline on screen belongs to, so a re-run that is not
    // a workspace change leaves it alone.
    const timelineWorkspaceRef = useRef(null);
    // The graph as of now, readable from an async callback that closed over an
    // older one.
    const latestGraphRef = useRef(null);
    // The AI proposal currently loaded into the timeline and awaiting approval.
    const [pendingApproval, setPendingApproval] = useState(null);

    const auth = getAuth();

    // Filename validation states
    const [aliasError, setAliasError] = useState('');
    const [generateAliasError, setGenerateAliasError] = useState('');
    const { getIdToken } = useAuth();
    const { quoteCost } = useWallet();
    // What rendering this edit is expected to cost, shown next to the button
    // that spends it. Null until quoted, and null forever if quoting fails —
    // a missing price label must not stop anyone rendering.
    const [editCostQuote, setEditCostQuote] = useState(null);
    const isSelectedWorkspacePosted = Boolean(selectedWorkspace?.IsPosted || selectedWorkspace?.PostId);
    // Only what a track can actually hold. An image chip in the bin would be an
    // affordance with nowhere to drop it.
    const timelineAssets = useMemo(
        () => assets.filter(asset => Boolean(getAssetLayerKind(asset?.FileType))),
        [assets]
    );
    // Search the bin by what the user can see (the alias) as well as what they
    // uploaded (the file name), plus "video"/"audio" so a kind narrows it too.
    const visibleBinAssets = useMemo(() => {
        const query = mediaBinQuery.trim().toLowerCase();
        if (!query) return timelineAssets;
        return timelineAssets.filter((asset) => [
            asset?.FileAlias,
            asset?.FileName,
            getAssetLayerKind(asset?.FileType),
        ].some(field => String(field || '').toLowerCase().includes(query)));
    }, [timelineAssets, mediaBinQuery]);
    // The workflow owns the idea↔workspace link and is the only document that
    // holds it; the project flow passes it down. A workspace deliberately has
    // no idea id of its own, so there is never a second answer to disagree with.
    const linkedIdeaId = String(ideaId || '').trim();
    // On an empty workspace the fork is the call to action. While it is up the
    // Media section stays quiet — otherwise the same two choices are on screen
    // three times over (fork cards, header buttons, empty-state button).
    const showGenerationFork = assets.length === 0 && !isSelectedWorkspacePosted;

    // Filename validation function
    const validateFilename = (filename) => {
        const filenameRegex = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9]+)?$/;
        if (!filenameRegex.test(filename)) {
            return {
                isValid: false,
                error: 'Filename can only contain letters (a-z, A-Z), numbers (0-9), and hyphens (-)'
            };
        }
        return { isValid: true, error: '' };
    };

    // Time format helper functions
    const secondsToTimeFormat = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = (seconds % 60).toFixed(2);
        return `${mins}:${secs.padStart(5, '0')}`;
    };

    const timeFormatToSeconds = (timeStr) => {
        const parts = String(timeStr || '').split(':');
        if (parts.length !== 2) return 0;
        const mins = parseInt(parts[0], 10) || 0;
        const secs = parseFloat(parts[1]) || 0;
        return mins * 60 + secs;
    };

    const validateTimeFormat = (timeStr) => {
        const timeRegex = /^\d+:[0-5]?\d(\.\d{1,2})?$/;
        return timeRegex.test(String(timeStr || ''));
    };

    // Validate file type based on extension
    const validateFileType = (file) => {
        if (!file) return { isValid: true, error: '' };

        const fileName = file.name.toLowerCase();
        const isSupported = ALL_SUPPORTED_FORMATS.split(',').some(ext => fileName.endsWith(ext.toLowerCase()));
        if (!isSupported) {
            return {
                isValid: false,
                error: `Unsupported file format. Supported formats: ${SUPPORTED_FORMATS_TEXT.ALL}`
            };
        }

        return { isValid: true, error: '' };
    };

    // Get file category for better UX messaging
    const getFileCategory = (fileName) => {
        const lowerName = String(fileName || '').toLowerCase();
        if (SUPPORTED_FORMATS.VIDEO.some(ext => lowerName.endsWith(ext))) return 'video';
        if (SUPPORTED_FORMATS.AUDIO.some(ext => lowerName.endsWith(ext))) return 'audio';
        if (SUPPORTED_FORMATS.IMAGE.some(ext => lowerName.endsWith(ext))) return 'image';
        if (SUPPORTED_FORMATS.DOCUMENT.some(ext => lowerName.endsWith(ext))) return 'document';
        return 'unknown';
    };

    // currentVersion represents the next draft version to be published
    const getVersionOptions = (currentVersion) => {
        const maxAvailableVersion = (currentVersion || 0) - 1;
        const options = [];
        for (let i = 0; i <= maxAvailableVersion; i++) {
            options.push(i);
        }
        return options;
    };

    const getLatestPublishedVersion = useCallback((workspace) => {
        if (!workspace?.Version) {
            return null;
        }
        return Math.max(0, workspace.Version - 1);
    }, []);

    const selectedWorkspaceAnalysis = useMemo(() => {
        const workspaceId = selectedWorkspace?.VideoWorkspaceId;
        if (!workspaceId) {
            return null;
        }

        const currentAnalysis = workspaceAnalysisById[workspaceId] || null;
        const persistedAnalysisId = String(selectedWorkspace?.AnalysisId || '').trim();

        if (!currentAnalysis && !persistedAnalysisId) {
            return null;
        }

        if (!currentAnalysis) {
            return {
                analysisId: persistedAnalysisId,
                status: 'linked',
                results: null,
                error: ''
            };
        }

        return {
            ...currentAnalysis,
            analysisId: String(currentAnalysis.analysisId || persistedAnalysisId || '').trim() || null,
        };
    }, [selectedWorkspace?.VideoWorkspaceId, selectedWorkspace?.AnalysisId, workspaceAnalysisById]);

    const updateWorkspaceAnalysis = useCallback((workspaceId, patch) => {
        if (!workspaceId) {
            return;
        }

        setWorkspaceAnalysisById((prev) => ({
            ...prev,
            [workspaceId]: {
                ...(prev[workspaceId] || {}),
                ...patch
            }
        }));
    }, []);

    const hydrateWorkspaceAnalysis = useCallback(async (workspaceId, analysisId) => {
        const resolvedWorkspaceId = String(workspaceId || '').trim();
        const resolvedAnalysisId = String(analysisId || '').trim();

        if (!resolvedWorkspaceId || !resolvedAnalysisId) {
            return null;
        }

        const idToken = await getIdToken();
        const result = await GetAnalysisById(idToken, resolvedAnalysisId);
        if (result.status !== 200) {
            throw new Error(result.myJson?.detail || 'Failed to load linked analysis');
        }

        const payload = result.myJson;
        const analysisResults = payload?.analysis_results || payload?.analysisResults || null;
        const nextStatus = String(payload?.status || '').toLowerCase() || (analysisResults ? 'completed' : 'linked');

        updateWorkspaceAnalysis(resolvedWorkspaceId, {
            analysisId: resolvedAnalysisId,
            status: nextStatus,
            results: analysisResults ? { ...payload, analysis_results: analysisResults } : null,
            error: '',
        });

        return {
            payload,
            analysisResults,
            nextStatus,
        };
    }, [getIdToken, updateWorkspaceAnalysis]);

    const loadWorkspaces = useCallback(async () => {
        try {
            setIsLoading(true);
            setError('');
            const idToken = await getIdToken();
            const pageDefinition = { LastEntityId: '', Direction: 1 };
            
            // Load both owned workspaces and editor workspaces
            const [ownedResponse, editorResponse] = await Promise.all([
                GetVideoWorkspaceList(idToken, pageDefinition),
                GetVideoWorkspaceEditorList(idToken, pageDefinition)
            ]);
            
            let allWorkspaces = [];
            
            // Add owned workspaces
            if (ownedResponse.status === 200) {
                const ownedWorkspaces = (ownedResponse.myJson.WorkSpaces || []).map(ws => ({
                    ...ws,
                    isOwner: true,
                    isEditor: false
                }));
                allWorkspaces = [...allWorkspaces, ...ownedWorkspaces];
            }
            
            // Add editor workspaces
            if (editorResponse.status === 200) {
                const editorWorkspaces = (editorResponse.myJson.WorkSpaces || []).map(ws => ({
                    ...ws,
                    isOwner: false,
                    isEditor: true
                }));
                allWorkspaces = [...allWorkspaces, ...editorWorkspaces];
            }
            
            // Remove duplicates (in case user is both owner and editor of same workspace)
            const uniqueWorkspaces = allWorkspaces.reduce((acc, current) => {
                const existing = acc.find(ws => ws.VideoWorkspaceId === current.VideoWorkspaceId);
                if (existing) {
                    // If workspace exists, update flags to show both roles
                    existing.isOwner = existing.isOwner || current.isOwner;
                    existing.isEditor = existing.isEditor || current.isEditor;
                } else {
                    acc.push(current);
                }
                return acc;
            }, []);
            
            setWorkspaces(uniqueWorkspaces);

            // Keep selected workspace in sync with refreshed data (e.g., updated Version after processing)
            setSelectedWorkspace(prevSelected => {
                if (!prevSelected) return prevSelected;
                const refreshedSelected = uniqueWorkspaces.find(
                    ws => ws.VideoWorkspaceId === prevSelected.VideoWorkspaceId
                );
                return refreshedSelected || prevSelected;
            });
            
            // Clear video URL cache when workspaces reload to prevent memory leaks
            setVideoUrlCache(new Map());
            
            // Show error only if both calls failed
            if (ownedResponse.status !== 200 && editorResponse.status !== 200) {
                setError('Failed to load workspaces');
            }

            return uniqueWorkspaces;
        } catch (err) {
            setError('Error loading workspaces: ' + err.message);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [getIdToken]);

    const loadAssets = useCallback(async (workspaceId) => {
        try {
            const user = auth.currentUser;
            if (!user || !workspaceId) return;

            const idToken = await user.getIdToken();
            const response = await ListVideoWorkspaceAssets(idToken, workspaceId);
            
            if (response.status === 200) {
                setAssets(response.myJson.Assets || []);
            } else {
                setError('Failed to load assets');
            }
        } catch (err) {
            setError('Error loading assets: ' + err.message);
        }
    }, [auth]);

    // Whether this workspace already has a generation run, so the empty state
    // can offer to resume one instead of starting over. A 404 is the normal
    // answer for a workspace that has only ever been edited by hand.
    const loadGenerationSummary = useCallback(async (workspaceId) => {
        try {
            const user = auth.currentUser;
            if (!user || !workspaceId) return;

            const idToken = await user.getIdToken();
            const { status, myJson } = await GetVideoGeneration(idToken, workspaceId);
            setGenerationSummary(status === 200 ? myJson?.Generation || null : null);
        } catch {
            setGenerationSummary(null);
        }
    }, [auth]);

    useEffect(() => {
        if (!selectedWorkspace) {
            setGenerationSummary(null);
            setShowGenerationStudio(false);
            return;
        }
        loadGenerationSummary(selectedWorkspace.VideoWorkspaceId);
    }, [selectedWorkspace, loadGenerationSummary]);

    // Uploads are finalized server-side from storage events, so refresh the
    // asset list while an upload is still moving (from here or from another
    // tab) or has every byte up and is waiting on that event. Interrupted
    // uploads sit still until someone resumes them, so they are not polled.
    // The bound covers a finalize event that never arrives.
    const unfinalizedPollCountRef = useRef(0);
    const uploads = useUploads();
    const uploadsByKey = useMemo(() => {
        const map = new Map();
        uploads.forEach((entry) => map.set(entry.key, entry));
        return map;
    }, [uploads]);
    const hasPendingUploads = useMemo(() => (Array.isArray(assets) ? assets : []).some((asset) => {
        if (!asset || asset.IsFinalized) return false;
        const kind = describeUpload(asset, uploadsByKey.get(uploadKey(asset.VideoWorkspaceId, asset.FileName))).kind;
        return kind === 'finalizing' || kind === 'elsewhere';
    }), [assets, uploadsByKey]);
    useEffect(() => {
        if (!selectedWorkspace) return undefined;
        if (!hasPendingUploads) {
            unfinalizedPollCountRef.current = 0;
            return undefined;
        }
        if (unfinalizedPollCountRef.current >= 450) return undefined;

        const timer = setTimeout(() => {
            unfinalizedPollCountRef.current += 1;
            loadAssets(selectedWorkspace.VideoWorkspaceId);
        }, 4000);
        return () => clearTimeout(timer);
    }, [assets, selectedWorkspace, loadAssets, hasPendingUploads]);

    // When one of this tab's uploads finishes, fetch the list so the row
    // moves on to finalizing; once storage has confirmed it, let the entry go.
    const uploadStatesRef = useRef(new Map());
    useEffect(() => {
        if (!selectedWorkspace) return;
        let refresh = false;
        uploads.forEach((entry) => {
            const previous = uploadStatesRef.current.get(entry.key);
            if (previous !== entry.state && entry.workspaceId === selectedWorkspace.VideoWorkspaceId
                && (entry.state === UPLOAD_UPLOADED || entry.state === UPLOAD_ERROR || entry.state === UPLOAD_CANCELLED)) {
                refresh = true;
            }
            uploadStatesRef.current.set(entry.key, entry.state);
        });
        (Array.isArray(assets) ? assets : []).forEach((asset) => {
            if (!asset?.IsFinalized) return;
            const key = uploadKey(asset.VideoWorkspaceId, asset.FileName);
            if (uploadsByKey.has(key)) dismissUpload(key);
        });
        if (refresh) {
            unfinalizedPollCountRef.current = 0;
            loadAssets(selectedWorkspace.VideoWorkspaceId);
        }
    }, [uploads, uploadsByKey, assets, selectedWorkspace, loadAssets]);

    // "Resume" on an interrupted row needs the file again: the picker opens
    // for that one asset, and the chosen file has to be the same one.
    const resumeInputRef = useRef(null);
    const resumeTargetRef = useRef(null);
    const handleResumeAsset = useCallback((asset, entry) => {
        if (!selectedWorkspace) return;
        if (entry?.file) {
            startUpload({
                workspaceId: selectedWorkspace.VideoWorkspaceId,
                file: entry.file,
                alias: entry.alias || asset.FileAlias || null,
                fileName: asset.FileName,
                getIdToken: () => auth.currentUser.getIdToken(),
                resume: true,
            }).catch((err) => setError(`Could not resume ${asset.FileName}: ${err.message}`));
            return;
        }
        resumeTargetRef.current = asset;
        if (resumeInputRef.current) {
            resumeInputRef.current.value = '';
            resumeInputRef.current.click();
        }
    }, [selectedWorkspace, auth]);
    const handleResumeFilePicked = useCallback((event) => {
        const file = event.target.files && event.target.files[0];
        const asset = resumeTargetRef.current;
        resumeTargetRef.current = null;
        if (!file || !asset || !selectedWorkspace) return;
        const wanted = String(asset.FileName).split('.').pop().toLowerCase();
        const picked = String(file.name).split('.').pop().toLowerCase();
        if (wanted !== picked) {
            setError(`Pick the same file to resume ${asset.FileName} (a .${wanted} file).`);
            return;
        }
        setError('');
        startUpload({
            workspaceId: selectedWorkspace.VideoWorkspaceId,
            file,
            alias: asset.FileAlias || null,
            fileName: asset.FileName,
            getIdToken: () => auth.currentUser.getIdToken(),
            resume: true,
        }).catch((err) => setError(`Could not resume ${asset.FileName}: ${err.message}`));
    }, [selectedWorkspace, auth]);
    const handleCancelUpload = useCallback((asset, entry) => {
        if (entry) cancelUpload(entry.key);
    }, []);

    useEffect(() => {
        loadWorkspaces();
    }, [loadWorkspaces]);

    const handleCreateWorkspace = async () => {
        try {
            setIsLoading(true);
            const user = auth.currentUser;
            if (!user) return;

            const idToken = await user.getIdToken();
            const response = await CreateVideoWorkspace(idToken);
            
            if (response.status === 200) {
                await loadWorkspaces();
                setShowCreateModal(false);
                const createdWorkspace = response.myJson;
                setSelectedWorkspace(createdWorkspace);
                setEditingWorkspace({ 
                    Prompt: createdWorkspace.Prompt,
                    VideoWorkspaceId: createdWorkspace.VideoWorkspaceId
                });
                setShowEditModal(true);
            } else {
                setError('Failed to create workspace');
            }
        } catch (err) {
            setError('Error creating workspace: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateWorkspace = async () => {
        try {
            setIsLoading(true);
            const user = auth.currentUser;
            if (!user || !selectedWorkspace) return;
            if (selectedWorkspace?.IsPosted || selectedWorkspace?.PostId) {
                setError('This video has been posted, so its edit is locked.');
                return;
            }

            const idToken = await user.getIdToken();
            const workspaceId = selectedWorkspace.VideoWorkspaceId || editingWorkspace.VideoWorkspaceId;
            
            if (!workspaceId) {
                setError('Video workspace ID is missing');
                return;
            }

            const response = await UpdateVideoWorkspace(
                idToken, 
                workspaceId, 
                editingWorkspace
            );
            
            if (response.status === 200) {
                await loadWorkspaces();
                setShowEditModal(false);
                // Update selected workspace with new data
                const updatedWorkspace = workspaces.find(w => w.VideoWorkspaceId === workspaceId);
                if (updatedWorkspace) {
                    setSelectedWorkspace({...updatedWorkspace, ...editingWorkspace});
                }
            } else {
                setError('Failed to update workspace');
            }
        } catch (err) {
            setError('Error updating workspace: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteWorkspace = async (workspaceId) => {
        // Find the workspace to check if user is owner
        const workspace = workspaces.find(w => w.VideoWorkspaceId === workspaceId);
        if (!workspace) {
            setError('Workspace not found');
            return;
        }
        
        // Check if user is the owner
        if (!workspace.isOwner) {
            setError('Only workspace owners can delete workspaces');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this workspace? This action cannot be undone.')) {
            return;
        }

        try {
            setIsLoading(true);
            const user = auth.currentUser;
            if (!user) return;

            const idToken = await user.getIdToken();
            const response = await DeleteVideoWorkspace(idToken, workspaceId);
            
            if (response.status === 200) {
                await loadWorkspaces();
                if (selectedWorkspace?.VideoWorkspaceId === workspaceId) {
                    setSelectedWorkspace(null);
                    setAssets([]);
                }
            } else {
                setError('Failed to delete workspace');
            }
        } catch (err) {
            setError('Error deleting workspace: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectWorkspace = async (workspace) => {
        setSelectedWorkspace(workspace);
        setAssets([]);
        await loadAssets(workspace.VideoWorkspaceId);
        await loadComments(workspace.VideoWorkspaceId);
    };

    const handleEditWorkspace = (workspace) => {
        if (workspace?.IsPosted || workspace?.PostId) {
            setError('This video has been posted, so its edit is locked.');
            return;
        }

        // Check if user has editing permissions (owner or editor)
        if (!workspace.isOwner && !workspace.isEditor) {
            setError('You can view this project but not edit it. Ask its owner for editor access.');
            return;
        }
        
        setSelectedWorkspace(workspace);
        setEditingWorkspace({ Prompt: workspace.Prompt });
        setShowEditModal(true);
    };

    const handleBackToIdea = () => {
        if (!linkedIdeaId) {
            setError('This project has no idea attached yet.');
            return;
        }

        navigate(`/Ideation?ideaId=${encodeURIComponent(linkedIdeaId)}`);
    };

    const handleAnalyzeWorkspace = async () => {
        if (!selectedWorkspace?.VideoWorkspaceId) return;
        if (isSelectedWorkspacePosted) {
            setError('This video has been posted, so its edit is locked.');
            return;
        }

        try {
            setIsAnalyzingWorkspace(true);
            setError('');
            await runWorkspaceAnalysis({ throwOnError: true });
        } catch (err) {
            setError(err.message || 'Couldn’t analyze this video. Try again in a moment.');
        } finally {
            setIsAnalyzingWorkspace(false);
        }
    };

    const resetAssetForm = () => {
        setAssetMode('upload');
        setUploadMode('single');
        setMultiFileStep(1);
        setSelectedFiles([]);
        setFileConfigurations([]);
        setBulkUploadProgress(null);
        setCurrentUploadIndex(0);
        setNewAsset({ file: null, alias: '' });
        setGenerateAsset({ 
            prompt: '', 
            assetType: 'image/jpeg', 
            aspectRatio: '16:9', 
            alias: '' 
        });
        setUploadProgress(null);
        setIsGeneratingAsset(false);
        setAliasError('');
        setGenerateAliasError('');
    };

    const handleOpenAssetModal = () => {
        resetAssetForm();
        setShowAssetModal(true);
    };

    /*
     * The one place a file actually reaches storage. The modal and the
     * drop zones both go through here so a dropped file is uploaded on
     * exactly the same terms as a picked one.
     */
    // Starts the transfer and returns once the asset row exists; the bytes
    // keep going in the background (uploadManager) and the row shows them.
    const uploadAssetFile = async (file, alias = null) => {
        const fileValidation = validateFileType(file);
        if (!fileValidation.isValid) throw new Error(fileValidation.error);

        return startUpload({
            workspaceId: selectedWorkspace.VideoWorkspaceId,
            file,
            alias: alias && alias.trim() ? alias : null,
            getIdToken: () => auth.currentUser.getIdToken(),
        });
    };

    const handleUploadAsset = async () => {
        try {
            if (!newAsset.file || !selectedWorkspace) return;
            if (isSelectedWorkspacePosted) {
                setError('This video has been posted, so its edit is locked.');
                return;
            }

            // Validate file type
            const fileValidation = validateFileType(newAsset.file);
            if (!fileValidation.isValid) {
                setError(fileValidation.error);
                return;
            }

            // Only validate alias if provided (since it's optional)
            if (newAsset.alias && newAsset.alias.trim()) {
                const aliasValidation = validateFilename(newAsset.alias);
                if (!aliasValidation.isValid) {
                    setAliasError(aliasValidation.error);
                    return;
                }
            }

            // Clear any previous errors
            setError('');
            setAliasError('');

            setUploadProgress('Starting upload...');
            await uploadAssetFile(newAsset.file, newAsset.alias);

            // The transfer carries on in the background; the asset row shows
            // its progress and, once every byte is up, "Finalizing" until the
            // storage event lands.
            const fileCategory = getFileCategory(newAsset.file.name);
            setUploadProgress(`${fileCategory.charAt(0).toUpperCase() + fileCategory.slice(1)} upload started — progress shows on the asset.`);

            setTimeout(() => {
                setUploadProgress(null);
                setNewAsset({ file: null, alias: '' });
                setShowAssetModal(false);
                unfinalizedPollCountRef.current = 0;
                loadAssets(selectedWorkspace.VideoWorkspaceId);
            }, 800);
        } catch (err) {
            setError('Error uploading asset: ' + err.message);
            setUploadProgress(null);
        }
    };

    /*
     * Files dropped straight onto the Media panel or the edit rail. No modal,
     * no alias step — the alias is optional anyway, and anything dropped can
     * still be renamed afterwards.
     */
    const handleDroppedFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0 || !selectedWorkspace) return;
        if (isSelectedWorkspacePosted) {
            setError('This video has been posted, so its edit is locked.');
            return;
        }

        setError('');
        const failed = [];
        let uploaded = 0;

        for (let i = 0; i < files.length; i += 1) {
            const file = files[i];
            setDropUploadStatus(`Starting ${i + 1} of ${files.length}: ${file.name}`);
            try {
                await uploadAssetFile(file);
                uploaded += 1;
            } catch (err) {
                failed.push(`${file.name} (${err.message})`);
            }
        }

        if (failed.length > 0) {
            setError(`Could not upload ${failed.join(', ')}`);
        }

        setDropUploadStatus(uploaded > 0
            ? `Uploading ${uploaded} file${uploaded === 1 ? '' : 's'} — progress shows on each item`
            : '');

        if (uploaded > 0) {
            unfinalizedPollCountRef.current = 0;
            loadAssets(selectedWorkspace.VideoWorkspaceId);
        }

        setTimeout(() => setDropUploadStatus(''), 2500);
    };

    // dragenter/dragleave fire for every child the pointer crosses, so the
    // zone tracks depth rather than trusting a single leave to mean "gone".
    const getFileDropProps = (zoneId) => ({
        onDragEnter: (event) => {
            if (!hasFileDrag(event) || isSelectedWorkspacePosted) return;
            event.preventDefault();
            fileDropDepthRef.current += 1;
            setFileDropZone(zoneId);
        },
        onDragOver: (event) => {
            if (!hasFileDrag(event) || isSelectedWorkspacePosted) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
        },
        onDragLeave: (event) => {
            if (!hasFileDrag(event)) return;
            event.preventDefault();
            fileDropDepthRef.current = Math.max(0, fileDropDepthRef.current - 1);
            if (fileDropDepthRef.current === 0) setFileDropZone(null);
        },
        onDrop: (event) => {
            if (!hasFileDrag(event)) return;
            event.preventDefault();
            fileDropDepthRef.current = 0;
            setFileDropZone(null);
            handleDroppedFiles(event.dataTransfer.files);
        },
    });

    const handleGenerateAsset = async () => {
        try {
            if (!generateAsset.prompt || !selectedWorkspace) return;
            if (isSelectedWorkspacePosted) {
                setError('This video has been posted, so its edit is locked.');
                return;
            }

            // Validate filename alias if provided
            if (generateAsset.alias) {
                const validation = validateFilename(generateAsset.alias);
                if (!validation.isValid) {
                    setError(validation.error);
                    return;
                }
            }

            setIsGeneratingAsset(true);
            setError('');

            const user = auth.currentUser;
            const idToken = await user.getIdToken();

            const generateRequest = {
                VideoWorkspaceId: selectedWorkspace.VideoWorkspaceId,
                Prompt: generateAsset.prompt,
                AssetType: generateAsset.assetType,
                AspectRatio: generateAsset.aspectRatio,
                FileAlias: generateAsset.alias
            };

            const response = await GenerateVideoWorkspaceAsset(idToken, generateRequest);
            
            if (response.status === 200) {
                // Reset form and close modal
                setGenerateAsset({ 
                    prompt: '', 
                    assetType: 'image/jpeg', 
                    aspectRatio: '16:9', 
                    alias: '' 
                });
                setShowAssetModal(false);
                setIsGeneratingAsset(false);
                
                // Reload assets to show the new generated asset
                await loadAssets(selectedWorkspace.VideoWorkspaceId);
                
                // Show success message
                setError('Asset generated successfully!');
                setTimeout(() => setError(''), 3000);
            } else {
                throw new Error(response.myJson?.detail || 'Failed to generate asset');
            }
        } catch (err) {
            setError('Error generating asset: ' + err.message);
            setIsGeneratingAsset(false);
        }
    };

    // Multi-file upload functions
    // One picker, two shapes. The single/multiple split used to be a choice the
    // user made before seeing a file dialog, which meant picking wrong and going
    // back; now it is derived from what they actually selected.
    const handleAssetFileSelection = (fileList) => {
        const files = Array.from(fileList || []);

        if (files.length <= 1) {
            setUploadMode('single');
            setSelectedFiles([]);
            setFileConfigurations([]);
            setMultiFileStep(1);
            setNewAsset((current) => ({ ...current, file: files[0] || null }));
            return;
        }

        setUploadMode('multiple');
        setNewAsset({ file: null, alias: '' });
        setAliasError('');
        handleFileSelection(files);
        // The files are already chosen, so the old "step 1: select files" has
        // nothing left to ask — go straight to naming them.
        setMultiFileStep(2);
    };

    const handleFileSelection = (files) => {
        const fileArray = Array.from(files);
        const validFiles = [];
        const errors = [];

        fileArray.forEach(file => {
            const fileValidation = validateFileType(file);
            if (fileValidation.isValid) {
                validFiles.push(file);
            } else {
                errors.push(`${file.name}: ${fileValidation.error}`);
            }
        });

        if (errors.length > 0) {
            setError(`Some files were rejected:\n${errors.join('\n')}`);
        } else {
            setError('');
        }

        setSelectedFiles(validFiles);
        
        // Initialize file configurations (only display name needed)
        const configs = validFiles.map(file => ({
            file,
            alias: ''
        }));
        setFileConfigurations(configs);
    };

    const updateFileConfiguration = (index, field, value) => {
        const updatedConfigs = [...fileConfigurations];
        updatedConfigs[index] = {
            ...updatedConfigs[index],
            [field]: value
        };
        setFileConfigurations(updatedConfigs);
    };

    const validateFileConfigurations = () => {
        const errors = [];
        fileConfigurations.forEach((config, index) => {
            // Only validate alias if it's provided (since it's optional)
            if (config.alias && config.alias.trim()) {
                const aliasValidation = validateFilename(config.alias);
                if (!aliasValidation.isValid) {
                    errors.push(`File ${index + 1} display name (${config.alias}): ${aliasValidation.error}`);
                }
            }
        });
        return errors;
    };

    const handleBulkUpload = async () => {
        try {
            if (!selectedWorkspace || fileConfigurations.length === 0) return;

            // Validate all file configurations
            const validationErrors = validateFileConfigurations();
            if (validationErrors.length > 0) {
                setError(`Configuration errors:\n${validationErrors.join('\n')}`);
                return;
            }

            setError('');
            setBulkUploadProgress('Starting bulk upload...');
            setCurrentUploadIndex(0);

            const totalFiles = fileConfigurations.length;
            let successCount = 0;
            let failureCount = 0;

            for (let i = 0; i < fileConfigurations.length; i++) {
                const config = fileConfigurations[i];
                setCurrentUploadIndex(i);
                setBulkUploadProgress(`Starting ${i + 1} of ${totalFiles}: ${config.file.name}...`);

                try {
                    // Each transfer continues in the background once its row
                    // exists; the rows show progress from here on.
                    await uploadAssetFile(config.file, config.alias);
                    successCount++;
                } catch (err) {
                    console.error(`Failed to upload ${config.file.name}:`, err);
                    failureCount++;
                }
            }

            setBulkUploadProgress(`Uploads started: ${successCount} in progress, ${failureCount} failed`);
            
            setTimeout(() => {
                setBulkUploadProgress(null);
                setCurrentUploadIndex(0);
                resetAssetForm();
                setShowAssetModal(false);
                unfinalizedPollCountRef.current = 0;
                loadAssets(selectedWorkspace.VideoWorkspaceId);
            }, 2000);

        } catch (err) {
            setError('Error during bulk upload: ' + err.message);
            setBulkUploadProgress(null);
            setCurrentUploadIndex(0);
        }
    };

    const resolvePlanAssetUrl = useCallback(async (fileName) => {
        const asset = (Array.isArray(assets) ? assets : []).find(item => item?.FileName === fileName);
        if (!asset) return null;

        const cacheKey = `${asset.VideoWorkspaceId}_${asset.FileName}`;
        const cached = videoUrlCache.get(cacheKey);
        if (cached && (Date.now() - cached.timestamp) < ASSET_URL_TTL_MS) {
            return cached.url;
        }

        const idToken = await getIdToken();
        const response = await GetVideoWorkspaceAssetUrl(idToken, {
            VideoWorkspaceId: asset.VideoWorkspaceId,
            FileName: asset.FileName
        });
        if (response.status !== 200) return null;

        const url = response.myJson.UploadUrl;
        setVideoUrlCache(prev => new Map(prev.set(cacheKey, { url, timestamp: Date.now() })));
        return url;
    }, [assets, videoUrlCache, getIdToken]);

    // Open the text-overlay editor for a video asset, resolving a signed URL to
    // preview against. Reuses the same asset-URL fetch/cache as preview.
    // Resolve a signed preview URL for a video asset (reusing the same fetch and
    // cache as asset preview) so an effect-block editor can render it. Returns
    // { ...asset, url } on success, or null (with an error set) otherwise.
    const resolveEditorAsset = async (asset) => {
        const user = auth.currentUser;
        const idToken = await user.getIdToken();

        const cacheKey = `${asset.VideoWorkspaceId}_${asset.FileName}`;
        const cached = videoUrlCache.get(cacheKey);
        if (cached && (Date.now() - cached.timestamp) < ASSET_URL_TTL_MS) {
            return { ...asset, url: cached.url };
        }

        const response = await GetVideoWorkspaceAssetUrl(idToken, {
            VideoWorkspaceId: asset.VideoWorkspaceId,
            FileName: asset.FileName,
        });
        if (response.status === 200) {
            const assetUrl = response.myJson.UploadUrl;
            setVideoUrlCache(prev => new Map(prev.set(cacheKey, { url: assetUrl, timestamp: Date.now() })));
            return { ...asset, url: assetUrl };
        }
        if (response.status === 409) {
            setError('This file is still uploading. Try again when its upload finishes.');
        } else {
            setError('Failed to open the editor for this asset.');
        }
        return null;
    };

    const handleAddText = async (asset) => {
        try {
            const resolved = await resolveEditorAsset(asset);
            if (resolved) setTextEditorAsset(resolved);
        } catch (err) {
            setError('Error opening text editor: ' + err.message);
        }
    };

    const handleAddBox = async (asset) => {
        try {
            const resolved = await resolveEditorAsset(asset);
            if (resolved) setBoxEditorAsset(resolved);
        } catch (err) {
            setError('Error opening box editor: ' + err.message);
        }
    };

    const handleAddCrop = async (asset) => {
        try {
            const resolved = await resolveEditorAsset(asset);
            if (resolved) setCropEditorAsset(resolved);
        } catch (err) {
            setError('Error opening crop editor: ' + err.message);
        }
    };

    const handleAddZoom = async (asset) => {
        try {
            const resolved = await resolveEditorAsset(asset);
            if (resolved) setZoomEditorAsset(resolved);
        } catch (err) {
            setError('Error opening zoom editor: ' + err.message);
        }
    };

    const handleAddSticker = async (asset) => {
        try {
            const resolved = await resolveEditorAsset(asset);
            if (resolved) setStickerEditorAsset(resolved);
        } catch (err) {
            setError('Error opening sticker editor: ' + err.message);
        }
    };

    const handleAddFade = async (asset) => {
        try {
            const resolved = await resolveEditorAsset(asset);
            if (resolved) setFadeEditorAsset(resolved);
        } catch (err) {
            setError('Error opening fade editor: ' + err.message);
        }
    };

    const handleAddFlip = async (asset) => {
        try {
            const resolved = await resolveEditorAsset(asset);
            if (resolved) setFlipEditorAsset(resolved);
        } catch (err) {
            setError('Error opening flip editor: ' + err.message);
        }
    };

    const handleAddLook = async (asset) => {
        try {
            const resolved = await resolveEditorAsset(asset);
            if (resolved) setLookEditorAsset(resolved);
        } catch (err) {
            setError('Error opening color editor: ' + err.message);
        }
    };

    // The generated-graphic overlays share one editor; the menu item picks which
    // template it opens on.
    const handleAddOverlay = (templateId) => async (asset) => {
        try {
            const resolved = await resolveEditorAsset(asset);
            if (resolved) setOverlayEditorAsset({ ...resolved, templateId });
        } catch (err) {
            setError('Error opening overlay editor: ' + err.message);
        }
    };

    const handleSuggestEffects = async (asset) => {
        try {
            const resolved = await resolveEditorAsset(asset);
            if (resolved) setSuggestEditorAsset(resolved);
        } catch (err) {
            setError('Error opening AI suggestions: ' + err.message);
        }
    };

    // Route a picked AI suggestion into the matching block editor, seeded with
    // its params + rationale. The suggestions panel already holds the resolved
    // asset (url), so reuse it rather than resolving again.
    const openSuggestionInEditor = (suggestion) => {
        const base = suggestEditorAsset;
        if (!base) return;
        const seeded = {
            ...base,
            aiSuggestion: { params: suggestion.params || {}, rationale: suggestion.ai?.rationale || '' },
        };
        setSuggestEditorAsset(null);
        const openers = {
            text: setTextEditorAsset,
            box: setBoxEditorAsset,
            crop: setCropEditorAsset,
            zoom: setZoomEditorAsset,
            fade: setFadeEditorAsset,
            flip: setFlipEditorAsset,
            look: setLookEditorAsset,
        };
        const open = openers[suggestion.type];
        if (open) open(seeded);
        else setError(`Unsupported suggested effect: ${suggestion.type}`);
    };

    // Effects live behind one per-asset dropdown; close it after a pick.
    const closeEffectsMenu = (e) => {
        const details = e.currentTarget.closest('details');
        if (details) details.open = false;
    };

    // The effect blocks available for a video asset. New blocks are added here.
    const effectMenuItems = [
        { label: 'Suggest effects (AI)', Icon: Sparkles, fn: handleSuggestEffects },
        { label: 'Text', Icon: Type, fn: handleAddText },
        { label: 'Box', Icon: Square, fn: handleAddBox },
        { label: 'Crop', Icon: Crop, fn: handleAddCrop },
        { label: 'Zoom', Icon: ZoomIn, fn: handleAddZoom },
        { label: 'Sticker', Icon: Sticker, fn: handleAddSticker },
        { label: 'Phone notification', Icon: Bell, fn: handleAddOverlay('notification') },
        { label: 'Call screen', Icon: Phone, fn: handleAddOverlay('call') },
        { label: 'Location card', Icon: MapPin, fn: handleAddOverlay('location') },
        { label: 'Fade', Icon: Contrast, fn: handleAddFade },
        { label: 'Flip', Icon: FlipHorizontal2, fn: handleAddFlip },
        { label: 'Color', Icon: Palette, fn: handleAddLook },
    ];

    const handlePreviewAsset = async (asset) => {
        try {
            const user = auth.currentUser;
            const idToken = await user.getIdToken();

            // Check cache first
            const cacheKey = `${asset.VideoWorkspaceId}_${asset.FileName}`;
            if (videoUrlCache.has(cacheKey)) {
                const cachedUrl = videoUrlCache.get(cacheKey);
                // Check if URL is still valid (not expired)
                const urlAge = Date.now() - cachedUrl.timestamp;
                if (urlAge < ASSET_URL_TTL_MS) {
                    setPreviewAsset({
                        ...asset,
                        url: cachedUrl.url
                    });
                    return;
                }
            }

            const assetRequest = {
                VideoWorkspaceId: asset.VideoWorkspaceId,
                FileName: asset.FileName
            };

            const response = await GetVideoWorkspaceAssetUrl(idToken, assetRequest);
            
            if (response.status === 200) {
                const url = response.myJson.UploadUrl;
                
                // Cache the URL
                setVideoUrlCache(prev => new Map(prev.set(cacheKey, {
                    url: url,
                    timestamp: Date.now()
                })));
                
                setPreviewAsset({
                    ...asset,
                    url: url
                });
            } else if (response.status === 409) {
                setError('This file is still uploading. Try again when its upload finishes.');
            } else {
                setError('Failed to get asset URL');
            }
        } catch (err) {
            setError('Error getting asset URL: ' + err.message);
        }
    };

    const handleDownloadAsset = async (asset) => {
        try {
            const user = auth.currentUser;
            const idToken = await user.getIdToken();

            const assetRequest = {
                VideoWorkspaceId: asset.VideoWorkspaceId,
                FileName: asset.FileName
            };

            const response = await GetVideoWorkspaceAssetUrl(idToken, assetRequest);
            
            if (response.status === 200) {
                // Create a temporary link to trigger download
                const link = document.createElement('a');
                link.href = response.myJson.UploadUrl;
                link.download = asset.FileAlias || asset.FileName;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else if (response.status === 409) {
                setError('This file is still uploading. Try again when its upload finishes.');
            } else {
                setError('Failed to get asset download URL');
            }
        } catch (err) {
            setError('Error downloading asset: ' + err.message);
        }
    };

    // One drawn icon per kind of file. The emoji these replace varied by
    // format for no reason a user could use, and four of them were broken
    // characters that rendered as a replacement box.
    const getFileIcon = (fileType) => {
        const type = String(fileType || '');
        if (type.startsWith('video/')) return <Film size={16} aria-hidden="true" />;
        if (type.startsWith('audio/')) return <Music size={16} aria-hidden="true" />;
        if (type.startsWith('image/')) return <ImageIcon size={16} aria-hidden="true" />;
        return <FileText size={16} aria-hidden="true" />;
    };

    const renderAssetPreview = (asset) => {
        if (!asset.url) return null;

        if (asset.FileType.startsWith('video/')) {
            return (
                <video 
                    key={asset.url} // Force re-render when URL changes
                    src={asset.url} 
                    controls 
                    preload="metadata"
                    className="asset-preview-media"
                    onError={() => setError('Error loading video preview')}
                />
            );
        }
        
        if (asset.FileType.startsWith('audio/')) {
            return (
                <audio 
                    src={asset.url} 
                    controls 
                    className="asset-preview-media"
                    onError={() => setError('Error loading audio preview')}
                />
            );
        }
        
        if (asset.FileType.startsWith('image/')) {
            // Handle SVG differently for better compatibility
            if (asset.FileType.includes('svg')) {
                return (
                    <div className="asset-preview-svg-container">
                        <object 
                            data={asset.url} 
                            type="image/svg+xml"
                            className="asset-preview-media"
                            aria-label={asset.FileAlias || asset.FileName}
                        >
                            <img 
                                src={asset.url} 
                                alt={asset.FileAlias || asset.FileName}
                                className="asset-preview-media"
                                onError={() => setError('Error loading SVG preview')}
                            />
                        </object>
                    </div>
                );
            }
            
            return (
                <img 
                    src={asset.url} 
                    alt={asset.FileAlias || asset.FileName}
                    className="asset-preview-media"
                    onError={() => setError('Error loading image preview')}
                />
            );
        }
        
        // Handle document previews
        if (asset.FileType.includes('pdf')) {
            return (
                <div className="asset-preview-document">
                    <iframe 
                        src={asset.url}
                        className="asset-preview-media"
                        title={asset.FileAlias || asset.FileName}
                        onError={() => setError('Error loading PDF preview')}
                    />
                    <p className="document-preview-note">
                        PDF document: <a href={asset.url} target="_blank" rel="noopener noreferrer">Open in new tab</a>
                    </p>
                </div>
            );
        }
        
        if (asset.FileType.includes('text')) {
            return (
                <div className="asset-preview-document">
                    <iframe 
                        src={asset.url}
                        className="asset-preview-text"
                        title={asset.FileAlias || asset.FileName}
                        onError={() => setError('Error loading text preview')}
                    />
                    <p className="document-preview-note">
                        Text document: <a href={asset.url} target="_blank" rel="noopener noreferrer">Open in new tab</a>
                    </p>
                </div>
            );
        }
        
        // For other document types (Word, RTF, etc.)
        if (asset.FileType.includes('word') || asset.FileType.includes('doc') || asset.FileType.includes('rtf')) {
            return (
                <div className="asset-preview-document">
                    <div className="document-placeholder">
                        <span className="document-icon">{getFileIcon(asset.FileType)}</span>
                        <p>{asset.FileAlias || asset.FileName}</p>
                        <p className="file-type">{asset.FileType}</p>
                        <a href={asset.url} target="_blank" rel="noopener noreferrer" className="download-link">
                            <Download size={16} aria-hidden="true" /> Download file
                        </a>
                    </div>
                </div>
            );
        }

        return (
            <div className="asset-preview-generic">
                <div className="file-placeholder">
                    <span className="file-icon">{getFileIcon(asset.FileType)}</span>
                    <p>{asset.FileAlias || asset.FileName}</p>
                    <p className="file-type">{asset.FileType}</p>
                    <a href={asset.url} target="_blank" rel="noopener noreferrer" className="download-link">
                        <Download size={16} aria-hidden="true" /> Download file
                    </a>
                </div>
            </div>
        );
    };

    const handleProcessVideo = async () => {
        try {
            if (!selectedWorkspace || !processPrompt.trim()) return;
            if (isSelectedWorkspacePosted) {
                setError('This video has been posted, so its edit is locked.');
                return;
            }

            if (!IS_AI_AGENT_ENABLED) {
                setError('AI video editing is temporarily unavailable.');
                setShowProcessModal(false);
                return;
            }

            // Instead of using traditional processing, use the AI chat workflow
            setShowProcessModal(false);
            setShowAIChat(true);
            
            // Ensure AI connection is established
            if (!isAiConnected) {
                await connectToAI();
                // Give a moment for connection to establish
                setTimeout(() => {
                    if (isAiConnected && aiWebSocket) {
                        const message = {
                            type: 'text',
                            content: processPrompt.trim()
                        };
                        aiWebSocket.send(JSON.stringify(message));
                        setAiChatInput('');
                    }
                }, 1000);
            } else if (aiWebSocket) {
                // Send the prompt directly to AI
                const message = {
                    type: 'text',
                    content: processPrompt.trim()
                };
                aiWebSocket.send(JSON.stringify(message));
            }
            
            setProcessPrompt('');
            setError('');
            
        } catch (err) {
            setError('Error starting AI video processing: ' + err.message);
            console.error('Process video error:', err);
        }
    };

    /* There is one processing document per workspace and every render rewrites
       it, so a status only means something next to the run it belongs to. The
       render we queued announces its run id over the socket; a document still
       carrying a different id is the previous render's ending, and showing it
       is exactly the bug -- a freshly queued render reporting "Completed"
       before it had done any work.

       The old code fetched once and never looked again, so whatever it read in
       that first moment -- usually the last run's result, because the queue
       write had not landed yet -- is what the user stared at for the rest of
       the render. */
    const activeRunIdRef = useRef(null);
    const pollTimerRef = useRef(null);
    const pollDeadlineRef = useRef(0);
    const checkProcessingStatusRef = useRef(null);

    const stopProcessingPoll = useCallback(() => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    const checkProcessingStatus = useCallback(async (workspaceId, options = {}) => {
        const poll = Boolean(options.poll);
        const scheduleNext = () => {
            if (!poll) return;
            if (Date.now() > pollDeadlineRef.current) {
                stopProcessingPoll();
                return;
            }
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = setTimeout(() => {
                checkProcessingStatusRef.current?.(workspaceId, { poll: true });
            }, POLL_INTERVAL_MS);
        };

        try {
            const user = auth.currentUser;
            if (!user) return;

            const idToken = await getIdToken();
            const response = await GetProcessStatus(idToken, workspaceId);

            if (response.status !== 200) {
                scheduleNext();
                return;
            }

            const status = response.myJson;
            const expectedRunId = activeRunIdRef.current;

            /* Waiting on a named run: anything else is a different render's
               result and is not ours to display, however recent it looks. With
               no run in flight (a page load, say) the last known status is the
               honest thing to show. */
            if (expectedRunId && status?.RunId !== expectedRunId) {
                scheduleNext();
                return;
            }

            setProcessingStatus(status);

            if (status?.Status === 'Completed' || status?.Status === 'Failed') {
                stopProcessingPoll();
                activeRunIdRef.current = null;
                setIsAiProcessing(false);
                if (status.Status === 'Completed') {
                    await loadWorkspaces();
                }
                return;
            }

            scheduleNext();
        } catch (err) {
            // A workspace that has never been processed has no document; that is
            // not an error, and it must not wipe a run we are actively watching.
            if (!activeRunIdRef.current) {
                setProcessingStatus(null);
            }
            scheduleNext();
        }
    }, [auth, getIdToken, loadWorkspaces, stopProcessingPoll]);

    checkProcessingStatusRef.current = checkProcessingStatus;

    // Stop polling when the component goes away.
    useEffect(() => stopProcessingPoll, [stopProcessingPoll]);

    const handleUploadOutput = async () => {
        try {
            if (!selectedWorkspace || !outputFile) return;
            if (isSelectedWorkspacePosted) {
                setError('This video has been posted, so its edit is locked.');
                return;
            }

            setIsUploadingOutput(true);
            setOutputUploadProgress('Getting upload URL...');
            setError('');
            
            const user = auth.currentUser;
            if (!user) return;

            const idToken = await user.getIdToken();
            
            // Get upload URL
            const uploadUrlResponse = await GetVideoWorkspaceOutputUploadUrl(idToken, selectedWorkspace.VideoWorkspaceId);
            
            if (uploadUrlResponse.status !== 200) {
                setError('Failed to get upload URL: ' + (uploadUrlResponse.myJson?.detail || 'Unknown error'));
                return;
            }

            const uploadUrl = uploadUrlResponse.myJson.UploadUrl;
            
            setOutputUploadProgress('Uploading file...');
            
            // Upload file
            await UploadFile(uploadUrl, outputFile, null);

            setOutputUploadProgress('Finalizing uploaded version...');
            const finalizeUploadResponse = await FinalizeVideoWorkspaceUpload(idToken, selectedWorkspace.VideoWorkspaceId);
            if (finalizeUploadResponse.status !== 200) {
                setError('Couldn’t save this video as a new version: ' + (finalizeUploadResponse.myJson?.detail || 'Unknown error'));
                setOutputUploadProgress(null);
                return;
            }

            const uploadedVersion = Number.parseInt(finalizeUploadResponse.myJson?.UploadedVersion, 10);
            if (!Number.isNaN(uploadedVersion)) {
                setSelectedWorkspace((prev) => {
                    if (!prev || prev.VideoWorkspaceId !== selectedWorkspace.VideoWorkspaceId) {
                        return prev;
                    }

                    return {
                        ...prev,
                        UploadedVersion: uploadedVersion,
                    };
                });
            }
            
            setOutputUploadProgress('Upload completed!');
            
            // Clear form and close modal
            setTimeout(() => {
                setShowOutputModal(false);
                setOutputFile(null);
                setOutputUploadProgress(null);
                
                // Refresh processing status to reflect the uploaded output
                if (selectedWorkspace) {
                    checkProcessingStatus(selectedWorkspace.VideoWorkspaceId);
                    loadWorkspaces();
                }
            }, 1000);
            
        } catch (err) {
            setError('Error uploading output: ' + err.message);
            setOutputUploadProgress(null);
        } finally {
            setIsUploadingOutput(false);
        }
    };

    const handleFinalizeVersion = async () => {
        try {
            if (!selectedWorkspace) return;
            if (isSelectedWorkspacePosted) {
                setError('This video has been posted, so its edit is locked.');
                return;
            }

            setIsFinalizing(true);
            setError('');
            
            const user = auth.currentUser;
            if (!user) return;

            const idToken = await user.getIdToken();
            const response = await FinalizeVideoWorkspaceVersion(idToken, selectedWorkspace.VideoWorkspaceId);
            
            if (response.status === 200) {
                // Refresh processing status and workspace data
                await checkProcessingStatus(selectedWorkspace.VideoWorkspaceId);
                // No confirmation dialog: the version number on screen has just
                // changed, which says the same thing without a click to dismiss.
                await loadWorkspaces();
            } else {
                setError('Failed to publish version: ' + (response.myJson?.detail || 'Unknown error'));
            }
        } catch (err) {
            setError('Error publishing version: ' + err.message);
        } finally {
            setIsFinalizing(false);
        }
    };

    const handleGetVersionOutput = async () => {
        try {
            if (!selectedWorkspace || !selectedVersion) return;

            setError('');
            
            const user = auth.currentUser;
            if (!user) return;

            const idToken = await user.getIdToken();
            const response = await GetVideoWorkspaceOutputUrl(idToken, selectedWorkspace.VideoWorkspaceId, selectedVersion);
            
            if (response.status === 200) {
                // Open the output URL in a new tab
                window.open(response.myJson.OutputUrl, '_blank');
                setShowVersionModal(false);
                setSelectedVersion('');
            } else {
                setError('Failed to get version output: ' + (response.myJson?.detail || 'Unknown error'));
            }
        } catch (err) {
            setError('Error getting version output: ' + err.message);
        }
    };

    // WebSocket streaming comparison function
    const handleStreamingComparison = async () => {
        try {
            if (!selectedWorkspace || !selectedFromVersion || !selectedToVersion) return;

            setIsStreamingComparison(true);
            setStreamingProgress(0);
            setStreamingStatus('Connecting to streaming service...');
            setStreamedChanges([]);
            setError('');
            
            const user = auth.currentUser;
            if (!user) return;

            const idToken = await user.getIdToken();
            
            // Close existing WebSocket if any
            if (websocketRef) {
                websocketRef.close();
                setWebsocketRef(null);
            }
            
            // Create WebSocket connection using the API helper
            const ws = createVideoWorkspaceStreamingConnection(
                selectedWorkspace.VideoWorkspaceId, 
                selectedFromVersion, 
                selectedToVersion
            );
            
            setWebsocketRef(ws);
            
            ws.onopen = () => {
                setStreamingStatus('Connected! Starting video analysis...');
                const impersonateUserId = String(localStorage.getItem('vixo_impersonate_user_id') || '').trim();
                const impersonateUserEmail = String(localStorage.getItem('vixo_impersonate_user_email') || '').trim().toLowerCase();
                ws.send(JSON.stringify({
                    type: 'auth',
                    token: idToken,
                    impersonate_user_id: impersonateUserId || undefined,
                    impersonate_user_email: impersonateUserEmail || undefined,
                }));
            };
            
            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    
                    switch (data.type) {
                        case 'status':
                            setStreamingStatus(data.message);
                            if (data.progress !== undefined) {
                                setStreamingProgress(data.progress);
                            }
                            break;
                            
                        case 'progress':
                            setStreamingProgress(data.progress);
                            setStreamingStatus(data.message || 'Processing...');
                            break;
                            
                        case 'change':
                            setStreamedChanges(prev => [...prev, {
                                startTime: data.change.StartTime,
                                endTime: data.change.EndTime,
                                type: data.change.ChangeType.toLowerCase().replace(' ', '-'),
                                description: data.change.Description,
                                severity: data.change.Severity || 'medium',
                                confidence: data.change.Confidence || 1.0,
                                timestamp: Date.now()
                            }]);
                            break;
                            
                        case 'warning':
                            console.warn('Streaming warning:', data.message);
                            break;
                            
                        case 'error':
                            setError('Streaming error: ' + data.message);
                            break;
                            
                        default:
                            console.log('Unknown streaming message type:', data.type);
                    }
                } catch (parseError) {
                    console.error('Failed to parse streaming message:', parseError);
                }
            };
            
            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
                setError('Connection error during streaming comparison');
            };
            
            ws.onclose = (event) => {
                setIsStreamingComparison(false);
                setWebsocketRef(null);
                
                if (event.wasClean) {
                    setStreamingStatus('Streaming comparison completed!');
                    setStreamingProgress(100);
                    
                    // Load the video URLs for display after streaming completes
                    loadVersionUrlsForComparison();
                } else {
                    setError('Connection lost during streaming comparison');
                }
            };
            
        } catch (err) {
            setError('Error starting streaming comparison: ' + err.message);
            setIsStreamingComparison(false);
        }
    };
    
    // Load video URLs after streaming comparison
    const loadVersionUrlsForComparison = async () => {
        try {
            const user = auth.currentUser;
            if (!user) return;

            const idToken = await user.getIdToken();
            
            const fromVersionPromise = GetVideoWorkspaceOutputUrl(idToken, selectedWorkspace.VideoWorkspaceId, selectedFromVersion);
            const toVersionPromise = GetVideoWorkspaceOutputUrl(idToken, selectedWorkspace.VideoWorkspaceId, selectedToVersion);
            
            const [fromVersionResponse, toVersionResponse] = await Promise.all([fromVersionPromise, toVersionPromise]);
            
            if (fromVersionResponse.status === 200 && toVersionResponse.status === 200) {
                const comparison = {
                    VideoWorkspaceId: selectedWorkspace.VideoWorkspaceId,
                    FromVersion: {
                        Version: parseInt(selectedFromVersion),
                        Prompt: selectedWorkspace.Prompt,
                        OutputUrl: fromVersionResponse.myJson.OutputUrl
                    },
                    ToVersion: {
                        Version: parseInt(selectedToVersion),
                        Prompt: selectedWorkspace.Prompt,
                        OutputUrl: toVersionResponse.myJson.OutputUrl
                    },
                    HasChanges: streamedChanges.length > 0,
                    Changes: streamedChanges,
                    TotalChanges: streamedChanges.length,
                    ComparisonTimestamp: new Date().toISOString(),
                    StreamingComparison: true
                };

                setVersionComparison(comparison);
                setChangeMarkers(streamedChanges);
            }
        } catch (err) {
            setError('Error loading video URLs: ' + err.message);
        }
    };

    const handleCompareVersions = async () => {
        // Only streaming comparison is supported now
        return handleStreamingComparison();
    };

    // Comments functionality
    const loadComments = useCallback(async (workspaceId) => {
        if (!workspaceId) return;
        
        try {
            setIsLoadingComments(true);
            const user = auth.currentUser;
            if (!user) return;
            
            const idToken = await getIdToken();
            const { status, myJson } = await GetVideoWorkspaceComments(idToken, workspaceId);
            
            if (status === 200) {
                setComments(myJson || []);
            } else {
                setError('Failed to load comments: ' + (myJson?.detail || 'Unknown error'));
            }
        } catch (err) {
            setError('Error loading comments: ' + err.message);
            setComments([]);
        } finally {
            setIsLoadingComments(false);
        }
    }, [auth, getIdToken]);

    useEffect(() => {
        const workspaceId = selectedWorkspace?.VideoWorkspaceId;
        const analysisId = String(selectedWorkspaceAnalysis?.analysisId || '').trim();
        const status = String(selectedWorkspaceAnalysis?.status || '').toLowerCase();

        if (!workspaceId || !analysisId) {
            return;
        }

        if (selectedWorkspaceAnalysis?.results) {
            return;
        }

        if (status && status !== 'linked' && status !== 'queued' && status !== 'processing' && status !== 'completed' && status !== 'unknown' && status !== 'awaiting_upload' && status !== 'uploading' && status !== 'preparing') {
            return;
        }

        let isCancelled = false;
        let intervalId = null;
        const hydrateAnalysis = async () => {
            try {
                const hydrated = await hydrateWorkspaceAnalysis(workspaceId, analysisId);
                if (isCancelled || !hydrated) {
                    return;
                }

                const { analysisResults, nextStatus } = hydrated;

                if (analysisResults || nextStatus === 'completed' || nextStatus === 'failed') {
                    if (intervalId) {
                        clearInterval(intervalId);
                        intervalId = null;
                    }
                }
            } catch (err) {
                if (isCancelled) {
                    return;
                }

                updateWorkspaceAnalysis(workspaceId, {
                    analysisId,
                    status: 'failed',
                    error: err.message || 'Failed to load linked analysis',
                });

                if (intervalId) {
                    clearInterval(intervalId);
                    intervalId = null;
                }
            }
        };

        hydrateAnalysis();
        intervalId = setInterval(hydrateAnalysis, 2500);

        return () => {
            isCancelled = true;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [selectedWorkspace?.VideoWorkspaceId, selectedWorkspaceAnalysis?.analysisId, selectedWorkspaceAnalysis?.status, selectedWorkspaceAnalysis?.results, hydrateWorkspaceAnalysis, updateWorkspaceAnalysis]);

    useEffect(() => {
        const workspaceId = selectedWorkspace?.VideoWorkspaceId;
        const status = String(selectedWorkspaceAnalysis?.status || '').toLowerCase();

        if (!workspaceId || status !== 'completed') {
            return;
        }

        loadComments(workspaceId).catch((err) => {
            setError(err.message || 'Failed to refresh comments');
        });
    }, [selectedWorkspace?.VideoWorkspaceId, selectedWorkspaceAnalysis?.status, loadComments]);

    useEffect(() => {
        if (!requestedWorkspaceId || workspaces.length === 0) {
            return;
        }

        const requestedWorkspace = workspaces.find(
            ws => ws.VideoWorkspaceId === requestedWorkspaceId
        );

        if (!requestedWorkspace) {
            return;
        }

        setSelectedWorkspace(requestedWorkspace);
        setEditingWorkspace({
            Prompt: requestedWorkspace.Prompt,
            VideoWorkspaceId: requestedWorkspace.VideoWorkspaceId
        });
        loadAssets(requestedWorkspace.VideoWorkspaceId);
        loadComments(requestedWorkspace.VideoWorkspaceId);
    }, [requestedWorkspaceId, workspaces, loadAssets, loadComments]);

    // Load latest version video for comments
    const loadCommentVideo = useCallback(async (workspaceOverride = null) => {
        const workspace = workspaceOverride || selectedWorkspace;
        if (!workspace) return null;
        
        try {
            setIsLoadingCommentVideo(true);
            setCommentVideoUrl(null);
            const user = auth.currentUser;
            if (!user) {
                setError('User not authenticated');
                return null;
            }
            
            const idToken = await getIdToken();
            
            const workspaceId = workspace.VideoWorkspaceId;
            
            console.log(`Loading comment video for workspace ${workspaceId}:`);
            console.log('Workspace UploadedVersion:', workspace.UploadedVersion);
            console.log('Workspace data:', workspace);
            
            const latestUploadedVersion = Number.parseInt(workspace.UploadedVersion, 10);
            if (Number.isNaN(latestUploadedVersion) || latestUploadedVersion < 0) {
                setError('There’s no rendered video to work with yet. Render the edit first, or upload a finished video.');
                return null;
            }

            const latestProcessedVersion = latestUploadedVersion;
            
            // Check cache first
            const cacheKey = `${workspaceId}_output_v${latestProcessedVersion}`;
            if (videoUrlCache.has(cacheKey)) {
                const cachedUrl = videoUrlCache.get(cacheKey);
                const urlAge = Date.now() - cachedUrl.timestamp;
                if (urlAge < ASSET_URL_TTL_MS) {
                    console.log('Using cached video URL:', cachedUrl.url);
                    setCommentVideoUrl(cachedUrl.url);
                    setError('');
                    return cachedUrl.url;
                }
            }
            
            console.log(`Attempting to load video for workspace ${workspaceId}, latest processed version: ${latestProcessedVersion}`);
            console.log(`Available versions should be: v0 to v${latestProcessedVersion}`);
            
            const response = await GetVideoWorkspaceOutputUrl(idToken, workspaceId, latestProcessedVersion);
            
            if (response.status === 200 && response.myJson) {
                console.log('Video URL loaded successfully:', response.myJson);
                const videoUrl = response.myJson.OutputUrl;
                
                // Cache the URL
                setVideoUrlCache(prev => new Map(prev.set(cacheKey, {
                    url: videoUrl,
                    timestamp: Date.now()
                })));
                
                setCommentVideoUrl(videoUrl);
                // Clear any previous errors
                setError('');
                return videoUrl;
            } else {
                const errorMsg = response.myJson?.detail || `Failed to get video URL (Status: ${response.status})`;
                console.error('Failed to load video:', errorMsg);
                console.error('Response:', response);
                setError(`Failed to load video v${latestProcessedVersion} for comments: ${errorMsg}. Check if this version exists.`);
                return null;
            }
        } catch (err) {
            console.error('Error loading comment video:', err);
            setError('Error loading comment video: ' + err.message);
            return null;
        } finally {
            setIsLoadingCommentVideo(false);
        }
    }, [auth, selectedWorkspace, videoUrlCache, getIdToken]);

    const loadUploadedPreviewVideo = useCallback(async (workspaceOverride = null) => {
        const workspace = workspaceOverride || selectedWorkspace;
        if (!workspace) return null;

        const uploadedVersion = Number.parseInt(workspace.UploadedVersion, 10);
        if (Number.isNaN(uploadedVersion) || uploadedVersion < 0) {
            setUploadedPreviewUrl(null);
            setUploadedPreviewError('');
            return null;
        }

        try {
            setIsLoadingUploadedPreview(true);
            setUploadedPreviewError('');

            const user = auth.currentUser;
            if (!user) {
                setUploadedPreviewError('User not authenticated');
                return null;
            }

            const idToken = await getIdToken();
            const workspaceId = workspace.VideoWorkspaceId;
            const cacheKey = `${workspaceId}_output_v${uploadedVersion}`;

            if (videoUrlCache.has(cacheKey)) {
                const cachedUrl = videoUrlCache.get(cacheKey);
                const urlAge = Date.now() - cachedUrl.timestamp;
                if (urlAge < ASSET_URL_TTL_MS) {
                    setUploadedPreviewUrl(cachedUrl.url);
                    return cachedUrl.url;
                }
            }

            const response = await GetVideoWorkspaceOutputUrl(idToken, workspaceId, uploadedVersion);
            if (response.status === 200 && response.myJson?.OutputUrl) {
                const videoUrl = response.myJson.OutputUrl;
                setVideoUrlCache(prev => new Map(prev.set(cacheKey, {
                    url: videoUrl,
                    timestamp: Date.now()
                })));
                setUploadedPreviewUrl(videoUrl);
                return videoUrl;
            }

            const errorMsg = response.myJson?.detail || `Failed to get video URL (Status: ${response.status})`;
            setUploadedPreviewUrl(null);
            setUploadedPreviewError(`Preview unavailable for v${uploadedVersion}: ${errorMsg}`);
            return null;
        } catch (err) {
            setUploadedPreviewUrl(null);
            setUploadedPreviewError('Error loading uploaded preview: ' + err.message);
            return null;
        } finally {
            setIsLoadingUploadedPreview(false);
        }
    }, [auth, selectedWorkspace, videoUrlCache, getIdToken]);

    const stopReviewPolling = useCallback(() => {
        if (reviewPollingRef.current) {
            clearInterval(reviewPollingRef.current);
            reviewPollingRef.current = null;
        }
    }, []);

    const fetchReviewStatus = useCallback(async (workspaceId, showError = false) => {
        const targetWorkspaceId = workspaceId || selectedWorkspace?.VideoWorkspaceId;
        if (!targetWorkspaceId) return null;

        try {
            const user = auth.currentUser;
            if (!user) return null;

            const idToken = await getIdToken();
            const { status, myJson } = await GetVideoWorkspaceReviewStatus(idToken, targetWorkspaceId);
            if (status === 200 && myJson) {
                setReviewStatus(myJson);
                const state = String(myJson.Status || '').toLowerCase();
                updateWorkspaceAnalysis(targetWorkspaceId, {
                    status: state || 'idle',
                    analysisId: String(myJson.AnalysisId || '').trim() || null,
                    sourceVersion: typeof myJson.SourceVersion === 'number' ? myJson.SourceVersion : null,
                    generatedThumbnailBlobName: String(myJson.GeneratedThumbnailBlobName || '').trim(),
                    generatedThumbnailFileName: String(myJson.GeneratedThumbnailFileName || '').trim(),
                    generatedThumbnailSourceVersion: typeof myJson.GeneratedThumbnailSourceVersion === 'number' ? myJson.GeneratedThumbnailSourceVersion : null,
                    error: String(myJson.ErrorMessage || '').trim(),
                    commentsAdded: Number(myJson.CommentsAdded || 0),
                });
                if (state === 'completed' || state === 'failed' || state === 'idle') {
                    stopReviewPolling();
                }
                if (state === 'completed') {
                    const analysisId = String(myJson.AnalysisId || '').trim();
                    if (analysisId) {
                        await hydrateWorkspaceAnalysis(targetWorkspaceId, analysisId);
                    }
                    await loadComments(targetWorkspaceId);
                }
                return myJson;
            }

            if (showError) {
                setError('Failed to fetch review status: ' + (myJson?.detail || 'Unknown error'));
            }
            return null;
        } catch (err) {
            if (showError) {
                setError('Error fetching review status: ' + err.message);
            }
            return null;
        }
    }, [auth, hydrateWorkspaceAnalysis, getIdToken, selectedWorkspace?.VideoWorkspaceId, loadComments, stopReviewPolling, updateWorkspaceAnalysis]);

    const startReviewPolling = useCallback((workspaceId) => {
        stopReviewPolling();
        const targetWorkspaceId = workspaceId || selectedWorkspace?.VideoWorkspaceId;
        if (!targetWorkspaceId) return;

        reviewPollingRef.current = setInterval(() => {
            fetchReviewStatus(targetWorkspaceId, false);
        }, 5000);
    }, [fetchReviewStatus, selectedWorkspace?.VideoWorkspaceId, stopReviewPolling]);

    const runWorkspaceAnalysis = useCallback(async ({ throwOnError = false } = {}) => {
        if (!selectedWorkspace?.VideoWorkspaceId) {
            return null;
        }

        const workspaceId = selectedWorkspace.VideoWorkspaceId;
        const uploadedVersion = Number.parseInt(selectedWorkspace?.UploadedVersion, 10);
        const sourceVersion = Number.isNaN(uploadedVersion) || uploadedVersion < 0
            ? getLatestPublishedVersion(selectedWorkspace)
            : uploadedVersion;
        if (sourceVersion === null) {
            const noVersionError = new Error('Upload an output version before running video analysis.');
            setError(noVersionError.message);
            if (throwOnError) {
                throw noVersionError;
            }
            return null;
        }

        try {
            setError('');
            updateWorkspaceAnalysis(workspaceId, {
                status: 'queued',
                error: '',
                results: null,
                analysisId: null,
                sourceVersion,
                startedAt: new Date().toISOString()
            });

            const idToken = await getIdToken();
            const sourceType = uploadedVersion >= 0 ? 'uploaded' : 'latest_published';
            const createResult = await QueueVideoWorkspaceReview(idToken, {
                VideoWorkspaceId: workspaceId,
                LlmProvider: 'anthropic',
                SourceVersion: sourceVersion,
                SourceType: sourceType,
            });

            if (createResult.status !== 200) {
                throw new Error(createResult.myJson?.detail || 'Failed to queue analysis');
            }

            const analysisId = String(createResult.myJson?.AnalysisId || '').trim();
            if (!analysisId) {
                throw new Error('Analysis service did not return an analysis id');
            }

            updateWorkspaceAnalysis(workspaceId, {
                analysisId,
                status: String(createResult.myJson?.Status || 'queued').toLowerCase() || 'queued',
                sourceVersion
            });

            setSelectedWorkspace((prev) => {
                if (!prev || prev.VideoWorkspaceId !== workspaceId) return prev;
                return { ...prev, AnalysisId: analysisId };
            });

            startReviewPolling(workspaceId);
            return analysisId;
        } catch (analysisError) {
            updateWorkspaceAnalysis(workspaceId, {
                status: 'failed',
                error: analysisError.message || 'Couldn’t analyze this video. Try again in a moment.'
            });
            if (throwOnError) {
                throw analysisError;
            }
            return null;
        }
    }, [selectedWorkspace, getLatestPublishedVersion, updateWorkspaceAnalysis, getIdToken, startReviewPolling]);

    const handleAddComment = async () => {
        if (!selectedWorkspace || !newComment.comment.trim()) return;
        
        try {
            const user = auth.currentUser;
            if (!user) return;
            
            const idToken = await getIdToken();
            const commentData = {
                VideoWorkspaceId: selectedWorkspace.VideoWorkspaceId,
                CommentId: editingComment ? editingComment.CommentId : '',
                Comment: newComment.comment,
                VideoTimestamp: newComment.videoTimestamp,
                VideoEndTimestamp: newComment.videoEndTimestamp,
                State: 'Open'
            };
            
            const { status, myJson } = await AddUpdateVideoWorkspaceComment(idToken, commentData);
            
            if (status === 200) {
                await loadComments(selectedWorkspace.VideoWorkspaceId);
                setNewComment({ comment: '', videoTimestamp: 0, videoEndTimestamp: 0 });
                setTimeInputFormat({ start: '0:00.00', end: '0:00.00' });
                setEditingComment(null);
            } else {
                setError('Failed to save comment: ' + (myJson?.detail || 'Unknown error'));
            }
        } catch (err) {
            setError('Error saving comment: ' + err.message);
        }
    };

    const handleEditComment = (comment) => {
        setEditingComment(comment);
        setNewComment({
            comment: comment.Comment,
            videoTimestamp: comment.VideoTimestamp,
            videoEndTimestamp: comment.VideoEndTimestamp || comment.VideoTimestamp
        });
        setTimeInputFormat({
            start: secondsToTimeFormat(comment.VideoTimestamp),
            end: secondsToTimeFormat(comment.VideoEndTimestamp || comment.VideoTimestamp)
        });
    };

    const handleDeleteComment = async (commentId) => {
        if (!commentId) return;
        
        try {
            const user = auth.currentUser;
            if (!user) return;
            
            const idToken = await getIdToken();
            const { status, myJson } = await DeleteVideoWorkspaceComment(idToken, commentId);
            
            if (status === 200) {
                await loadComments(selectedWorkspace.VideoWorkspaceId);
            } else {
                setError('Failed to delete comment: ' + (myJson?.detail || 'Unknown error'));
            }
        } catch (err) {
            setError('Error deleting comment: ' + err.message);
        }
    };

    const handleCancelEditComment = () => {
        setEditingComment(null);
        setNewComment({ comment: '', videoTimestamp: 0, videoEndTimestamp: 0 });
        setTimeInputFormat({ start: '0:00.00', end: '0:00.00' });
    };

    // Video playback controls for comments
    const handleCommentVideoTimeUpdate = useCallback(() => {
        const now = Date.now();
        // Throttle comment video time updates
        if (now - lastTimeUpdate < timeUpdateThrottle) {
            return;
        }
        setLastTimeUpdate(now);
        
        if (commentVideoRef && commentVideoRef.currentTime !== undefined) {
            setCommentVideoCurrentTime(commentVideoRef.currentTime);
        }
    }, [commentVideoRef, lastTimeUpdate, timeUpdateThrottle]);

    const handleCommentVideoLoadedMetadata = () => {
        if (commentVideoRef) {
            setCommentVideoDuration(commentVideoRef.duration);
        }
    };

    const handleCommentVideoPlay = () => {
        setIsCommentVideoPlaying(true);
    };

    const handleCommentVideoPause = () => {
        setIsCommentVideoPlaying(false);
        // Auto-set timestamps when paused (only for new comments)
        if (commentVideoRef && commentVideoRef.currentTime !== undefined && !editingComment) {
            const currentTime = parseFloat(commentVideoRef.currentTime.toFixed(1));
            setNewComment(prev => ({
                ...prev,
                videoTimestamp: prev.videoTimestamp === 0 ? currentTime : prev.videoTimestamp,
                videoEndTimestamp: currentTime
            }));
            console.log(`Video paused at ${currentTime}s - timestamp auto-captured for comment`);
        }
    };

    const seekToCommentTime = (timestamp) => {
        if (commentVideoRef) {
            commentVideoRef.currentTime = timestamp;
            setCommentVideoCurrentTime(timestamp);
        }
    };

    const handleCommentVideoError = (e) => {
        console.error('Video loading error:', e.target.error);
        setError('Failed to load video. Please try again or contact support.');
    };

    const handleCommentVideoCanPlay = () => {
        // Video is ready to play, clear any loading errors
        console.log('Comment video ready to play');
    };

    const formatVideoTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Format all comments for clipboard copy
    const formatCommentsForCopy = () => {
        if (comments.length === 0) return '';
        
        return comments
            .sort((a, b) => a.VideoTimestamp - b.VideoTimestamp)
            .map(comment => {
                const startTime = formatVideoTime(comment.VideoTimestamp);
                const endTime = comment.VideoEndTimestamp && comment.VideoEndTimestamp !== comment.VideoTimestamp 
                    ? formatVideoTime(comment.VideoEndTimestamp)
                    : null;
                const timeRange = endTime ? `${startTime} - ${endTime}` : startTime;
                return `${timeRange}:   ${comment.Comment}`;
            })
            .join('\n');
    };

    // Handle copying all comments to clipboard
    const handleCopyCommentsToClipboard = async () => {
        try {
            const textToCopy = formatCommentsForCopy();
            if (!textToCopy) {
                setError('No comments to copy');
                return;
            }
            await navigator.clipboard.writeText(textToCopy);
            // Show success message
            const previousError = error;
            setError('All comments copied to the clipboard.');
            setTimeout(() => setError(previousError), 2000);
        } catch (err) {
            console.error('Failed to copy comments:', err);
            setError('Failed to copy comments to clipboard');
        }
    };

    // Timeline drag selection functions
    const handleTimelineMouseDown = useCallback((e) => {
        if (!commentVideoDuration || editingComment) return;
        
        const rect = timelineRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
        const time = (percentage / 100) * commentVideoDuration;
        
        setTimelineSelection({
            start: time,
            end: time,
            isSelecting: true
        });
        
        // Update comment timestamps
        setNewComment(prev => ({
            ...prev,
            videoTimestamp: time,
            videoEndTimestamp: time
        }));
        
        // Seek video to this position
        if (commentVideoRef) {
            commentVideoRef.currentTime = time;
        }
    }, [commentVideoDuration, editingComment, commentVideoRef, timelineRef]);

    const handleTimelineMouseMove = useCallback((e) => {
        if (!timelineSelection.isSelecting || !commentVideoDuration || editingComment) return;
        
        const rect = timelineRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
        const time = (percentage / 100) * commentVideoDuration;
        
        const startTime = Math.min(timelineSelection.start, time);
        const endTime = Math.max(timelineSelection.start, time);
        
        setTimelineSelection(prev => ({
            ...prev,
            end: time
        }));
        
        // Update comment timestamps
        setNewComment(prev => ({
            ...prev,
            videoTimestamp: startTime,
            videoEndTimestamp: endTime
        }));
    }, [timelineSelection.isSelecting, timelineSelection.start, commentVideoDuration, editingComment, timelineRef]);

    const handleTimelineMouseUp = useCallback(() => {
        if (!timelineSelection.isSelecting) return;
        
        setTimelineSelection(prev => ({
            ...prev,
            isSelecting: false
        }));
    }, [timelineSelection.isSelecting]);

    const handleTimelineClick = useCallback((e) => {
        if (!commentVideoDuration) return;
        
        const rect = timelineRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
        const time = (percentage / 100) * commentVideoDuration;
        
        // If not creating a selection, just seek to the clicked position
        if (commentVideoRef) {
            commentVideoRef.currentTime = time;
        }
    }, [commentVideoDuration, commentVideoRef, timelineRef]);

    const clearTimelineSelection = useCallback(() => {
        setTimelineSelection({ start: null, end: null, isSelecting: false });
        setNewComment(prev => ({
            ...prev,
            videoTimestamp: 0,
            videoEndTimestamp: 0
        }));
    }, []);

    const renderTimelineSelection = useCallback(() => {
        if (!timelineSelection.start || timelineSelection.start === null || !commentVideoDuration) return null;
        
        const startPercent = (Math.min(timelineSelection.start, timelineSelection.end || timelineSelection.start) / commentVideoDuration) * 100;
        const endPercent = (Math.max(timelineSelection.start, timelineSelection.end || timelineSelection.start) / commentVideoDuration) * 100;
        const width = Math.max(0.5, endPercent - startPercent);
        
        return (
            <div
                className="timeline-selection"
                style={{
                    left: `${startPercent}%`,
                    width: `${width}%`
                }}
            />
        );
    }, [timelineSelection.start, timelineSelection.end, commentVideoDuration]);

    // Handle showing comments modal and loading video
    const handleShowCommentsModal = async () => {
        if (selectedWorkspace) {
            setShowCommentsModal(true);
            setNewComment({ comment: '', videoTimestamp: 0, videoEndTimestamp: 0 });
            setTimeInputFormat({ start: '0:00.00', end: '0:00.00' });

            // Refresh workspace data to get latest draft/published version before loading video.
            const refreshedWorkspaces = await loadWorkspaces();
            const refreshedSelected = (refreshedWorkspaces || []).find(
                ws => ws.VideoWorkspaceId === selectedWorkspace.VideoWorkspaceId
            ) || selectedWorkspace;

            await Promise.all([
                loadComments(selectedWorkspace.VideoWorkspaceId),
                loadCommentVideo(refreshedSelected)
            ]);
        }
    };

    // Synchronized video playback controls
    const handleSyncedPlay = () => {
        if (videoRefs.fromVideo && videoRefs.toVideo) {
            if (isPlayingSynced) {
                videoRefs.fromVideo.pause();
                videoRefs.toVideo.pause();
                setIsPlayingSynced(false);
            } else {
                videoRefs.fromVideo.play();
                videoRefs.toVideo.play();
                setIsPlayingSynced(true);
            }
        }
    };

    const handleSyncedTimeUpdate = useCallback((currentTime) => {
        const now = Date.now();
        // Throttle time updates to reduce frequency
        if (now - lastTimeUpdate < timeUpdateThrottle) {
            return;
        }
        setLastTimeUpdate(now);
        
        setSyncedCurrentTime(currentTime);
        if (videoRefs.fromVideo && videoRefs.toVideo) {
            const timeDiff = Math.abs(videoRefs.fromVideo.currentTime - videoRefs.toVideo.currentTime);
            // Sync videos if they drift apart by more than 0.2 seconds (increased tolerance)
            if (timeDiff > 0.2) {
                const targetTime = Math.min(videoRefs.fromVideo.currentTime, videoRefs.toVideo.currentTime);
                videoRefs.fromVideo.currentTime = targetTime;
                videoRefs.toVideo.currentTime = targetTime;
            }
        }
    }, [lastTimeUpdate, timeUpdateThrottle, videoRefs.fromVideo, videoRefs.toVideo]);

    const handleSeekTo = (time) => {
        if (videoRefs.fromVideo && videoRefs.toVideo) {
            videoRefs.fromVideo.currentTime = time;
            videoRefs.toVideo.currentTime = time;
            setSyncedCurrentTime(time);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const renderTimelineDiff = () => {
        return (
            <div className="timeline-diff-view">
                <div className="timeline-controls">
                    <button 
                        className={`timeline-play-btn ${isPlayingSynced ? 'playing' : 'paused'}`}
                        onClick={handleSyncedPlay}
                    >
                        {isPlayingSynced ? <Pause size={16} aria-hidden="true" /> : <Play size={16} aria-hidden="true" />}
                    </button>
                    <span className="timeline-time">
                        {formatTime(syncedCurrentTime)} / {formatTime(videoDuration)}
                    </span>
                </div>

                <div className="timeline-container">
                    <div className="timeline-track">
                        <div 
                            className="timeline-progress"
                            style={{ width: `${(syncedCurrentTime / videoDuration) * 100}%` }}
                        />
                        <div 
                            className="timeline-scrubber"
                            style={{ left: `${(syncedCurrentTime / videoDuration) * 100}%` }}
                        />
                        
                        {/* Change markers */}
                        {changeMarkers.map((marker, index) => (
                            <div
                                key={index}
                                className={`change-marker marker-${marker.type}`}
                                style={{
                                    left: `${(marker.startTime / videoDuration) * 100}%`,
                                    width: `${((marker.endTime - marker.startTime) / videoDuration) * 100}%`
                                }}
                                title={`${marker.type}: ${marker.description}`}
                                onClick={() => handleSeekTo(marker.startTime)}
                            />
                        ))}
                    </div>
                    
                    <div 
                        className="timeline-clickable"
                        onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const clickX = e.clientX - rect.left;
                            const newTime = (clickX / rect.width) * videoDuration;
                            handleSeekTo(newTime);
                        }}
                    />
                </div>

                <div className="timeline-legend">
                    <div className="legend-item">
                        <span className="legend-color marker-modified"></span>
                        <span>Modified</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-color marker-added"></span>
                        <span>Added</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-color marker-removed"></span>
                        <span>Removed</span>
                    </div>
                </div>

                <div className="synchronized-videos">
                    <div className="sync-video-container">
                        <h5>Version {versionComparison.FromVersion.Version}</h5>
                        <video 
                            ref={(ref) => {
                                if (ref && ref !== videoRefs.fromVideo) {
                                    setVideoRefs(prev => ({ ...prev, fromVideo: ref }));
                                }
                            }}
                            src={versionComparison.FromVersion.OutputUrl}
                            className="sync-video"
                            preload="metadata"
                            onLoadedMetadata={(e) => {
                                setVideoDuration(e.target.duration);
                            }}
                            onTimeUpdate={(e) => handleSyncedTimeUpdate(e.target.currentTime)}
                            onPlay={() => setIsPlayingSynced(true)}
                            onPause={() => setIsPlayingSynced(false)}
                            muted // Prevent audio conflicts during sync
                        />
                    </div>
                    <div className="sync-video-container">
                        <h5>Version {versionComparison.ToVersion.Version}</h5>
                        <video 
                            ref={(ref) => {
                                if (ref && ref !== videoRefs.toVideo) {
                                    setVideoRefs(prev => ({ ...prev, toVideo: ref }));
                                }
                            }}
                            src={versionComparison.ToVersion.OutputUrl}
                            className="sync-video"
                            preload="metadata"
                            onTimeUpdate={(e) => handleSyncedTimeUpdate(e.target.currentTime)}
                            onPlay={() => setIsPlayingSynced(true)}
                            onPause={() => setIsPlayingSynced(false)}
                        />
                    </div>
                </div>

                {/* Change details */}
                <div className="changes-detail-panel">
                    <h4>Changes Overview</h4>
                    {changeMarkers.length === 0 ? (
                        <p>No changes detected between versions</p>
                    ) : (
                        <div className="change-list">
                            {changeMarkers.map((marker, index) => (
                                <div 
                                    key={index} 
                                    className={`change-item change-${marker.type}`}
                                    onClick={() => handleSeekTo(marker.startTime)}
                                >
                                    <div className="change-time">
                                        {formatTime(marker.startTime)} - {formatTime(marker.endTime)}
                                    </div>
                                    <div className="change-description">
                                        <span className={`change-type-badge ${marker.type}`}>
                                            {marker.type.toUpperCase()}
                                        </span>
                                        {marker.description}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const handleShowDiffModal = async () => {
        setShowDiffModal(true);
        // Reset version comparison state
        setVersionComparison(null);
        setSelectedFromVersion('');
        setSelectedToVersion('');
        setSyncedCurrentTime(0);
        setVideoDuration(0);
        setIsPlayingSynced(false);
        setVideoRefs({ fromVideo: null, toVideo: null });
        setChangeMarkers([]);
        setDiffViewMode('side-by-side');
    };

    const handleShowVersionModal = async () => {
        setShowVersionModal(true);
        // Reset selected version
        setSelectedVersion('');
    };

    // Check processing status when workspace is selected
    useEffect(() => {
        const selectedWorkspaceId = selectedWorkspace?.VideoWorkspaceId;
        if (selectedWorkspaceId) {
            checkProcessingStatus(selectedWorkspaceId);
        } else {
            setProcessingStatus(null);
        }
    }, [selectedWorkspace?.VideoWorkspaceId, checkProcessingStatus]);

    // Cleanup video refs when diff modal is closed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!showDiffModal) {
            // Pause any playing videos when modal is closed
            if (videoRefs.fromVideo) {
                videoRefs.fromVideo.pause();
                videoRefs.fromVideo.removeAttribute('src');
                videoRefs.fromVideo.load();
            }
            if (videoRefs.toVideo) {
                videoRefs.toVideo.pause();
                videoRefs.toVideo.removeAttribute('src');
                videoRefs.toVideo.load();
            }
            setIsPlayingSynced(false);
            setSyncedCurrentTime(0);
            setVideoRefs({ fromVideo: null, toVideo: null });
            setVersionComparison(null);
        }
    }, [showDiffModal]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cleanup comment video when modal is closed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!showCommentsModal) {
            // Properly cleanup video resources
            if (commentVideoRef) {
                commentVideoRef.pause();
                commentVideoRef.removeAttribute('src');
                commentVideoRef.load(); // Reset video element
            }
            setCommentVideoUrl(null);
            setCommentVideoRef(null);
            setCommentVideoCurrentTime(0);
            setCommentVideoDuration(0);
            setIsCommentVideoPlaying(false);
            setNewComment({ comment: '', videoTimestamp: 0, videoEndTimestamp: 0 });
            setTimeInputFormat({ start: '0:00.00', end: '0:00.00' });
            setEditingComment(null);
            setIsLoadingCommentVideo(false);
        }
    }, [showCommentsModal]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return () => {
            stopReviewPolling();
        };
    }, [stopReviewPolling]);

    useEffect(() => {
        const workspace = selectedWorkspace;
        if (!workspace?.VideoWorkspaceId) {
            setUploadedPreviewUrl(null);
            setUploadedPreviewError('');
            return;
        }

        const uploadedVersion = Number.parseInt(workspace.UploadedVersion, 10);
        if (Number.isNaN(uploadedVersion) || uploadedVersion < 0) {
            setUploadedPreviewUrl(null);
            setUploadedPreviewError('');
            return;
        }

        loadUploadedPreviewVideo(workspace);
    }, [selectedWorkspace, selectedWorkspace?.VideoWorkspaceId, selectedWorkspace?.UploadedVersion, loadUploadedPreviewVideo]);

    useEffect(() => {
        const workspaceId = selectedWorkspace?.VideoWorkspaceId;
        if (!workspaceId) {
            setReviewStatus(null);
            stopReviewPolling();
            return;
        }

        let isMounted = true;
        const syncReviewState = async () => {
            const statusPayload = await fetchReviewStatus(workspaceId, false);
            if (!isMounted || !statusPayload) {
                return;
            }

            const state = String(statusPayload.Status || '').toLowerCase();
            if (state === 'queued' || state === 'processing' || state === 'running') {
                startReviewPolling(workspaceId);
            } else {
                stopReviewPolling();
            }
        };

        syncReviewState();
        return () => {
            isMounted = false;
        };
    }, [selectedWorkspace?.VideoWorkspaceId, fetchReviewStatus, startReviewPolling, stopReviewPolling]);

    // Handle global mouse events for timeline selection
    useEffect(() => {
        const handleGlobalMouseMove = (e) => {
            if (timelineSelection.isSelecting) {
                handleTimelineMouseMove(e);
            }
        };

        const handleGlobalMouseUp = () => {
            if (timelineSelection.isSelecting) {
                handleTimelineMouseUp();
            }
        };

        if (timelineSelection.isSelecting) {
            document.addEventListener('mousemove', handleGlobalMouseMove);
            document.addEventListener('mouseup', handleGlobalMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleGlobalMouseMove);
            document.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [timelineSelection.isSelecting, handleTimelineMouseMove, handleTimelineMouseUp]);

    // Agentic AI Chat Functions
    const handleAiMessage = useCallback(async (message) => {
        const newMessage = {
            id: Date.now() + Math.random(),
            type: message.type,
            content: message.content,
            timestamp: message.timestamp || new Date().toISOString(),
            metadata: message.metadata || {}
        };
        
        setAiChatMessages(prev => [...prev, newMessage]);

        if (
            message.type === 'ai_question'
            && message.metadata
            && typeof message.metadata === 'object'
            && message.metadata.plan
            && message.metadata.layered_graph
        ) {
            const originalPlan = deepClone(message.metadata.plan);
            const originalLayeredGraph = deepClone(message.metadata.layered_graph);
            const originalScript = String(message.metadata.script || '');

            // A proposal lands in the timeline the user was already working in,
            // so accepting the AI's idea and then adjusting it by hand is one
            // continuous act instead of a mode switch.
            if (Array.isArray(message.metadata.actions) && message.metadata.actions.includes('approve')) {
                setManualGraph((prev) => {
                    preProposalGraphRef.current = deepClone(prev);
                    // The planner emits clips without ids or in/out points.
                    // Migrating here means one shape is in play from the moment
                    // the proposal lands, rather than the timeline and the
                    // script emitter each filling the gaps their own way.
                    return migrateGraph(originalLayeredGraph);
                });
                setPendingApproval({
                    messageId: newMessage.id,
                    plan: originalPlan,
                    prompt: String(message.metadata.prompt || ''),
                    metadata: message.metadata,
                });
            }

            setEditableApprovalPayloads(prev => ({
                ...prev,
                [newMessage.id]: {
                    prompt: String(message.metadata.prompt || ''),
                    originalPlan,
                    originalLayeredGraph,
                    editedPlan: originalPlan,
                    editedLayeredGraph: originalLayeredGraph,
                    originalScript,
                    editedScript: originalScript,
                    scriptEditable: Boolean(message.metadata.script_editable),
                    scriptDirty: false,
                    hasEdits: false,
                }
            }));
        }
        
        /* The queue confirmation names the render we are now waiting on. From
           here every status is checked against this id, so the previous run's
           ending cannot be mistaken for this one's, and polling runs until this
           id reaches a terminal state. */
        if (message?.metadata?.run_id) {
            activeRunIdRef.current = message.metadata.run_id;
            pollDeadlineRef.current = Date.now() + POLL_LIMIT_MS;
            setProcessingStatus(null);
            const workspaceId = selectedWorkspace?.VideoWorkspaceId;
            if (workspaceId) {
                checkProcessingStatusRef.current?.(workspaceId, { poll: true });
            }
        }

        // Handle different message types
        switch (message.type) {
            case 'ai_thinking':
                setIsAiProcessing(true);
                break;
            case 'ai_response':
            case 'ai_question':
            case 'video_ready':
                setIsAiProcessing(false);
                break;
            case 'error':
                setIsAiProcessing(false);
                /* Onto the timeline's own banner as well as into the chat. A
                   manual render is started from the Edit tab, where the chat
                   panel is usually closed -- so a failure the server explained
                   perfectly well ("'Asset' object has no attribute 'get'") was
                   being delivered somewhere nobody was looking, and the click
                   simply appeared to do nothing. */
                setManualNotice('');
                setManualError(
                    String(message.content || 'The render failed.').replace(/^[❌⚠️]\s*/, '')
                );
                break;
            case 'progress_update':
                // Keep processing state for progress updates
                break;
            default:
                setIsAiProcessing(false);
        }
        
        // Auto-scroll to bottom
        setTimeout(() => {
            if (aiChatRef.current) {
                aiChatRef.current.scrollTop = aiChatRef.current.scrollHeight;
            }
        }, 100);
        
        // If video is ready, refresh assets and processing status
        if (message.type === 'video_ready') {
            const workspaceId = newMessage.metadata?.video_workspace_id;
            const nextVersion = Number(newMessage.metadata?.version);

            if (workspaceId && Number.isFinite(nextVersion)) {
                // UploadedVersion as well as Version. The render publishes the cut
                // server-side and points UploadedVersion at it, but nothing here
                // re-reads the workspace, so the client kept the value it loaded
                // the page with -- -1 on a workspace that had never rendered. The
                // preview effect keys on UploadedVersion and silently blanks when
                // it is negative, so a finished render showed nothing at all until
                // the page was reloaded by hand.
                const patch = { Version: nextVersion, UploadedVersion: nextVersion };

                setWorkspaces(prev => prev.map(ws =>
                    ws.VideoWorkspaceId === workspaceId
                        ? { ...ws, ...patch }
                        : ws
                ));

                setSelectedWorkspace(prev => (
                    prev && prev.VideoWorkspaceId === workspaceId
                        ? { ...prev, ...patch }
                        : prev
                ));
            }

            setTimeout(() => {
                const refreshWorkspaceId = workspaceId || selectedWorkspace?.VideoWorkspaceId;
                if (!refreshWorkspaceId) return;
                loadAssets(refreshWorkspaceId);
                checkProcessingStatus(refreshWorkspaceId);
            }, 1000);

            try {
                // Number.isFinite, not truthiness: the first render of a
                // workspace is version 0, which is falsy, so the one render every
                // user does first was the one that never asked for a link at all.
                if (newMessage.metadata?.video_workspace_id && Number.isFinite(nextVersion)) {
                    const idToken = await getIdToken();
                    const downloadResponse = await GetVideoWorkspaceOutputUrl(
                        idToken,
                        newMessage.metadata.video_workspace_id,
                        nextVersion
                    );

                    // OutputUrl is what GetOutputUrl returns; DownloadUrl is a
                    // field the API has never had, so this read was always
                    // undefined and the link never rendered -- leaving the raw
                    // endpoint path printed as text in its place.
                    if (downloadResponse.status === 200 && downloadResponse.myJson?.OutputUrl) {
                        setAiChatMessages(prev => prev.map(msg =>
                            msg.id === newMessage.id
                                ? {
                                    ...msg,
                                    metadata: {
                                        ...msg.metadata,
                                        download_url: downloadResponse.myJson.OutputUrl
                                    }
                                }
                                : msg
                        ));
                    }
                }
            } catch (downloadError) {
                console.error('Error fetching output download URL:', downloadError);
            }

            // Auto-disconnect once processing completes to avoid holding idle sockets
            const socket = aiWebSocketRef.current;
            if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
                try {
                    socket.close(1000, 'Video edit completed');
                } catch (closeError) {
                    console.warn('Error closing AI WebSocket after completion:', closeError);
                }
            }
        }
    }, [selectedWorkspace, loadAssets, checkProcessingStatus, getIdToken]);

    /* The socket reads the handler through a ref. Depending on it directly gave
       `connectToAI` a new identity every time the workspace object was
       refreshed, which re-fired the auto-connect effect below. */
    const handleAiMessageRef = useRef(handleAiMessage);
    useEffect(() => {
        handleAiMessageRef.current = handleAiMessage;
    }, [handleAiMessage]);

    /* Give up on payloads that will now never be sent, and SAY SO.
       Emptying this queue in silence is what made a failed render click
       indistinguishable from a successful one: the button had already said
       "Sent for rendering", so the edit looked under way while the payload had
       actually been thrown on the floor, and the spinner never came back. */
    const dropPendingAiSends = useCallback((reason) => {
        const dropped = pendingAiSendsRef.current;
        pendingAiSendsRef.current = [];
        if (!dropped.length) return;
        setIsAiProcessing(false);
        setManualNotice('');
        setManualError(reason);
    }, []);

    const connectToAI = useCallback(async () => {
        if (!IS_AI_AGENT_ENABLED) {
            setAiChatError('AI video editing is disabled outside development/test environments.');
            return;
        }

        /* Guard on the attempt, not on `isAiConnected`: that only becomes true
           in onopen, so during the token fetch and handshake below every
           re-render used to slip past this line and open another socket. */
        if (!selectedWorkspace || aiConnectingRef.current || aiWebSocketRef.current) return;

        aiWantsConnectionRef.current = true;
        aiConnectingRef.current = true;

        try {
            setAiConnectionStatus('connecting');
            setAiChatError('');

            const token = await getIdToken();

            // Teardown may have run while the token was in flight.
            if (!aiWantsConnectionRef.current) {
                aiConnectingRef.current = false;
                return;
            }

            // Use the client function to create the WebSocket connection
            const ws = createVideoWorkspaceAIConnection(selectedWorkspace.VideoWorkspaceId);
            aiWebSocketRef.current = ws;

            ws.onopen = () => {
                aiConnectingRef.current = false;
                aiReconnectAttemptsRef.current = 0;
                setIsAiConnected(true);
                setAiConnectionStatus('connected');
                setAiWebSocket(ws);
                console.log('Connected to AI Video Editor for workspace:', selectedWorkspace.VideoWorkspaceId);

                const impersonateUserId = String(localStorage.getItem('vixo_impersonate_user_id') || '').trim();
                const impersonateUserEmail = String(localStorage.getItem('vixo_impersonate_user_email') || '').trim().toLowerCase();

                ws.send(JSON.stringify({
                    type: 'auth',
                    token,
                    impersonate_user_id: impersonateUserId || undefined,
                    impersonate_user_email: impersonateUserEmail || undefined,
                }));
                
                // Send a ping to confirm connection
                ws.send(JSON.stringify({ type: 'ping' }));

                // Anything composed while this was still opening.
                const queued = pendingAiSendsRef.current;
                pendingAiSendsRef.current = [];
                queued.forEach((payload) => ws.send(JSON.stringify(payload)));
            };
            
            ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    handleAiMessageRef.current(message);
                } catch (e) {
                    console.error('Error parsing AI message:', e);
                    setAiChatError('Error processing AI response');
                }
            };
            
            ws.onclose = (event) => {
                aiConnectingRef.current = false;
                if (aiWebSocketRef.current === ws) {
                    aiWebSocketRef.current = null;
                }
                setIsAiConnected(false);
                setAiConnectionStatus('disconnected');
                setAiWebSocket(null);
                console.log('Disconnected from AI Video Editor:', event.code, event.reason);

                if (!aiWantsConnectionRef.current) return;

                if (!isRetryableAiCloseCode(event.code)) {
                    /* The server said no for a reason that will not change --
                       the AI kill switch, an unauthorized user, a workspace
                       that is already posted. Retrying those was a hot loop
                       against Cloud Run rather than a recovery. */
                    setAiChatError(event.reason || 'The AI editor refused the connection.');
                    setAiConnectionStatus('error');
                    dropPendingAiSends(
                        event.reason
                            ? `The server refused the connection, so this render was not started: ${event.reason}`
                            : 'The server refused the connection, so this render was not started.'
                    );
                    return;
                }

                if (aiReconnectAttemptsRef.current >= MAX_AI_RECONNECT_ATTEMPTS) {
                    setAiChatError('Lost the connection to the AI and could not get it back. Reload the page to keep going.');
                    setAiConnectionStatus('error');
                    dropPendingAiSends(
                        'Lost the connection before this render could be sent — nothing was started. Reload the page and try again.'
                    );
                    return;
                }

                const delay = Math.min(15000, 1000 * (2 ** aiReconnectAttemptsRef.current));
                aiReconnectAttemptsRef.current += 1;
                setAiConnectionStatus('reconnecting');
                aiReconnectTimerRef.current = setTimeout(() => {
                    aiReconnectTimerRef.current = null;
                    if (!aiWantsConnectionRef.current) return;
                    console.log('Attempting to reconnect to AI...');
                    connectToAIRef.current();
                }, delay);
            };

            ws.onerror = (error) => {
                console.error('AI WebSocket error:', error);
                setAiChatError('Connection error. Retrying...');
                setAiConnectionStatus('error');
            };

        } catch (error) {
            aiConnectingRef.current = false;
            console.error('Error connecting to AI:', error);
            setAiChatError('Failed to connect to AI. Please check your connection.');
            setAiConnectionStatus('error');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWorkspace, auth]);

    /* The retry fires through a ref so a queued reconnect always runs the
       current callback rather than the one captured when the socket opened. */
    const connectToAIRef = useRef(connectToAI);
    useEffect(() => {
        connectToAIRef.current = connectToAI;
    }, [connectToAI]);

    // Drop the socket and stop any retry -- the chat closed, the workspace
    // changed, or the component is going away.
    const teardownAiConnection = useCallback(() => {
        aiWantsConnectionRef.current = false;
        aiConnectingRef.current = false;
        aiReconnectAttemptsRef.current = 0;
        if (aiReconnectTimerRef.current) {
            clearTimeout(aiReconnectTimerRef.current);
            aiReconnectTimerRef.current = null;
        }
        pendingAiSendsRef.current = [];
        const socket = aiWebSocketRef.current;
        aiWebSocketRef.current = null;
        if (socket) {
            try {
                socket.close(1000, 'Chat closed');
            } catch (closeError) {
                console.warn('Error closing AI WebSocket:', closeError);
            }
        }
        setIsAiConnected(false);
        setAiWebSocket(null);
        setAiConnectionStatus('disconnected');
    }, []);
    
    // Send now if the socket is up, otherwise open it and let ws.onopen flush
    // the payload. Manual edits go over the same socket as the chat because the
    // server executes both through the same approved-plan path.
    const sendOrQueueAiPayload = useCallback((payload) => {
        const socket = aiWebSocketRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(payload));
            return;
        }

        pendingAiSendsRef.current = [...pendingAiSendsRef.current, payload];
        connectToAI();
    }, [connectToAI]);

    // Compile the timeline and hand the script to the server, which validates
    // and executes it exactly like an approved AI plan — no planner, no tokens.
    /* What the progress panel reports, which is not simply the last status the
       server sent. A second render starts while the previous run's row still
       says "Completed", and polling only catches up a few seconds later — so
       clicking Render looked like it did nothing, or worse, still offered the
       PREVIOUS result to view. A run we just started outranks a stale row. */
    /* The fetched status wins now that it can only ever describe the render we
       are waiting on -- it is cleared when one is queued and only refilled by a
       matching run id. Before that guarantee this had to be overridden by
       isAiProcessing, because the document might still be holding the previous
       render's "Completed". */
    const renderStatus = processingStatus?.Status || 'Processing';

    const handleRenderManualEdit = useCallback(() => {
        setManualError('');
        setManualNotice('');

        if (isSelectedWorkspacePosted) {
            setManualError('This project has been posted and is locked for editing.');
            return;
        }

        let script = '';
        try {
            script = buildScriptFromTimeline(manualGraph, assets);
        } catch (buildError) {
            setManualError(buildError.message || 'This timeline cannot be rendered yet.');
            return;
        }

        /* Forget the previous run before asking for this one. The processing
           document is one row per workspace, so it still holds the LAST
           render's "Completed" -- and a click that never reaches the server (a
           dropped socket, a script that will not compile) left that row on
           screen, which read as "this render finished instantly". Clearing it
           here means the panel can only ever show the render being waited on. */
        activeRunIdRef.current = null;
        setProcessingStatus(null);

        setIsAiProcessing(true);
        sendOrQueueAiPayload({ type: 'manual_plan', script });
        setManualNotice('Sent for rendering — progress appears below.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assets, manualGraph, isSelectedWorkspacePosted, sendOrQueueAiPayload]);

    const disconnectFromAI = useCallback(() => {
        teardownAiConnection();
    }, [teardownAiConnection]);
    
    const sendAiMessage = useCallback(async () => {
        if (!IS_AI_AGENT_ENABLED) return;
        if (!aiChatInput.trim() || !isAiConnected || !aiWebSocket) return;

        /* The chat carries the timeline as it currently stands, so "make the
           intro shorter" means the user's intro rather than a fresh plan from
           the media. This used to ride on a separate ask box beneath the
           timeline; the chat is the only way in now, so the context comes with
           it. An unbuildable timeline is not an error here -- there is simply
           nothing to edit yet, and the AI plans from the media instead. */
        let currentScript = '';
        try {
            currentScript = buildScriptFromTimeline(manualGraph, assets);
        } catch {
            // Nothing renderable on the timeline yet: let the AI start fresh.
        }

        const userMessage = {
            id: Date.now(),
            type: 'user_input',
            content: aiChatInput.trim(),
            timestamp: new Date().toISOString(),
            metadata: { from_timeline: Boolean(currentScript) },
        };

        // Add user message to chat
        setAiChatMessages(prev => [...prev, userMessage]);

        // Send to AI
        try {
            aiWebSocket.send(JSON.stringify({
                type: 'text',
                content: aiChatInput.trim(),
                current_script: currentScript,
            }));

            setAiChatInput('');
            setIsAiProcessing(true);

        } catch (error) {
            console.error('Error sending message to AI:', error);
            setAiChatError('Failed to send message. Please try again.');
        }
    }, [aiChatInput, isAiConnected, aiWebSocket, manualGraph, assets]);
    
    const handleAiChatKeyPress = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendAiMessage();
        }
    }, [sendAiMessage]);
    
    const clearAiChat = useCallback(() => {
        setAiChatMessages([]);
        setAiChatError('');
        setEditableApprovalPayloads({});
    }, []);

    const handleApprovalGraphChange = useCallback((messageId, updatedGraph, fallbackPlan, fallbackPrompt) => {
        const editedGraph = deepClone(updatedGraph);
        setEditableApprovalPayloads((prev) => {
            const current = prev[messageId] || {
                prompt: String(fallbackPrompt || ''),
                originalPlan: deepClone(fallbackPlan || {}),
                originalLayeredGraph: deepClone(editedGraph || {}),
                editedPlan: deepClone(fallbackPlan || {}),
                editedLayeredGraph: deepClone(editedGraph || {}),
                hasEdits: false,
            };

            const sourcePlan = current.originalPlan && Object.keys(current.originalPlan).length > 0
                ? current.originalPlan
                : (fallbackPlan || {});

            const nextEditedPlan = buildEditedPlanFromTimeline(sourcePlan, editedGraph, assets);
            return {
                ...prev,
                [messageId]: {
                    ...current,
                    prompt: current.prompt || String(fallbackPrompt || ''),
                    editedLayeredGraph: editedGraph,
                    editedPlan: nextEditedPlan,
                    hasEdits: true,
                }
            };
        });

        setAiChatMessages(prev => prev.map((msg) => {
            if (msg.id !== messageId) return msg;

            const sourcePlan = msg.metadata?.plan || fallbackPlan || {};
            const nextPlan = buildEditedPlanFromTimeline(sourcePlan, editedGraph, assets);

            return {
                ...msg,
                metadata: {
                    ...(msg.metadata || {}),
                    layered_graph: editedGraph,
                    plan: nextPlan,
                    timeline_edited: true,
                }
            };
        }));
    }, [assets]);

    const handleApprovalScriptChange = useCallback((messageId, nextScript, fallbackScript) => {
        setEditableApprovalPayloads((prev) => {
            const current = prev[messageId] || {
                originalScript: String(fallbackScript || ''),
                scriptEditable: true,
            };
            const baseline = String(current.originalScript ?? fallbackScript ?? '');
            return {
                ...prev,
                [messageId]: {
                    ...current,
                    editedScript: nextScript,
                    scriptDirty: nextScript.trim() !== baseline.trim(),
                }
            };
        });
    }, []);

    const sendApprovalDecision = useCallback((decision, messageId, fallbackMetadata, timelineScript) => {
        const socket = aiWebSocketRef.current || aiWebSocket;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
            setAiChatError('AI connection is not ready. Please reconnect and try again.');
            return;
        }

        const approvalPayload = {
            type: 'approval_decision',
            decision,
        };

        const editedPayload = editableApprovalPayloads[messageId];
        if (decision === 'approve') {
            const planOverride = editedPayload?.hasEdits
                ? editedPayload.editedPlan
                : fallbackMetadata?.plan;
            const layeredGraphOverride = editedPayload?.hasEdits
                ? editedPayload.editedLayeredGraph
                : fallbackMetadata?.layered_graph;
            const promptOverride = editedPayload?.prompt || fallbackMetadata?.prompt || '';

            if (planOverride && typeof planOverride === 'object') {
                approvalPayload.plan = planOverride;
            }
            if (layeredGraphOverride && typeof layeredGraphOverride === 'object') {
                approvalPayload.layered_graph = layeredGraphOverride;
            }
            if (promptOverride) {
                approvalPayload.prompt = String(promptOverride);
            }
            // A hand-edited script is authoritative: the backend recompiles the
            // plan from it (and surfaces a validation error if it doesn't compile).
            if (editedPayload?.scriptDirty && typeof editedPayload.editedScript === 'string') {
                approvalPayload.script = editedPayload.editedScript;
            }
            // Rendering from the editor sends the timeline itself, compiled the
            // same way as any hand-made render. The plan rebuilt from the graph
            // above predates the editor and silently drops what it does not
            // know (overlay clips, their sound), so the render could differ
            // from the preview; the backend treats a script as authoritative.
            if (typeof timelineScript === 'string' && timelineScript.trim()) {
                approvalPayload.script = timelineScript;
            }
        }

        socket.send(JSON.stringify(approvalPayload));
        setIsAiProcessing(true);

        const editLabel = editedPayload?.scriptDirty
            ? 'approve (with script edits)'
            : editedPayload?.hasEdits
                ? 'approve (with timeline edits)'
                : 'approve';
        const userMessageText = decision === 'approve' ? editLabel : decision;

        setAiChatMessages(prev => [...prev, {
            id: Date.now() + Math.random(),
            type: 'user_input',
            content: userMessageText,
            timestamp: new Date().toISOString(),
            metadata: {
                decision,
                timeline_edited: Boolean(editedPayload?.hasEdits),
                script_edited: Boolean(editedPayload?.scriptDirty)
            }
        }]);
    }, [aiWebSocket, editableApprovalPayloads]);

    // Hand edits to the timeline. When an AI proposal is loaded, they are also
    // the edits that get approved — the graph on screen is the one that runs.
    const handleTimelineChange = useCallback((nextGraph) => {
        setManualGraph(nextGraph);
        setManualError('');
        if (pendingApproval) {
            handleApprovalGraphChange(
                pendingApproval.messageId,
                nextGraph,
                pendingApproval.plan,
                pendingApproval.prompt
            );
        }
    }, [pendingApproval, handleApprovalGraphChange]);

    /* ---- the edit survives the tab ----------------------------------------
       The timeline used to live only in this component's state, so a refresh,
       a crash or a laptop closing threw away every cut the user had made. It
       is saved against the workspace instead: debounced, rewriting one
       document in place, because an autosave is work in progress and must not
       look like a render. */

    useEffect(() => {
        latestGraphRef.current = manualGraph;
    }, [manualGraph]);

    // Load the saved cut for whichever workspace is open.
    useEffect(() => {
        const workspaceId = selectedWorkspace?.VideoWorkspaceId || '';
        // Only a change of workspace starts over. This effect also re-runs when
        // the auth token identity changes, and blanking a timeline someone is
        // editing because their session refreshed would be the very loss this
        // whole mechanism exists to prevent.
        if (timelineWorkspaceRef.current === workspaceId) return undefined;
        timelineWorkspaceRef.current = workspaceId;

        timelineLoadedForRef.current = '';
        savedTimelineRef.current = '';
        setTimelineSaveState('');
        // An empty timeline while the saved one is on its way — showing the
        // previous workspace's cut under a new workspace's name is worse than
        // showing nothing for a moment.
        setManualGraph(createManualTimelineGraph());

        if (!workspaceId) return undefined;

        /* Switching workspace is the only thing that abandons this load, and
           the ref above already records that. A cleanup-function flag would be
           wrong here: React runs it before every re-run, including the ones
           that change nothing, and a load cancelled that way would never arm
           the autosave. */
        (async () => {
            const stillCurrent = () => timelineWorkspaceRef.current === workspaceId;
            try {
                const idToken = await getIdToken();
                const { status, myJson } = await GetVideoWorkspaceTimeline(idToken, workspaceId);
                if (!stillCurrent()) return;
                if (status !== 200) {
                    // Not knowing what is already saved, saving would risk
                    // writing this empty timeline over a real cut. So the
                    // autosave stays disarmed and says so.
                    setTimelineSaveState('error');
                    return;
                }
                // Someone who started cutting while this was in flight has a
                // newer timeline than the saved one; theirs wins.
                if (myJson?.Graph && isEmptyTimeline(latestGraphRef.current)) {
                    const restored = migrateGraph(myJson.Graph);
                    savedTimelineRef.current = JSON.stringify(restored);
                    setManualGraph(restored);
                }
                // A workspace with nothing saved yet arms the autosave too: an
                // empty answer is an answer.
                timelineLoadedForRef.current = workspaceId;
            } catch (error) {
                if (stillCurrent()) setTimelineSaveState('error');
            }
        })();

        return undefined;
    }, [selectedWorkspace?.VideoWorkspaceId, getIdToken]);

    // Save it back, once the edits stop.
    useEffect(() => {
        const workspaceId = selectedWorkspace?.VideoWorkspaceId;
        // Saving before the load has answered would write the empty placeholder
        // over the cut being fetched. A posted workspace is read-only.
        if (!workspaceId
            || timelineLoadedForRef.current !== workspaceId
            || isSelectedWorkspacePosted) {
            return undefined;
        }

        const serialized = JSON.stringify(manualGraph);
        if (serialized === savedTimelineRef.current) return undefined;

        setTimelineSaveState('saving');
        const timer = setTimeout(async () => {
            try {
                const idToken = await getIdToken();
                const { status } = await SaveVideoWorkspaceTimeline(idToken, workspaceId, manualGraph);
                if (status === 200) {
                    savedTimelineRef.current = serialized;
                    setTimelineSaveState('saved');
                } else {
                    setTimelineSaveState('error');
                }
            } catch (error) {
                setTimelineSaveState('error');
            }
        }, TIMELINE_SAVE_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [manualGraph, selectedWorkspace?.VideoWorkspaceId, isSelectedWorkspacePosted, getIdToken]);

    /* Measure how far apart these cameras were started, by their sound.
       Queued on a worker and polled, like a render: it has to decode a window
       of audio out of every file, which is not something to do inside a click
       handler. Returns what was measured rather than applying it, so the panel
       that knows which angle is which decides what to do with each answer. */
    const alignAngles = useCallback(async (fileNames, referenceFileName) => {
        const workspaceId = selectedWorkspace?.VideoWorkspaceId;
        if (!workspaceId) throw new Error('Open a project first.');

        const idToken = await getIdToken();
        const queued = await QueueAudioAlignment(idToken, workspaceId, fileNames, referenceFileName);
        if (queued.status !== 200) {
            throw new Error(queued.myJson?.detail || 'Could not start lining these cameras up.');
        }

        const deadline = Date.now() + ALIGN_LIMIT_MS;
        while (Date.now() < deadline) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, ALIGN_POLL_MS));
            // eslint-disable-next-line no-await-in-loop
            const polled = await GetAudioAlignment(await getIdToken(), workspaceId);
            const state = String(polled.myJson?.Status || '');
            if (state === 'Completed') return polled.myJson?.Angles || [];
            if (state === 'Failed') {
                throw new Error(polled.myJson?.ErrorMessage || 'Could not line these cameras up.');
            }
        }
        throw new Error('Lining these cameras up is taking longer than expected — try again in a moment.');
    }, [selectedWorkspace?.VideoWorkspaceId, getIdToken]);

    /* What rendering this edit is expected to cost, quoted from the same
       endpoint the confirmation dialog quotes from — a price on the page and a
       different price in the dialog that follows it reads as a bait and switch.

       Re-asked whenever a render finishes, because the estimate is drawn from
       what this user's own edits have actually cost: it gets sharper with every
       one, and a figure that never moved would go stale against their history. */
    useEffect(() => {
        if (editorTab !== 'edit' || !selectedWorkspace?.VideoWorkspaceId) {
            setEditCostQuote(null);
            return undefined;
        }

        let cancelled = false;
        quoteCost({ flow: 'video_edit' }).then((quote) => {
            if (!cancelled) setEditCostQuote(quote);
        });
        return () => { cancelled = true; };
    }, [editorTab, selectedWorkspace?.VideoWorkspaceId, isAiProcessing, quoteCost]);

    const resolveApproval = useCallback((decision, timelineScript) => {
        if (!pendingApproval) return;
        sendApprovalDecision(decision, pendingApproval.messageId, pendingApproval.metadata, timelineScript);
        setPendingApproval(null);

        // Turning the proposal down puts the user's own cut back on the timeline.
        if (decision !== 'approve' && preProposalGraphRef.current) {
            setManualGraph(preProposalGraphRef.current);
        }
        preProposalGraphRef.current = null;
    }, [pendingApproval, sendApprovalDecision]);

    /* One Render button, whether or not the AI proposed this cut. A proposal
       is already on the timeline for the user to adjust; asking them to
       approve it as a separate step before rendering added a click and said
       nothing new. While a proposal is open the server is waiting for its
       answer, so rendering it IS that answer -- carrying the timeline as it
       stands now. */
    const handleRender = useCallback(() => {
        if (!pendingApproval) {
            handleRenderManualEdit();
            return;
        }
        setManualError('');
        setManualNotice('');
        let script = '';
        try {
            script = buildScriptFromTimeline(manualGraph, assets);
        } catch (buildError) {
            setManualError(buildError.message || 'This timeline cannot be rendered yet.');
            return;
        }
        activeRunIdRef.current = null;
        setProcessingStatus(null);
        resolveApproval('approve', script);
        setManualNotice('Sent for rendering — progress appears below.');
    }, [pendingApproval, handleRenderManualEdit, manualGraph, assets, resolveApproval]);

    const formatAiMessageTime = (timestamp) => {
        try {
            const date = new Date(timestamp);
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        } catch {
            return '';
        }
    };
    
    const getMessageIcon = (type) => {
        const props = { size: 16, 'aria-hidden': true };
        switch (type) {
            case 'user_input': return <UserRound {...props} />;
            case 'ai_response': return <Bot {...props} />;
            case 'ai_thinking': return <LoaderCircle {...props} />;
            case 'ai_question': return <CircleHelp {...props} />;
            case 'progress_update': return <LoaderCircle {...props} />;
            case 'error': return <TriangleAlert {...props} />;
            case 'video_ready': return <Clapperboard {...props} />;
            default: return <MessageSquare {...props} />;
        }
    };
    
    /* One owner for the socket's lifetime: open while the chat is on screen for
       a workspace, torn down when either goes away.

       This used to be two effects that fought each other. The first re-fired
       whenever `isAiConnected` went false -- which is exactly what onclose
       does -- so a server refusal reconnected instantly, was refused again, and
       looped at network speed while onclose's own 3s retry piled on top. The
       second was meant to close the socket when the chat closed, but its
       condition read the *previous* render's `showAIChat`, so it was false on
       precisely the render that needed it and the socket was never closed.

       The reconnect policy now lives in onclose alone (capped, backed off, and
       skipped for close codes that will not change). This effect only says
       whether a connection is wanted at all. */
    const aiChatId = showAIChat ? (selectedWorkspace?.VideoWorkspaceId || '') : '';
    useEffect(() => {
        if (!IS_AI_AGENT_ENABLED || !aiChatId) return undefined;

        connectToAIRef.current();
        return () => {
            teardownAiConnection();
        };
    }, [aiChatId, teardownAiConnection]);

    /* A manual render can open the socket with the chat panel closed, which
       puts it outside the effect above's ownership. Nothing outlives the
       component either way. */
    useEffect(() => teardownAiConnection, [teardownAiConnection]);

    return (
        <div className="video-workspace">

            {!hideHeader && (
                <div className="workspace-header">
                    <h1 className="workspace-title">Edit</h1>
                    <p className="workspace-subtitle">Manage video workspaces, upload assets, and create the downstream post.</p>
                </div>
            )}

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            <div className={`workspace-layout ${hideSidebar ? 'workspace-layout-content-only' : ''}`}>
                {!hideSidebar && (
                    <div className="workspace-sidebar">
                        <div className="sidebar-header">
                            <h2>{isSingleWorkspaceMode ? 'Workspace' : 'Workspaces'}</h2>
                            {!isSingleWorkspaceMode && (
                                <button 
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setShowCreateModal(true)}
                                    disabled={isLoading}
                                    title="Create New Workspace"
                                    aria-label="Create a new workspace"
                                >
                                    <Plus size={16} aria-hidden="true" />
                                </button>
                            )}
                        </div>

                    <div className="workspace-list">
                        {isLoading && visibleWorkspaces.length === 0 ? (
                            <div className="loading">Loading workspaces...</div>
                        ) : visibleWorkspaces.length === 0 ? (
                            <div className="no-workspaces">
                                {isSingleWorkspaceMode ? (
                                    <p>The linked workspace could not be found.</p>
                                ) : (
                                    <>
                                        <p>No workspaces found.</p>
                                        <button 
                                            className="btn btn-primary btn-sm"
                                            onClick={() => setShowCreateModal(true)}
                                        >
                                            Create your first workspace
                                        </button>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="workspace-items">
                                {visibleWorkspaces.map((workspace) => (
                                    <div 
                                        key={workspace.VideoWorkspaceId} 
                                        className={`workspace-item ${selectedWorkspace?.VideoWorkspaceId === workspace.VideoWorkspaceId ? 'selected' : ''}`}
                                        onClick={() => handleSelectWorkspace(workspace)}
                                    >
                                        <div className="workspace-item-header">
                                            <div className="workspace-name-section">
                                                <h4 className="workspace-name">
                                                    {projectName || 'Untitled Workspace'}
                                                </h4>
                                                <div className="workspace-roles">
                                                    {workspace.isOwner && (
                                                        <span className="role-badge owner" title="You own this workspace">
                                                            Owner
                                                        </span>
                                                    )}
                                                    {workspace.isEditor && (
                                                        <span className="role-badge editor" title="You can edit this workspace">
                                                            Editor
                                                        </span>
                                                    )}
                                                    {(workspace.IsPosted || workspace.PostId) && (
                                                        <span className="role-badge" title="Workspace is posted and locked" style={{ backgroundColor: 'var(--sb-good-wash)', color: 'var(--sb-good)' }}>
                                                            <Lock size={12} aria-hidden="true" /> Posted
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="workspace-actions" onClick={(e) => e.stopPropagation()}>
                                                <button 
                                                    className="action-btn edit-btn"
                                                    onClick={() => handleEditWorkspace(workspace)}
                                                    title="Edit Workspace"
                                                    aria-label="Edit workspace"
                                                    disabled={(!workspace.isOwner && !workspace.isEditor) || workspace.IsPosted || workspace.PostId}
                                                    style={{
                                                        opacity: (workspace.isOwner || workspace.isEditor) && !(workspace.IsPosted || workspace.PostId) ? 1 : 0.5,
                                                        cursor: (workspace.isOwner || workspace.isEditor) && !(workspace.IsPosted || workspace.PostId) ? 'pointer' : 'not-allowed'
                                                    }}
                                                >
                                                    <Pencil size={14} aria-hidden="true" />
                                                </button>
                                                <button 
                                                    className="action-btn delete-btn"
                                                    onClick={() => handleDeleteWorkspace(workspace.VideoWorkspaceId)}
                                                    title="Delete Workspace"
                                                    aria-label="Delete workspace"
                                                    disabled={!workspace.isOwner || workspace.IsPosted || workspace.PostId}
                                                    style={{
                                                        opacity: workspace.isOwner && !(workspace.IsPosted || workspace.PostId) ? 1 : 0.5,
                                                        cursor: workspace.isOwner && !(workspace.IsPosted || workspace.PostId) ? 'pointer' : 'not-allowed'
                                                    }}
                                                >
                                                    <Trash2 size={14} aria-hidden="true" />
                                                </button>
                                            </div>
                                        </div>
                                        
                                        <div className="workspace-preview">
                                            <p className="workspace-prompt-preview">
                                                {workspace.Prompt ? 
                                                    (workspace.Prompt.length > 100 ? 
                                                        workspace.Prompt.substring(0, 100) + '...' : 
                                                        workspace.Prompt
                                                    ) : 
                                                    'No description'
                                                }
                                            </p>
                                            <div className="workspace-footer">
                                                <small className="workspace-date">
                                                    {formatDateTimeFromTicks(workspace.ModifiedDateTime)}
                                                </small>
                                                <small className="workspace-version">
                                                    {workspace.Version > 0 
                                                        ? `v0-${workspace.Version - 1} published` 
                                                        : 'No published versions'
                                                    }
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    </div>
                )}

                {/* Right Panel - Asset Management */}
                <div className="workspace-main">
                    {selectedWorkspace ? (
                        <>
                            {/* Inside a project the project page already shows the title; versions
                                live in the Renders tab and the description came from the idea. */}
                            {!inWorkflow && (
                            <div className="workspace-details">
                                <div className="workspace-details-header">
                                    <div className="workspace-info">
                                        <div className="workspace-title-group">
                                            <h2>{projectName || 'Untitled Workspace'}</h2>
                                            <span className="version-badge">
                                                Current Draft: {selectedWorkspace.Version || 0}
                                                {selectedWorkspace.Version > 0 
                                                    ? ` (Published: v0-${selectedWorkspace.Version - 1})` 
                                                    : ' (No published versions)'
                                                }
                                            </span>
                                        </div>
                                        {/* Inside a project there is nothing here to edit: the
                                            name is the project's (renamed from the project
                                            header) and the description came from the idea.
                                            Only the standalone editor still needs the form. */}
                                        {!inWorkflow && (
                                            <button
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => handleEditWorkspace(selectedWorkspace)}
                                                disabled={isSelectedWorkspacePosted}
                                            >
                                                <Pencil size={14} aria-hidden="true" /> Edit details
                                            </button>
                                        )}
                                        {linkedIdeaId && !inWorkflow && (
                                            <button
                                                className="btn btn-info btn-sm"
                                                onClick={handleBackToIdea}
                                                title="Open linked ideation idea"
                                                style={{ marginLeft: '8px' }}
                                            >
                                                <ArrowLeft size={14} aria-hidden="true" /> Back to Idea
                                            </button>
                                        )}
                                    </div>
                                </div>
                                
                                {selectedWorkspace.Prompt && (
                                    <div className="workspace-prompt-display">
                                        <h4>Description:</h4>
                                        <p>{selectedWorkspace.Prompt}</p>
                                    </div>
                                )}
                            </div>
                            )}

            {/* The generate-or-edit fork. Both paths fill this same workspace,
                so the studio replaces the editor while it is open rather than
                living somewhere else in the product. */}
            {showGenerationStudio ? (
                <GenerationStudio
                    workspaceId={selectedWorkspace.VideoWorkspaceId}
                    workspaceName={projectName}
                    ideaId={linkedIdeaId}
                    onClose={() => {
                        setShowGenerationStudio(false);
                        loadAssets(selectedWorkspace.VideoWorkspaceId);
                        loadGenerationSummary(selectedWorkspace.VideoWorkspaceId);
                        // Stitching writes UploadedVersion on the workspace, not on
                        // the generation the studio was polling. Without re-reading
                        // it the Output tab still holds the pre-render copy and
                        // hides the finished cut it is supposed to be showing.
                        loadWorkspaces();
                    }}
                    onAssetsChanged={() => loadAssets(selectedWorkspace.VideoWorkspaceId)}
                />
            ) : (
              <>
            {/* Three places to be, in the order the work happens: gather the
                media, cut it, then publish the result. Everything below used to
                be stacked on one page, so the screen showed six competing
                sections whatever the user was actually doing. */}
            <div className="workspace-tabs" role="tablist">
                {[
                    { key: 'media', label: 'Media', count: assets.length },
                    { key: 'edit', label: 'Timeline' },
                    { key: 'output', label: 'Renders' },
                ].map((tab) => (
                    <button
                        key={tab.key}
                        role="tab"
                        aria-selected={editorTab === tab.key}
                        className={`workspace-tab ${editorTab === tab.key ? 'active' : ''}`}
                        onClick={() => setEditorTab(tab.key)}
                    >
                        {tab.label}
                        {typeof tab.count === 'number' && tab.count > 0 && (
                            <span className="workspace-tab-count">{tab.count}</span>
                        )}
                    </button>
                ))}
            </div>

            {editorTab === 'media' && showGenerationFork && (
                <GenerationFork
                    hasScript={Boolean(linkedIdeaId)}
                    resumeStage={generationSummary?.Stage}
                    disabled={isLoading}
                    onGenerate={() => setShowGenerationStudio(true)}
                    onUpload={handleOpenAssetModal}
                />
            )}

            {/* The edit itself: one timeline the user cuts by hand and the AI
                proposes into. Everything below (chat, effect modals) acts on
                this same graph, so there is a single place the cut lives. */}
            {editorTab === 'edit' && (
            <div className="manual-edit-section">
                <div className="manual-edit-header">
                    {/* The tab already says Timeline; a heading and a paragraph
                        restating it pushed the cut a hundred pixels down. */}
                    <p className="manual-edit-subtitle">
                        Drag clips onto the timeline, or ask the AI for an edit.
                    </p>
                    {/* Autosave is only trustworthy if it says so. Silence would
                        leave "will this still be here tomorrow?" unanswerable
                        without closing the tab and finding out. */}
                    {timelineSaveState && (
                        <span className={`timeline-save-state ${timelineSaveState}`}>
                            {timelineSaveState === 'saving' && 'Saving…'}
                            {timelineSaveState === 'saved' && 'Saved'}
                            {timelineSaveState === 'error' && 'Not saved — your last changes are only in this tab'}
                        </span>
                    )}
                </div>

                {manualError && <div className="manual-edit-banner bad">{manualError}</div>}
                {manualNotice && !manualError && <div className="manual-edit-banner ok">{manualNotice}</div>}

                <div className="manual-edit-body">
                {/* The bin the cut is built from, down the left of the timeline
                    it feeds: picking a clip and placing it is one gesture, and a
                    column holds far more names legibly than a strip does. */}
                <aside
                    className={`edit-media-bin ${fileDropZone === 'edit' ? 'file-drop-active' : ''}`}
                    {...getFileDropProps('edit')}
                >
                    <div className="edit-media-bin-header">
                        <span className="edit-media-bin-title">Media bin</span>
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={handleOpenAssetModal}
                            disabled={isSelectedWorkspacePosted}
                        >
                            ＋ Add
                        </button>
                    </div>

                    <input
                        type="search"
                        className="edit-media-bin-search"
                        value={mediaBinQuery}
                        onChange={(e) => setMediaBinQuery(e.target.value)}
                        placeholder="Search media…"
                        aria-label="Search media bin"
                    />

                    <p className="edit-media-bin-hint">
                        Drag a clip onto a track — or drop files here to upload
                    </p>

                    {dropUploadStatus && (
                        <div className="edit-media-bin-status">{dropUploadStatus}</div>
                    )}

                    <div className="edit-media-bin-items">
                        {timelineAssets.length === 0 ? (
                            <p className="edit-media-bin-empty">
                                No video or audio yet. Drop files here, or use “Add”.
                            </p>
                        ) : visibleBinAssets.length === 0 ? (
                            <p className="edit-media-bin-empty">
                                Nothing matches “{mediaBinQuery.trim()}”.
                            </p>
                        ) : (
                            visibleBinAssets.map((asset) => {
                                const kind = getAssetLayerKind(asset.FileType);
                                const isDraggable = Boolean(asset.IsFinalized) && !isSelectedWorkspacePosted;
                                const uploadEntry = uploadsByKey.get(uploadKey(asset.VideoWorkspaceId, asset.FileName));
                                const uploadView = describeUpload(asset, uploadEntry);
                                return (
                                    <div
                                        key={asset.FileName}
                                        className={`edit-asset-chip ${kind} ${isDraggable ? '' : 'pending'}`}
                                        draggable={isDraggable}
                                        onDragStart={(event) => setAssetDragPayload(event, asset)}
                                        title={asset.IsFinalized
                                            ? `Drag onto the ${kind} track`
                                            : [uploadView.label, uploadView.detail].filter(Boolean).join(' — ')}
                                    >
                                        <span className="edit-asset-chip-icon">{getFileIcon(asset.FileType)}</span>
                                        <span className="edit-asset-chip-name">{asset.FileAlias || asset.FileName}</span>
                                        <span className="edit-asset-chip-kind">{kind}</span>
                                        <AssetUploadStatus asset={asset} entry={uploadEntry} compact />
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {fileDropZone === 'edit' && (
                        <div className="file-drop-overlay">Drop files to add them</div>
                    )}
                </aside>

                <div className="manual-edit-main">
                {assets.length === 0 ? (
                    <p className="manual-edit-empty">
                        Nothing to cut yet — drop media in the bin to start the timeline.
                    </p>
                ) : (
                    <>
                        <Editor
                            graph={manualGraph}
                            assets={assets}
                            editable={!isSelectedWorkspacePosted}
                            onGraphChange={handleTimelineChange}
                            resolveAssetUrl={resolvePlanAssetUrl}
                            onAlignAngles={alignAngles}
                        />

                        {/* Against the timeline rather than up in the section
                            header: this renders what is on the tracks directly
                            above it, and a button that far from its subject
                            reads as belonging to the whole tab. */}
                        <div className="manual-edit-actions">
                            {/* The price sits with the button that spends it,
                                not in a settings page: the moment to know what
                                a render costs is the moment before starting
                                one. */}
                            {editCostQuote && (() => {
                                const cost = describeRenderCost(editCostQuote);
                                return (
                                    <span className="manual-edit-cost">
                                        <span className="manual-edit-cost-figure">{cost.figure}</span>
                                        <span className="manual-edit-cost-basis">
                                            {cost.basis} · you pay for what it actually uses
                                        </span>
                                        {editCostQuote.sufficient === false && (
                                            <span className="manual-edit-cost-short">
                                                {formatUsd(editCostQuote.shortfall_usd)} short — add funds to run this
                                            </span>
                                        )}
                                    </span>
                                );
                            })()}
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleRender}
                                disabled={isSelectedWorkspacePosted || isAiProcessing}
                                title="Render exactly this timeline"
                            >
                                {isAiProcessing ? 'Rendering…' : <><Play size={16} aria-hidden="true" /> Render this edit</>}
                            </button>
                        </div>

                        {pendingApproval && (
                            <div className="manual-edit-proposal">
                                <span>
                                    The AI's edit is on the timeline. Change anything you like, then
                                    Render — what renders is the timeline as it stands here.
                                </span>
                                <div className="manual-edit-proposal-actions">
                                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => resolveApproval('disapprove')}>
                                        <Undo2 size={14} aria-hidden="true" /> Undo the AI's edit
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Effects are added from the inspector now: they attach to
                            the selected clip and live on the timeline, so they can
                            be retimed and undone. The apply-and-bake block editors
                            remain on the Media tab, where making a new reusable
                            asset is the point. */}
                    </>
                )}
                </div>
                </div>
            </div>
            )}

            {/* A run in progress reports itself here, and only then. Starting one
                is the timeline's "Render this edit" and the docked chat — a
                separate header offering the same two things again was three
                entry points to one action. */}
            {editorTab === 'edit' && (processingStatus || isAiProcessing) && (
            <div className="processing-section">
                <div className="processing-header">
                    <div className={`processing-status status-${renderStatus.toLowerCase()}`}>
                        <span className="status-indicator"></span>
                        Status: {renderStatus}
                    </div>
                </div>
                                <div className="processing-details">
                                        {processingStatus?.Prompt && (
                                            <div className="processing-prompt">
                                                <h4>Processing Prompt:</h4>
                                                <p>{processingStatus?.Prompt}</p>
                                            </div>
                                        )}
                                        
                                        {renderStatus === 'Processing' && (
                                            <div className="processing-indicator">
                                                <div className="spinner"></div>
                                                <span>Rendering your edit…</span>
                                            </div>
                                        )}
                                        
                                        {renderStatus === 'Completed' && processingStatus?.ResultUrl && (
                                            <div className="processing-result">
                                                <h4>Processing Complete!</h4>
                                                <a 
                                                    href={processingStatus?.ResultUrl} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="btn btn-success"
                                                >
                                                    View result
                                                </a>
                                            </div>
                                        )}
                                        
                                        {renderStatus === 'Failed' && (
                                            <div className="processing-error">
                                                <h4>Processing Failed</h4>
                                                <p>The render didn’t finish. Try again.</p>
                                                <button 
                                                    className="btn btn-primary"
                                                    onClick={() => setShowProcessModal(true)}
                                                    disabled={isSelectedWorkspacePosted}
                                                >
                                                    <RefreshCw size={14} aria-hidden="true" /> Try again
                                                </button>
                                            </div>
                                        )}
                                    </div>
                            </div>
            )}

            {editorTab === 'output' && (
                            <div className="processing-section">
                                {/* What the video is right now, before anything about
                                    versions or analysis — the answer people open this
                                    tab for. */}
                                {typeof selectedWorkspace.UploadedVersion === 'number' && selectedWorkspace.UploadedVersion >= 0 && (
                                    <div className="workspace-analysis-panel">
                                        <div className="workspace-analysis-header">
                                            <h4>Current cut (v{selectedWorkspace.UploadedVersion})</h4>
                                        </div>
                                        {isLoadingUploadedPreview ? (
                                            <p>Loading preview...</p>
                                        ) : uploadedPreviewUrl ? (
                                            <video
                                                controls
                                                src={uploadedPreviewUrl}
                                                style={{ width: '100%', maxHeight: '360px', borderRadius: 'var(--sb-corner)', background: '#000' }}
                                                preload="metadata"
                                            />
                                        ) : (
                                            <p>{uploadedPreviewError || 'Preview is not available for this uploaded version.'}</p>
                                        )}
                                    </div>
                                )}

                                <div className="output-management">
                                    <h4>Versions</h4>
                                    <div className="version-info-text">
                                        {/* "Nothing published yet" on its own reads as
                                            "you have nothing", which is wrong when a
                                            rendered cut is sitting right above. */}
                                        <small>
                                            Draft v{selectedWorkspace?.Version || 0}
                                            {selectedWorkspace?.Version > 0
                                                ? ` · published v0–v${selectedWorkspace.Version - 1}`
                                                : (Number.parseInt(selectedWorkspace?.UploadedVersion, 10) >= 0
                                                    ? ' · cut ready, not published yet'
                                                    : ' · nothing published yet')}
                                        </small>
                                    </div>
                                    {/* Six same-weight buttons in six colors made
                                        every action look equally urgent. The one
                                        that moves the project forward leads; the
                                        rest are quiet, and disabled ones say why. */}
                                    <div className="output-actions">
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleFinalizeVersion}
                                            disabled={isFinalizing || isSelectedWorkspacePosted}
                                            title="Publish current draft as a new version"
                                        >
                                            {isFinalizing ? 'Publishing…' : `Publish version ${selectedWorkspace?.Version || 0}`}
                                        </button>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={handleAnalyzeWorkspace}
                                            disabled={isAnalyzingWorkspace || isSelectedWorkspacePosted}
                                            title={isSelectedWorkspacePosted ? 'This video has been posted, so its edit is locked.' : 'Analyze the latest render, or the published version if there’s no newer render'}
                                        >
                                            {isAnalyzingWorkspace ? 'Analyzing…' : 'Analyze workspace'}
                                        </button>
                                    </div>

                                    <div className="output-actions-quiet">
                                        <button
                                            className="output-link"
                                            onClick={() => setShowOutputModal(true)}
                                            disabled={isUploadingOutput || isSelectedWorkspacePosted}
                                        >
                                            Upload output
                                        </button>
                                        <button
                                            className="output-link"
                                            onClick={handleShowVersionModal}
                                            disabled={!selectedWorkspace?.Version || selectedWorkspace.Version === 0}
                                            title={!selectedWorkspace?.Version || selectedWorkspace.Version === 0 ? 'Publish a version first.' : 'Download version output'}
                                        >
                                            Download a version
                                        </button>
                                        <button
                                            className="output-link"
                                            onClick={handleShowDiffModal}
                                            disabled={!selectedWorkspace?.Version || selectedWorkspace.Version < 2}
                                            title={!selectedWorkspace?.Version || selectedWorkspace.Version < 2 ? 'Needs at least 2 published versions.' : 'Compare versions'}
                                        >
                                            Compare versions
                                        </button>
                                        <button
                                            className="output-link"
                                            onClick={handleShowCommentsModal}
                                            title="View and add comments"
                                        >
                                            Comments{comments.length ? ` (${comments.length})` : ''}
                                        </button>
                                    </div>
                                </div>

                                <div className="workspace-analysis-panel">
                                    <div className="workspace-analysis-header">
                                        <h4>Video Analysis</h4>
                                    </div>

                                    {selectedWorkspaceAnalysis && (
                                        <div className="workspace-analysis-status">
                                            <span>Status: {selectedWorkspaceAnalysis.status || 'idle'}</span>
                                            {typeof selectedWorkspaceAnalysis.sourceVersion === 'number' && (
                                                <span>Version: v{selectedWorkspaceAnalysis.sourceVersion}</span>
                                            )}
                                        </div>
                                    )}

                                    {selectedWorkspaceAnalysis?.error && (
                                        <div className="workspace-analysis-error">
                                            {selectedWorkspaceAnalysis.error}
                                        </div>
                                    )}

                                    {selectedWorkspaceAnalysis?.results ? (
                                        <StructuredAnalysisView analysis={selectedWorkspaceAnalysis.results} />
                                    ) : (
                                        <div className="processing-disabled">
                                            <p>
                                                Run analysis to generate structured feedback for the latest uploaded output or latest published version.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
            )}

                            {/* AI chat, docked bottom-right: the conversation is a side
                                channel to the timeline, not a section competing with it. */}
                            {editorTab === 'edit' && IS_AI_AGENT_ENABLED && (
                            <FloatingChatDock
                                title="AI Video Editor"
                                icon={<Bot size={18} aria-hidden="true" />}
                                launcherLabel="Ask AI to edit"
                                launcherHint="Chat with the AI video editor"
                                busy={isAiProcessing}
                                open={showAIChat}
                                onOpen={() => setShowAIChat(true)}
                                onClose={() => {
                                    setShowAIChat(false);
                                    disconnectFromAI();
                                }}
                                status={
                                    /* Only surface the state the user can act on. "Connected"
                                       and "Disconnected" are plumbing; a failed connection is
                                       the one case worth interrupting them for. */
                                    aiConnectionStatus === 'error' ? (
                                        <div className="connection-status status-error">
                                            <span className="status-dot"></span>
                                            Connection failed
                                        </div>
                                    ) : null
                                }
                                actions={
                                            <div className="ai-chat-buttons">
                                                <button
                                                    className="btn btn-info btn-sm"
                                                    onClick={() => {
                                                        const helpMessage = {
                                                            id: Date.now(),
                                                            type: 'ai_response',
                                                            content: `🆘 **Help & Tips:**

**How to use me:**
• Be specific: "Add a 2-second fade-in to my main video"
• Mention files: "Use my background-music.mp3"
• Describe your goal: "Create a social media video"

**What I can do:**
• Add effects (fade, zoom, filters)
• Combine videos and audio
• Create slideshows from images
• Add text overlays and titles
• Trim and edit videos
• Apply professional effects

**Example requests:**
• "Add background music to my video"
• "Create a 30-second slideshow from my photos"
• "Add text saying 'Welcome' at the beginning"
• "Make my video more professional looking"

**Pro tip:** I'll ask questions if I need clarification!`,
                                                            timestamp: new Date().toISOString()
                                                        };
                                                        setAiChatMessages(prev => [...prev, helpMessage]);
                                                    }}
                                                    title="Show help and tips"
                                                    aria-label="Show help and tips"
                                                >
                                                    <CircleHelp size={16} aria-hidden="true" />
                                                </button>
                                                <button
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={clearAiChat}
                                                    title="Clear chat history"
                                                    aria-label="Clear chat history"
                                                >
                                                    <Trash2 size={16} aria-hidden="true" />
                                                </button>
                                            </div>
                                }
                            >
                                    <div className="ai-chat-container">
                                        {aiChatError && (
                                            <div className="ai-chat-error">
                                                <TriangleAlert size={16} aria-hidden="true" /> {aiChatError}
                                                <button 
                                                    className="btn btn-sm btn-primary"
                                                    onClick={connectToAI}
                                                    style={{ marginLeft: '10px' }}
                                                >
                                                    Retry
                                                </button>
                                            </div>
                                        )}
                                        
                                        <div className="ai-chat-messages" ref={aiChatRef}>
                                            {aiChatMessages.length === 0 ? (
                                                <div className="ai-welcome-message">
                                                    <div className="welcome-content">
                                                        <h4>Welcome to your AI Video Editor!</h4>
                                                        <p>I can help you edit videos using natural language. Here are some examples:</p>
                                                        <div className="example-prompts">
                                                            <button 
                                                                className="example-prompt-btn"
                                                                onClick={() => setAiChatInput("Add a fade-in effect to my video")}
                                                                disabled={!isAiConnected}
                                                            >
                                                                "Add a fade-in effect to my video"
                                                            </button>
                                                            <button 
                                                                className="example-prompt-btn"
                                                                onClick={() => setAiChatInput("Combine my video with background music")}
                                                                disabled={!isAiConnected}
                                                            >
                                                                "Combine my video with background music"
                                                            </button>
                                                            <button 
                                                                className="example-prompt-btn"
                                                                onClick={() => setAiChatInput("Create a slideshow from my images")}
                                                                disabled={!isAiConnected}
                                                            >
                                                                "Create a slideshow from my images"
                                                            </button>
                                                            <button 
                                                                className="example-prompt-btn"
                                                                onClick={() => setAiChatInput("Add text overlay saying 'Welcome'")}
                                                                disabled={!isAiConnected}
                                                            >
                                                                "Add text overlay saying 'Welcome'"
                                                            </button>
                                                            <button 
                                                                className="example-prompt-btn"
                                                                onClick={() => setAiChatInput("Trim my video to the first 30 seconds")}
                                                                disabled={!isAiConnected}
                                                            >
                                                                "Trim my video to the first 30 seconds"
                                                            </button>
                                                        </div>
                                                        <p><strong>Just click an example above or type what you want me to do!</strong></p>
                                                    </div>
                                                </div>
                                            ) : (
                                                aiChatMessages.map((message) => (
                                                    <div 
                                                        key={message.id} 
                                                        className={`chat-message message-${message.type}`}
                                                    >
                                                        <div className="message-header">
                                                            <span className="message-icon">
                                                                {getMessageIcon(message.type)}
                                                            </span>
                                                            <span className="message-type">
                                                                {message.type === 'user_input' ? 'You' : 
                                                                 message.type === 'ai_response' ? 'AI Assistant' :
                                                                 message.type === 'ai_thinking' ? 'AI (Thinking)' :
                                                                 message.type === 'ai_question' ? 'AI (Question)' :
                                                                 message.type === 'progress_update' ? 'AI (Progress)' :
                                                                 message.type === 'error' ? 'AI (Error)' :
                                                                 message.type === 'video_ready' ? 'AI (Success)' : 'AI'}
                                                            </span>
                                                            <span className="message-time">
                                                                {formatAiMessageTime(message.timestamp)}
                                                            </span>
                                                        </div>
                                                        <div className="message-content">
                                                            {(message.content || '').split('\\n').map((line, index) => (
                                                                <div key={index}>{line}</div>
                                                            ))}
                                                            {message.metadata?.layered_graph && (
                                                                <>
                                                                    {/* Read-only here: the editable copy of this
                                                                        proposal is the timeline at the top of the
                                                                        tab, so there is only ever one graph to
                                                                        reason about. */}
                                                                    <LayeredPlanGraph
                                                                        graph={message.metadata.layered_graph}
                                                                        editable={false}
                                                                        availableAssets={assets}
                                                                        resolveAssetUrl={resolvePlanAssetUrl}
                                                                    />
                                                                    {pendingApproval?.messageId === message.id && (
                                                                        <p className="chat-plan-hint">
                                                                            Loaded into the timeline above — edit it there, then approve.
                                                                        </p>
                                                                    )}
                                                                </>
                                                            )}
                                                            {!message.metadata?.layered_graph && message.metadata?.plan && (
                                                                <PlanGraph
                                                                    plan={message.metadata.plan}
                                                                    messageId={message.id}
                                                                />
                                                            )}
                                                            {message.type === 'ai_question' && typeof message.metadata?.script === 'string' && message.metadata.script.trim() && (() => {
                                                                const payload = editableApprovalPayloads[message.id];
                                                                const scriptValue = payload?.editedScript ?? message.metadata.script;
                                                                const canEdit = Array.isArray(message.metadata?.actions)
                                                                    && message.metadata.actions.includes('approve')
                                                                    && (message.metadata.script_editable !== false);
                                                                return (
                                                                    <div className="plan-script-panel">
                                                                        <div className="plan-script-title">
                                                                            Edit Script {canEdit ? '(editable)' : '(read-only)'}
                                                                        </div>
                                                                        <textarea
                                                                            className="plan-script-editor"
                                                                            value={scriptValue}
                                                                            readOnly={!canEdit}
                                                                            spellCheck={false}
                                                                            rows={Math.min(16, Math.max(4, scriptValue.split('\n').length + 1))}
                                                                            onChange={(e) => handleApprovalScriptChange(message.id, e.target.value, message.metadata.script)}
                                                                        />
                                                                        {canEdit && (
                                                                            <div className="plan-script-hint">
                                                                                {payload?.scriptDirty
                                                                                    ? 'Script edited — approving runs this script exactly (overrides timeline edits).'
                                                                                    : 'Edit the script to control the exact deterministic steps, then Approve.'}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })()}
                                                            {message.metadata?.download_url && (
                                                                <div className="message-metadata">
                                                                    <strong>Download:</strong>{' '}
                                                                    <a
                                                                        href={message.metadata.download_url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        Download File
                                                                    </a>
                                                                </div>
                                                            )}
                                                            {!message.metadata?.download_url && message.metadata?.download_endpoint && (
                                                                <div className="message-metadata">
                                                                    <strong>Download:</strong> {message.metadata.download_endpoint}
                                                                </div>
                                                            )}
                                                            {message.type === 'ai_question' && Array.isArray(message.metadata?.actions) && message.metadata.actions.includes('approve') && (
                                                                <div className="message-approval-actions">
                                                                    {/* Rendering a proposal renders the timeline it was
                                                                        loaded onto, so the button only exists on the one
                                                                        proposal that is on the timeline now. */}
                                                                    {pendingApproval?.messageId === message.id && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-success btn-sm"
                                                                            onClick={handleRender}
                                                                            disabled={!isAiConnected || isAiProcessing}
                                                                        >
                                                                            <Play size={14} aria-hidden="true" /> Render
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-warning btn-sm"
                                                                        onClick={() => sendApprovalDecision('disapprove', message.id, message.metadata)}
                                                                        disabled={!isAiConnected || isAiProcessing}
                                                                    >
                                                                        Disapprove
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-secondary btn-sm"
                                                                        onClick={() => sendApprovalDecision('stop', message.id, message.metadata)}
                                                                        disabled={!isAiConnected || isAiProcessing}
                                                                    >
                                                                        Stop
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                            {isAiProcessing && (
                                                <div className="typing-indicator">
                                                    <div className="typing-dots">
                                                        <span></span>
                                                        <span></span>
                                                        <span></span>
                                                    </div>
                                                    <span>AI is processing...</span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="ai-chat-input">
                                            <textarea
                                                ref={aiInputRef}
                                                value={aiChatInput}
                                                onChange={(e) => setAiChatInput(e.target.value)}
                                                onKeyPress={handleAiChatKeyPress}
                                                placeholder={
                                                    isAiConnected 
                                                        ? "Ask me to edit your video (e.g., 'Add a fade effect and background music')..."
                                                        : "Connecting to AI..."
                                                }
                                                disabled={!isAiConnected || isAiProcessing}
                                                rows="2"
                                                style={{ resize: 'vertical' }}
                                            />
                                            <button
                                                onClick={sendAiMessage}
                                                disabled={!isAiConnected || !aiChatInput.trim() || isAiProcessing}
                                                className="send-button"
                                                title="Send message (Enter to send)"
                                            >
                                            aria-label="Send message"
                                                {isAiProcessing ? <LoaderCircle size={16} aria-hidden="true" /> : <Send size={16} aria-hidden="true" />}
                                            </button>
                                        </div>
                                        
                                        {!isAiConnected && aiConnectionStatus !== 'error' && (
                                            <div className="ai-connection-status">
                                                <div className="connection-spinner"></div>
                                                <span>Connecting to AI Video Editor...</span>
                                            </div>
                                        )}
                                    </div>
                            </FloatingChatDock>
                            )}

                            {editorTab === 'media' && (
                            <div
                                className={`assets-section ${fileDropZone === 'media' ? 'file-drop-active' : ''}`}
                                {...getFileDropProps('media')}
                            >
                                {fileDropZone === 'media' && (
                                    <div className="file-drop-overlay">Drop files to add them to this project</div>
                                )}
                                <div className="assets-header">
                                    <h3>Media</h3>
                                    {/* Both ways to get footage sit together, so
                                        "generate" is not hidden behind an empty
                                        state the user has already left. */}
                                    {!showGenerationFork && (
                                        <div className="assets-header-actions">
                                            <button
                                                className="btn btn-secondary"
                                                onClick={() => setShowGenerationStudio(true)}
                                                disabled={isSelectedWorkspacePosted}
                                                title="Turn a script into shots"
                                            >
                                                <Sparkles size={16} aria-hidden="true" /> Generate
                                            </button>
                                            <button
                                                className="btn btn-primary"
                                                onClick={handleOpenAssetModal}
                                                disabled={isSelectedWorkspacePosted}
                                            >
                                                <FolderPlus size={16} aria-hidden="true" /> Add media
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {dropUploadStatus && (
                                    <div className="assets-drop-status">{dropUploadStatus}</div>
                                )}
                                <input
                                    ref={resumeInputRef}
                                    type="file"
                                    style={{ display: 'none' }}
                                    onChange={handleResumeFilePicked}
                                    aria-hidden="true"
                                    tabIndex={-1}
                                />

                                {assets.length === 0 ? (
                                    <div className="no-assets">
                                        {showGenerationFork ? (
                                            <p>Nothing here yet. Generated shots and anything you upload land here.</p>
                                        ) : (
                                            <p>No media yet. Add clips, images or audio to start cutting.</p>
                                        )}
                                        <p className="no-assets-drop-hint">Drag files anywhere in this panel to upload them.</p>
                                    </div>
                                ) : (
                                    <div className="assets-grid">
                                        {assets.map((asset) => (
                                            <div key={asset.FileName} className="asset-card">
                                                <div className="asset-header">
                                                    <span className="asset-icon">{getFileIcon(asset.FileType)}</span>
                                                    <h4 className="asset-name">{asset.FileAlias || asset.FileName}</h4>
                                                </div>
                                                
                                                <div className="asset-info">
                                                    <p className="asset-filename">{asset.FileName}</p>
                                                    <p className="asset-type">{asset.FileType}</p>
                                                    <AssetUploadStatus
                                                        asset={asset}
                                                        entry={uploadsByKey.get(uploadKey(asset.VideoWorkspaceId, asset.FileName))}
                                                        onResume={handleResumeAsset}
                                                        onRetry={handleResumeAsset}
                                                        onCancel={handleCancelUpload}
                                                    />
                                                </div>

                                                <div className="asset-actions">
                                                    <button 
                                                        className="btn btn-info asset-preview-btn"
                                                        onClick={() => handlePreviewAsset(asset)}
                                                        title={asset.IsFinalized ? 'Preview asset' : 'Asset is still finalizing'}
                                                        disabled={!asset.IsFinalized}
                                                    >
                                                        Preview
                                                    </button>
                                                    <button
                                                        className="btn btn-success asset-download-btn"
                                                        onClick={() => handleDownloadAsset(asset)}
                                                        title={asset.IsFinalized ? 'Download asset' : 'Asset is still finalizing'}
                                                        disabled={!asset.IsFinalized}
                                                    >
                                                        Download
                                                    </button>
                                                    {asset.FileType?.startsWith('video/') && (
                                                        asset.IsFinalized && !isSelectedWorkspacePosted ? (
                                                            <details className="asset-effects">
                                                                <summary className="btn btn-primary asset-effects-summary"><Sparkles size={16} aria-hidden="true" /> Effects <ChevronDown size={14} aria-hidden="true" /></summary>
                                                                <div className="asset-effects-menu" role="menu">
                                                                    {effectMenuItems.map((item) => (
                                                                        <button
                                                                            key={item.label}
                                                                            type="button"
                                                                            role="menuitem"
                                                                            className="asset-effects-item"
                                                                            onClick={(e) => { closeEffectsMenu(e); item.fn(asset); }}
                                                                        >
                                                                            <item.Icon size={16} aria-hidden="true" />
                                                                            {item.label}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </details>
                                                        ) : (
                                                            <button
                                                                className="btn btn-primary"
                                                                disabled
                                                                title={isSelectedWorkspacePosted ? 'Project is posted' : 'Asset is still finalizing'}
                                                            >
                                                                Effects
                                                            </button>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            )}
              </>
            )}
                        </>
                    ) : (
                        <div className="no-workspace-selected">
                            <div className="empty-state">
                                <h3>{hideSidebar ? 'Linked workspace not available' : 'Select a workspace to manage assets'}</h3>
                                <p>
                                    {hideSidebar
                                        ? 'The linked workflow workspace could not be loaded.'
                                        : 'Choose a workspace from the left panel to view and manage its assets, or create a new workspace to get started.'}
                                </p>
                                {!hideSidebar && (
                                    <button 
                                        className="btn btn-primary"
                                        onClick={() => setShowCreateModal(true)}
                                    >
                                        Create New Workspace
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Workspace Modal */}
            {showCreateModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Create New Workspace</h2>
                            <button 
                                className="modal-close"
                                onClick={() => setShowCreateModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}
                        <div className="modal-content">
                            <p>Click "Create" to create a new workspace. You can edit the name and description after creation.</p>
                            <div className="modal-actions">
                                <button 
                                    className="btn btn-secondary"
                                    onClick={() => setShowCreateModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleCreateWorkspace}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Creating...' : 'Create'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Workspace Modal */}
            {showEditModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Edit Workspace</h2>
                            <button 
                                className="modal-close"
                                onClick={() => setShowEditModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}
                        <div className="modal-content">
                            {/* No name field: the project header owns the
                                one name, and renaming goes through it. */}
                            <div className="form-group">
                                <label htmlFor="workspace-prompt">Description</label>
                                <textarea
                                    id="workspace-prompt"
                                    value={editingWorkspace.Prompt}
                                    onChange={(e) => setEditingWorkspace({
                                        ...editingWorkspace,
                                        Prompt: e.target.value
                                    })}
                                    placeholder="Enter workspace description"
                                    maxLength={10000}
                                    rows={4}
                                />
                            </div>
                            <div className="modal-actions">
                                <button 
                                    className="btn btn-secondary"
                                    onClick={() => setShowEditModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleUpdateWorkspace}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Asset Modal */}
            {showAssetModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Add Asset</h2>
                            <button 
                                className="modal-close"
                                onClick={() => {
                                    resetAssetForm();
                                    setShowAssetModal(false);
                                }}
                            >
                                ×
                            </button>
                        </div>
                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}
                        <div className="modal-content">
                            {/* Mode Selection */}
                            <div className="form-group">
                                <label>Asset Type</label>
                                <div className="radio-group">
                                    <label>
                                        <input
                                            type="radio"
                                            value="upload"
                                            checked={assetMode === 'upload'}
                                            onChange={(e) => setAssetMode(e.target.value)}
                                        />
                                        Upload File
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            value="generate"
                                            checked={assetMode === 'generate'}
                                            onChange={(e) => setAssetMode(e.target.value)}
                                        />
                                        Generate with AI
                                    </label>
                                </div>
                            </div>

                            {assetMode === 'upload' ? (
                                <>
                                    {/* One file input for any number of files. The per-file
                                        settings below appear only once more than one is
                                        chosen, so the common case stays a single field. */}
                                    <div className="form-group">
                                        <label htmlFor="asset-file">Choose files</label>
                                        <input
                                            id="asset-file"
                                            type="file"
                                            accept={ALL_SUPPORTED_FORMATS}
                                            multiple
                                            onChange={(e) => handleAssetFileSelection(e.target.files)}
                                        />
                                        <small>
                                            Select several at once to upload a batch. Supported formats: {SUPPORTED_FORMATS_TEXT.ALL}
                                        </small>
                                    </div>

                                    {uploadMode === 'single' ? (
                                        <>
                                            {/* File Preview */}
                                            {newAsset.file && (
                                                <div className="form-group">
                                                    <label>Selected File Preview</label>
                                                    <div className="file-preview">
                                                        <div className="file-info">
                                                            <span className="file-icon">{getFileIcon(newAsset.file.type || `file/${getFileCategory(newAsset.file.name)}`)}</span>
                                                            <div className="file-details">
                                                                <strong>{newAsset.file.name}</strong>
                                                                <div className="file-meta">
                                                                    <span className="file-size">{((newAsset.file?.size || 0) / (1024 * 1024)).toFixed(2)} MB</span>
                                                                    <span className="file-category">{getFileCategory(newAsset.file?.name || '').toUpperCase()}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            <div className="form-group">
                                                <label htmlFor="asset-alias">Display Name</label>
                                                <input
                                                    id="asset-alias"
                                                    type="text"
                                                    value={newAsset.alias}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setNewAsset({
                                                            ...newAsset,
                                                            alias: value
                                                        });
                                                        
                                                        // Real-time validation
                                                        if (value) {
                                                            const validation = validateFilename(value);
                                                            setAliasError(validation.error);
                                                        } else {
                                                            setAliasError('');
                                                        }
                                                    }}
                                                    placeholder="Enter a display name for the asset"
                                                    maxLength={200}
                                                    className={aliasError ? 'error' : ''}
                                                />
                                                {aliasError && <small className="error-text">{aliasError}</small>}
                                                <small>Only letters (a-z, A-Z), numbers (0-9), and hyphens (-) allowed. Leave empty to auto-generate from the filename.</small>
                                            </div>
                                        </>
                                    ) : (
                                        /* Multi-file Upload Interface */
                                        <div className="multi-file-upload">
                                            {multiFileStep === 2 && (
                                                <>
                                                    <h3>Name your {fileConfigurations.length} files</h3>
                                                    <p className="form-hint">Leave a name blank to use the filename.</p>
                                                    <div className="file-configurations">
                                                        {fileConfigurations.map((config, index) => (
                                                            <div key={index} className="file-config-item">
                                                                <div className="file-header">
                                                                    <span className="file-icon">{getFileIcon(config.file.type || `file/${getFileCategory(config.file.name)}`)}</span>
                                                                    <strong>{config.file?.name || 'Unknown file'}</strong>
                                                                    <span className="file-size">({((config.file?.size || 0) / (1024 * 1024)).toFixed(2)} MB)</span>
                                                                    <button
                                                                        type="button"
                                                                        className="btn-remove"
                                                                        title="Don't upload this one"
                                                                        onClick={() => {
                                                                            const remainingFiles = selectedFiles.filter((_, i) => i !== index);
                                                                            const remainingConfigs = fileConfigurations.filter((_, i) => i !== index);
                                                                            setSelectedFiles(remainingFiles);
                                                                            setFileConfigurations(remainingConfigs);
                                                                            // Dropping back to one file returns to the
                                                                            // simple form rather than a one-row list.
                                                                            if (remainingConfigs.length === 1) {
                                                                                setUploadMode('single');
                                                                                setNewAsset({ file: remainingConfigs[0].file, alias: remainingConfigs[0].alias || '' });
                                                                            }
                                                                        }}
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </div>
                                                                
                                                                <div className="config-fields">
                                                                    <div className="form-group">
                                                                        <label>Display Name</label>
                                                                        <input
                                                                            type="text"
                                                                            value={config.alias}
                                                                            onChange={(e) => updateFileConfiguration(index, 'alias', e.target.value)}
                                                                            placeholder={config.file.name}
                                                                            maxLength={200}
                                                                        />
                                                                        <small>Leave empty to auto-generate from the filename</small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}

                                            {multiFileStep === 3 && (
                                                <>
                                                    <h3>Step 3: Upload Progress</h3>
                                                    <div className="upload-progress-container">
                                                        {fileConfigurations.map((config, index) => (
                                                            <div key={index} className={`upload-item ${index < currentUploadIndex ? 'completed' : index === currentUploadIndex ? 'current' : 'pending'}`}>
                                                                <span className="file-icon">{getFileIcon(config.file.type || `file/${getFileCategory(config.file.name)}`)}</span>
                                                                <div className="file-info">
                                                                    <div className="file-name">{config.file.name}</div>
                                                                    {config.alias && config.alias.trim() && (
                                                                        <div className="display-name">Display: {config.alias}</div>
                                                                    )}
                                                                </div>
                                                                <span className="upload-status">
                                                                    {index < currentUploadIndex ? <Check size={14} aria-hidden="true" /> : index === currentUploadIndex ? <LoaderCircle size={14} aria-hidden="true" /> : <Pause size={14} aria-hidden="true" />}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="form-group">
                                        <label htmlFor="generate-prompt">AI Prompt</label>
                                        <textarea
                                            id="generate-prompt"
                                            value={generateAsset.prompt}
                                            onChange={(e) => setGenerateAsset({
                                                ...generateAsset,
                                                prompt: e.target.value
                                            })}
                                            placeholder="Describe what you want to generate..."
                                            rows={3}
                                            maxLength={1000}
                                        />
                                        <small>Describe the asset you want to generate</small>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="asset-type">Asset Type</label>
                                        <select
                                            id="asset-type"
                                            value={generateAsset.assetType}
                                            onChange={(e) => setGenerateAsset({
                                                ...generateAsset,
                                                assetType: e.target.value
                                            })}
                                        >
                                            <option value="image/jpeg">Image (JPEG)</option>
                                            <option value="video/mp4">Video (MP4)</option>
                                            <option value="audio/mpeg">Audio (MP3)</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="aspect-ratio">Aspect Ratio</label>
                                        <select
                                            id="aspect-ratio"
                                            value={generateAsset.aspectRatio}
                                            onChange={(e) => setGenerateAsset({
                                                ...generateAsset,
                                                aspectRatio: e.target.value
                                            })}
                                        >
                                            <option value="16:9">16:9 (Landscape)</option>
                                            <option value="9:16">9:16 (Portrait)</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="generate-alias">File Name (What name would you like to give the generated asset?)</label>
                                        <input
                                            id="generate-alias"
                                            type="text"
                                            value={generateAsset.alias}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setGenerateAsset({
                                                    ...generateAsset,
                                                    alias: value
                                                });
                                                
                                                // Real-time validation
                                                if (value) {
                                                    const validation = validateFilename(value);
                                                    setGenerateAliasError(validation.error);
                                                } else {
                                                    setGenerateAliasError('');
                                                }
                                            }}
                                            placeholder="Enter a file name for the asset"
                                            maxLength={200}
                                            className={generateAliasError ? 'error' : ''}
                                        />
                                        {generateAliasError && <small className="error-text">{generateAliasError}</small>}
                                        <small>Only letters (a-z, A-Z), numbers (0-9), and hyphens (-) allowed</small>
                                    </div>
                                </>
                            )}
                            
                            {uploadProgress && (
                                <div className="upload-progress">
                                    {uploadProgress}
                                </div>
                            )}

                            {bulkUploadProgress && (
                                <div className="upload-progress">
                                    {bulkUploadProgress}
                                </div>
                            )}

                            {isGeneratingAsset && (
                                <div className="upload-progress">
                                    Generating asset with AI... This may take a few moments.
                                </div>
                            )}

                            <div className="modal-actions">
                                <button 
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        resetAssetForm();
                                        setShowAssetModal(false);
                                    }}
                                    disabled={uploadProgress || isGeneratingAsset || bulkUploadProgress}
                                >
                                    Cancel
                                </button>
                                
                                {assetMode === 'upload' ? (
                                    uploadMode === 'single' ? (
                                        <button 
                                            className="btn btn-primary"
                                            onClick={handleUploadAsset}
                                            disabled={!newAsset.file || uploadProgress || aliasError}
                                        >
                                            {uploadProgress ? 'Uploading...' : 'Upload Asset'}
                                        </button>
                                    ) : (
                                        /* Multi-file upload buttons. Choosing the files IS
                                           step one now, so naming them is where this starts. */
                                        <>
                                            {multiFileStep === 2 && (
                                                <button
                                                    className="btn btn-primary"
                                                    onClick={() => {
                                                        setMultiFileStep(3);
                                                        handleBulkUpload();
                                                    }}
                                                    disabled={validateFileConfigurations().length > 0}
                                                >
                                                    Upload {fileConfigurations.length} files
                                                </button>
                                            )}
                                            
                                            {multiFileStep === 3 && (
                                                <button 
                                                    className="btn btn-primary"
                                                    onClick={() => {
                                                        resetAssetForm();
                                                        setShowAssetModal(false);
                                                    }}
                                                    disabled={bulkUploadProgress}
                                                >
                                                    {bulkUploadProgress ? 'Uploading...' : 'Close'}
                                                </button>
                                            )}
                                        </>
                                    )
                                ) : (
                                    <button 
                                        className="btn btn-primary"
                                        onClick={handleGenerateAsset}
                                        disabled={!generateAsset.prompt || isGeneratingAsset || generateAliasError}
                                    >
                                        {isGeneratingAsset ? 'Generating...' : 'Generate Asset'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Asset Preview Modal */}
            {previewAsset && (
                <div className="modal-overlay">
                    <div className="modal modal-large">
                        <div className="modal-header">
                            <h2>Preview: {previewAsset.FileAlias || previewAsset.FileName}</h2>
                            <button 
                                className="modal-close"
                                onClick={() => setPreviewAsset(null)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-content">
                            <div className="asset-preview">
                                {renderAssetPreview(previewAsset)}
                            </div>
                            <div className="asset-details">
                                <p><strong>File Name:</strong> {previewAsset.FileName}</p>
                                <p><strong>File Type:</strong> {previewAsset.FileType}</p>
                                <p><strong>Display Name:</strong> {previewAsset.FileAlias || 'None'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Text Overlay Editor Modal */}
            {textEditorAsset && (
                <div className="modal-overlay">
                    <div className="modal modal-large">
                        <div className="modal-header">
                            <h2>Add Text: {textEditorAsset.FileAlias || textEditorAsset.FileName}</h2>
                            <button className="modal-close" onClick={() => setTextEditorAsset(null)}>×</button>
                        </div>
                        <div className="modal-content">
                            <TextBlockEditor
                                videoWorkspaceId={textEditorAsset.VideoWorkspaceId}
                                frameUrl={textEditorAsset.url}
                                sourceFileName={textEditorAsset.FileName}
                                aiSuggestion={textEditorAsset.aiSuggestion}
                                onApplied={() => {
                                    setTextEditorAsset(null);
                                    if (selectedWorkspace) {
                                        checkProcessingStatus(selectedWorkspace.VideoWorkspaceId);
                                    }
                                }}
                                onClose={() => setTextEditorAsset(null)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Box Overlay Editor Modal */}
            {boxEditorAsset && (
                <div className="modal-overlay">
                    <div className="modal modal-large">
                        <div className="modal-header">
                            <h2>Add Box: {boxEditorAsset.FileAlias || boxEditorAsset.FileName}</h2>
                            <button className="modal-close" onClick={() => setBoxEditorAsset(null)}>×</button>
                        </div>
                        <div className="modal-content">
                            <BoxBlockEditor
                                videoWorkspaceId={boxEditorAsset.VideoWorkspaceId}
                                frameUrl={boxEditorAsset.url}
                                sourceFileName={boxEditorAsset.FileName}
                                aiSuggestion={boxEditorAsset.aiSuggestion}
                                onApplied={() => {
                                    setBoxEditorAsset(null);
                                    if (selectedWorkspace) {
                                        checkProcessingStatus(selectedWorkspace.VideoWorkspaceId);
                                    }
                                }}
                                onClose={() => setBoxEditorAsset(null)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Crop Editor Modal */}
            {cropEditorAsset && (
                <div className="modal-overlay">
                    <div className="modal modal-large">
                        <div className="modal-header">
                            <h2>Crop: {cropEditorAsset.FileAlias || cropEditorAsset.FileName}</h2>
                            <button className="modal-close" onClick={() => setCropEditorAsset(null)}>×</button>
                        </div>
                        <div className="modal-content">
                            <CropBlockEditor
                                videoWorkspaceId={cropEditorAsset.VideoWorkspaceId}
                                frameUrl={cropEditorAsset.url}
                                sourceFileName={cropEditorAsset.FileName}
                                aiSuggestion={cropEditorAsset.aiSuggestion}
                                onApplied={() => {
                                    setCropEditorAsset(null);
                                    if (selectedWorkspace) {
                                        checkProcessingStatus(selectedWorkspace.VideoWorkspaceId);
                                    }
                                }}
                                onClose={() => setCropEditorAsset(null)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Zoom Editor Modal */}
            {zoomEditorAsset && (
                <div className="modal-overlay">
                    <div className="modal modal-large">
                        <div className="modal-header">
                            <h2>Zoom: {zoomEditorAsset.FileAlias || zoomEditorAsset.FileName}</h2>
                            <button className="modal-close" onClick={() => setZoomEditorAsset(null)}>×</button>
                        </div>
                        <div className="modal-content">
                            <ZoomBlockEditor
                                videoWorkspaceId={zoomEditorAsset.VideoWorkspaceId}
                                frameUrl={zoomEditorAsset.url}
                                sourceFileName={zoomEditorAsset.FileName}
                                aiSuggestion={zoomEditorAsset.aiSuggestion}
                                onApplied={() => {
                                    setZoomEditorAsset(null);
                                    if (selectedWorkspace) {
                                        checkProcessingStatus(selectedWorkspace.VideoWorkspaceId);
                                    }
                                }}
                                onClose={() => setZoomEditorAsset(null)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Sticker Editor Modal */}
            {stickerEditorAsset && (
                <div className="modal-overlay">
                    <div className="modal modal-large">
                        <div className="modal-header">
                            <h2>Sticker: {stickerEditorAsset.FileAlias || stickerEditorAsset.FileName}</h2>
                            <button className="modal-close" onClick={() => setStickerEditorAsset(null)}>×</button>
                        </div>
                        <div className="modal-content">
                            <StickerBlockEditor
                                videoWorkspaceId={stickerEditorAsset.VideoWorkspaceId}
                                frameUrl={stickerEditorAsset.url}
                                sourceFileName={stickerEditorAsset.FileName}
                                imageAssets={(assets || []).filter((a) => a.FileType?.startsWith('image/') && a.IsFinalized)}
                                resolveAssetUrl={async (asset) => {
                                    const resolved = await resolveEditorAsset(asset);
                                    return resolved ? resolved.url : null;
                                }}
                                onApplied={() => {
                                    setStickerEditorAsset(null);
                                    if (selectedWorkspace) {
                                        checkProcessingStatus(selectedWorkspace.VideoWorkspaceId);
                                    }
                                }}
                                onClose={() => setStickerEditorAsset(null)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Superimposed Graphic Editor Modal (notification / call / location) */}
            {overlayEditorAsset && (
                <div className="modal-overlay">
                    <div className="modal modal-large">
                        <div className="modal-header">
                            <h2>Superimpose: {overlayEditorAsset.FileAlias || overlayEditorAsset.FileName}</h2>
                            <button className="modal-close" onClick={() => setOverlayEditorAsset(null)}>×</button>
                        </div>
                        <div className="modal-content">
                            <OverlayBlockEditor
                                videoWorkspaceId={overlayEditorAsset.VideoWorkspaceId}
                                frameUrl={overlayEditorAsset.url}
                                sourceFileName={overlayEditorAsset.FileName}
                                templateId={overlayEditorAsset.templateId}
                                aiSuggestion={overlayEditorAsset.aiSuggestion}
                                onApplied={() => {
                                    setOverlayEditorAsset(null);
                                    if (selectedWorkspace) {
                                        checkProcessingStatus(selectedWorkspace.VideoWorkspaceId);
                                    }
                                }}
                                onClose={() => setOverlayEditorAsset(null)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Fade Editor Modal */}
            {fadeEditorAsset && (
                <div className="modal-overlay">
                    <div className="modal modal-large">
                        <div className="modal-header">
                            <h2>Fade: {fadeEditorAsset.FileAlias || fadeEditorAsset.FileName}</h2>
                            <button className="modal-close" onClick={() => setFadeEditorAsset(null)}>×</button>
                        </div>
                        <div className="modal-content">
                            <FadeBlockEditor
                                videoWorkspaceId={fadeEditorAsset.VideoWorkspaceId}
                                frameUrl={fadeEditorAsset.url}
                                sourceFileName={fadeEditorAsset.FileName}
                                aiSuggestion={fadeEditorAsset.aiSuggestion}
                                onApplied={() => {
                                    setFadeEditorAsset(null);
                                    if (selectedWorkspace) {
                                        checkProcessingStatus(selectedWorkspace.VideoWorkspaceId);
                                    }
                                }}
                                onClose={() => setFadeEditorAsset(null)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Flip Editor Modal */}
            {flipEditorAsset && (
                <div className="modal-overlay">
                    <div className="modal modal-large">
                        <div className="modal-header">
                            <h2>Flip: {flipEditorAsset.FileAlias || flipEditorAsset.FileName}</h2>
                            <button className="modal-close" onClick={() => setFlipEditorAsset(null)}>×</button>
                        </div>
                        <div className="modal-content">
                            <FlipBlockEditor
                                videoWorkspaceId={flipEditorAsset.VideoWorkspaceId}
                                frameUrl={flipEditorAsset.url}
                                sourceFileName={flipEditorAsset.FileName}
                                aiSuggestion={flipEditorAsset.aiSuggestion}
                                onApplied={() => {
                                    setFlipEditorAsset(null);
                                    if (selectedWorkspace) {
                                        checkProcessingStatus(selectedWorkspace.VideoWorkspaceId);
                                    }
                                }}
                                onClose={() => setFlipEditorAsset(null)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Color Look Editor Modal */}
            {lookEditorAsset && (
                <div className="modal-overlay">
                    <div className="modal modal-large">
                        <div className="modal-header">
                            <h2>Color: {lookEditorAsset.FileAlias || lookEditorAsset.FileName}</h2>
                            <button className="modal-close" onClick={() => setLookEditorAsset(null)}>×</button>
                        </div>
                        <div className="modal-content">
                            <LookBlockEditor
                                videoWorkspaceId={lookEditorAsset.VideoWorkspaceId}
                                frameUrl={lookEditorAsset.url}
                                sourceFileName={lookEditorAsset.FileName}
                                aiSuggestion={lookEditorAsset.aiSuggestion}
                                onApplied={() => {
                                    setLookEditorAsset(null);
                                    if (selectedWorkspace) {
                                        checkProcessingStatus(selectedWorkspace.VideoWorkspaceId);
                                    }
                                }}
                                onClose={() => setLookEditorAsset(null)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* AI Suggest Effects Modal (Effect Blocks phase 4) */}
            {suggestEditorAsset && (
                <div className="modal-overlay">
                    <div className="modal modal-large">
                        <div className="modal-header">
                            <h2>Suggest effects: {suggestEditorAsset.FileAlias || suggestEditorAsset.FileName}</h2>
                            <button className="modal-close" onClick={() => setSuggestEditorAsset(null)}>×</button>
                        </div>
                        <div className="modal-content">
                            <SuggestPanel
                                videoWorkspaceId={suggestEditorAsset.VideoWorkspaceId}
                                frameUrl={suggestEditorAsset.url}
                                sourceFileName={suggestEditorAsset.FileName}
                                onPick={openSuggestionInEditor}
                                onClose={() => setSuggestEditorAsset(null)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Process Video Modal */}
            {showProcessModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>AI Video Processing</h2>
                            <button 
                                className="modal-close"
                                onClick={() => setShowProcessModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}
                        <div className="modal-content">
                            <div className="ai-processing-info">
                                <p><strong>New AI-Powered Processing!</strong></p>
                                <p>Describe what you want in natural language, and our AI will understand and process your video intelligently. You'll get real-time feedback and can ask for adjustments!</p>
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="process-prompt">What would you like to create?</label>
                                <textarea
                                    id="process-prompt"
                                    value={processPrompt}
                                    onChange={(e) => setProcessPrompt(e.target.value)}
                                    placeholder="Try something like: 'Create a 30-second highlight video with my clips, add upbeat background music, and include smooth transitions. Make it engaging for social media!' The AI will handle the technical details."
                                    rows={4}
                                    maxLength={10000}
                                />
                                <small>{processPrompt.length}/10000 characters</small>
                            </div>

                            <div className="example-prompts-section">
                                <p><strong>Example prompts:</strong></p>
                                <div className="quick-examples">
                                    <button 
                                        type="button"
                                        className="quick-example-btn"
                                        onClick={() => setProcessPrompt("Create a professional intro video using my assets with smooth transitions and background music")}
                                    >
                                        Professional intro video
                                    </button>
                                    <button 
                                        type="button"
                                        className="quick-example-btn"
                                        onClick={() => setProcessPrompt("Make a fun social media video with quick cuts, text overlays, and energetic music")}
                                    >
                                        Social media video
                                    </button>
                                    <button 
                                        type="button"
                                        className="quick-example-btn"
                                        onClick={() => setProcessPrompt("Combine my clips into a story with fade transitions and ambient background music")}
                                    >
                                        Story-style video
                                    </button>
                                </div>
                            </div>

                            <div className="assets-summary">
                                <h4>Assets available:</h4>
                                <ul>
                                    {assets.map((asset) => (
                                        <li key={asset.FileName}>
                                            {getFileIcon(asset.FileType)} {asset.FileAlias || asset.FileName}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="modal-actions">
                                <button 
                                    className="btn btn-secondary"
                                    onClick={() => setShowProcessModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleProcessVideo}
                                    disabled={!processPrompt.trim()}
                                >
                                    Start AI Processing
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Output Modal */}
            {showOutputModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Upload a finished video</h2>
                            <button 
                                className="modal-close"
                                onClick={() => setShowOutputModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}
                        <div className="modal-content">
                            <p>A video you finished somewhere else. It becomes this project’s latest render.</p>
                            
                            <div className="form-group">
                                <label htmlFor="output-file">Video file</label>
                                <input
                                    id="output-file"
                                    type="file"
                                    accept={SUPPORTED_FORMATS.VIDEO.join(',')}
                                    onChange={(e) => setOutputFile(e.target.files[0])}
                                />
                                <small>Supported video formats: {SUPPORTED_FORMATS_TEXT.VIDEO}</small>
                            </div>
                            
                            {outputUploadProgress && (
                                <div className="upload-progress">
                                    {outputUploadProgress}
                                </div>
                            )}

                            <div className="modal-actions">
                                <button 
                                    className="btn btn-secondary"
                                    onClick={() => setShowOutputModal(false)}
                                    disabled={isUploadingOutput}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleUploadOutput}
                                    disabled={!outputFile || isUploadingOutput}
                                >
                                    {isUploadingOutput ? 'Uploading...' : 'Upload Output'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Version Selection Modal */}
            {showVersionModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Open a version</h2>
                            <button 
                                className="modal-close"
                                onClick={() => setShowVersionModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}
                        <div className="modal-content">
                            <p>Select the version to retrieve the output:</p>
                            
                            <div className="form-group">
                                <label htmlFor="version-select">Version Number</label>
                                {selectedWorkspace && (selectedWorkspace.Version > 0) ? (
                                    <select
                                        id="version-select"
                                        value={selectedVersion}
                                        onChange={(e) => setSelectedVersion(e.target.value)}
                                        className="form-control"
                                    >
                                        <option value="">Select a version...</option>
                                        {getVersionOptions(selectedWorkspace.Version).map(version => (
                                            <option key={version} value={version}>
                                                Version {version} {version === selectedWorkspace.Version - 1 ? '(Latest)' : ''}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <div className="no-versions">
                                        <p>No versions available. Publish a version first to create downloadable outputs.</p>
                                    </div>
                                )}
                                <small>Available versions: {selectedWorkspace?.Version > 0 ? `0-${selectedWorkspace.Version - 1}` : 'None'}</small>
                            </div>

                            <div className="modal-actions">
                                <button 
                                    className="btn btn-secondary"
                                    onClick={() => setShowVersionModal(false)}
                                >
                                    Cancel
                                </button>
                                {selectedWorkspace && (selectedWorkspace.Version > 0) && (
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleGetVersionOutput}
                                    disabled={!selectedVersion && selectedVersion !== '0'}
                                >
                                    Get Output
                                </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Version Comparison Modal */}
            {showDiffModal && (
                <div className="modal-overlay">
                    <div className="modal modal-large">
                        <div className="modal-header">
                            <h2>Compare versions</h2>
                            <button 
                                className="modal-close"
                                onClick={() => setShowDiffModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}
                        <div className="modal-content">
                            {!versionComparison ? (
                                <div className="version-selection">
                                    <p>Select two versions to compare:</p>
                                    
                                    {selectedWorkspace && (selectedWorkspace.Version > 1) ? (
                                        <div className="comparison-form">
                                            <div className="form-group">
                                                <label htmlFor="from-version-select">From Version</label>
                                                <select
                                                    id="from-version-select"
                                                    value={selectedFromVersion}
                                                    onChange={(e) => setSelectedFromVersion(e.target.value)}
                                                    className="form-control"
                                                >
                                                    <option value="">Select from version...</option>
                                                    {getVersionOptions(selectedWorkspace.Version).map(version => (
                                                        <option key={version} value={version}>
                                                            Version {version} {version === selectedWorkspace.Version - 1 ? '(Latest)' : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="to-version-select">To Version</label>
                                                <select
                                                    id="to-version-select"
                                                    value={selectedToVersion}
                                                    onChange={(e) => setSelectedToVersion(e.target.value)}
                                                    className="form-control"
                                                >
                                                    <option value="">Select to version...</option>
                                                    {getVersionOptions(selectedWorkspace.Version).map(version => (
                                                        <option key={version} value={version}>
                                                            Version {version} {version === selectedWorkspace.Version - 1 ? '(Latest)' : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            
                                            <small>Available versions: {selectedWorkspace?.Version > 0 ? `0-${selectedWorkspace.Version - 1}` : 'None'}</small>
                                        </div>
                                    ) : (
                                        <div className="no-versions-compare">
                                            <p>You need at least 2 versions to perform a comparison.</p>
                                            <p>Available versions: {selectedWorkspace?.Version > 0 ? `0-${selectedWorkspace.Version - 1}` : 'None'}</p>
                                            <p>Publish more versions to enable comparison.</p>
                                        </div>
                                    )}

                                    {isStreamingComparison && (
                                        <div className="loading-indicator">
                                            <div className="spinner"></div>
                                            {isStreamingComparison ? (
                                                <div className="streaming-progress">
                                                    <div className="progress-info">
                                                        <span>{streamingStatus}</span>
                                                        <span className="progress-percent">{streamingProgress}%</span>
                                                    </div>
                                                    <div className="progress-bar">
                                                        <div 
                                                            className="progress-fill" 
                                                            style={{ width: `${streamingProgress}%` }}
                                                        ></div>
                                                    </div>
                                                    {streamedChanges.length > 0 && (
                                                        <div className="changes-detected">
                                                            <small>{streamedChanges.length} changes detected so far...</small>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span>Loading comparison...</span>
                                            )}
                                        </div>
                                    )}

                                    <div className="modal-actions">
                                        <button 
                                            className="btn btn-secondary"
                                            onClick={() => setShowDiffModal(false)}
                                            disabled={isStreamingComparison}
                                        >
                                            {isStreamingComparison ? 'Close' : 'Cancel'}
                                        </button>
                                        {selectedWorkspace && (selectedWorkspace.Version > 1) && (
                                            <button 
                                                className="btn btn-primary"
                                                onClick={handleCompareVersions}
                                                disabled={!selectedFromVersion || !selectedToVersion || selectedFromVersion === selectedToVersion || isStreamingComparison}
                                            >
                                                {isStreamingComparison ? 
                                                    'Streaming...' : 
                                                    'Stream Differences'
                                                }
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="version-comparison">
                                    <div className="comparison-header">
                                        <h3>Comparison Results</h3>
                                        <button 
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => {
                                                setVersionComparison(null);
                                                setSelectedFromVersion('');
                                                setSelectedToVersion('');
                                            }}
                                        >
                                            <ArrowLeft size={14} aria-hidden="true" /> Back to Selection
                                        </button>
                                    </div>

                                    <div className="comparison-summary">
                                        <h4>
                                            Version {versionComparison.FromVersion.Version} → Version {versionComparison.ToVersion.Version}
                                            {versionComparison.StreamingComparison && (
                                                <span className="streaming-badge">Real-time Analysis</span>
                                            )}
                                        </h4>
                                        <div className={`changes-status ${versionComparison.HasChanges ? 'has-changes' : 'no-changes'}`}>
                                            {versionComparison.HasChanges ? 
                                                `${versionComparison.TotalChanges} Change${versionComparison.TotalChanges !== 1 ? 's' : ''} Detected` : 
                                                'No Changes Detected'
                                            }
                                        </div>
                                        
                                        {/* View mode toggle */}
                                        <div className="view-mode-toggle">
                                            <button 
                                                className={`toggle-btn ${diffViewMode === 'side-by-side' ? 'active' : ''}`}
                                                onClick={() => setDiffViewMode('side-by-side')}
                                            >
                                                Side by Side
                                            </button>
                                            <button 
                                                className={`toggle-btn ${diffViewMode === 'timeline-diff' ? 'active' : ''}`}
                                                onClick={() => setDiffViewMode('timeline-diff')}
                                                disabled={!versionComparison.HasChanges}
                                                title={!versionComparison.HasChanges ? 'No changes to show in timeline view' : 'Timeline diff view with synchronized playback'}
                                            >
                                                Timeline Diff
                                            </button>
                                        </div>
                                    </div>

                                    <div className="comparison-details">
                                        {diffViewMode === 'side-by-side' ? (
                                            <>
                                                <div className="version-comparison-grid">
                                                    <div className="version-column">
                                                        <h4>Version {versionComparison.FromVersion.Version}</h4>
                                                        <div className="version-info">
                                                            <p><strong>Prompt:</strong></p>
                                                            <div className="prompt-display">
                                                                {versionComparison.FromVersion.Prompt || 'No prompt'}
                                                            </div>
                                                        </div>
                                                        <div className="video-preview">
                                                            <h5>Rendered video</h5>
                                                            <video 
                                                                src={versionComparison.FromVersion.OutputUrl}
                                                                controls
                                                                className="comparison-video"
                                                            />
                                                            <a 
                                                                href={versionComparison.FromVersion.OutputUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-info btn-sm"
                                                            >
                                                                Open Full Video
                                                            </a>
                                                        </div>
                                                    </div>

                                                    <div className="version-separator">
                                                        <div className="separator-line"></div>
                                                        <div className="separator-icon">🆚</div>
                                                        <div className="separator-line"></div>
                                                    </div>

                                                    <div className="version-column">
                                                        <h4>Version {versionComparison.ToVersion.Version}</h4>
                                                        <div className="version-info">
                                                            <p><strong>Prompt:</strong></p>
                                                            <div className={`prompt-display ${versionComparison.PromptChanged ? 'changed' : ''}`}>
                                                                {versionComparison.ToVersion.Prompt || 'No prompt'}
                                                                {versionComparison.PromptChanged && <span className="change-indicator"><Pencil size={12} aria-hidden="true" /> prompt changed</span>}
                                                            </div>
                                                        </div>
                                                        <div className="video-preview">
                                                            <h5>Rendered video</h5>
                                                            <video 
                                                                src={versionComparison.ToVersion.OutputUrl}
                                                                controls
                                                                className="comparison-video"
                                                            />
                                                            <a 
                                                                href={versionComparison.ToVersion.OutputUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-info btn-sm"
                                                            >
                                                                Open Full Video
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {/* Changes Summary */}
                                                {versionComparison.Changes && versionComparison.Changes.length > 0 && (
                                                    <div className="changes-summary">
                                                        <h4>Detected Changes ({versionComparison.TotalChanges})</h4>
                                                        <div className="changes-list">
                                                            {versionComparison.Changes.map((change, index) => (
                                                                <div key={index} className={`change-item change-${change.ChangeType?.toLowerCase()}`}>
                                                                    <div className="change-header">
                                                                        <span className={`change-type ${change.ChangeType?.toLowerCase()}`}>
                                                                            {change.ChangeType}
                                                                        </span>
                                                                        <span className="change-time">
                                                                            {change.StartTime}s - {change.EndTime}s
                                                                        </span>
                                                                        <span className={`change-severity ${change.Severity?.toLowerCase()}`}>
                                                                            {change.Severity}
                                                                        </span>
                                                                    </div>
                                                                    <div className="change-description">
                                                                        {change.Description}
                                                                    </div>
                                                                    {change.Confidence && (
                                                                        <div className="change-confidence">
                                                                            Confidence: {Math.round(change.Confidence * 100)}%
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            renderTimelineDiff()
                                        )}
                                    </div>

                                    <div className="modal-actions">
                                        <button 
                                            className="btn btn-secondary"
                                            onClick={() => setShowDiffModal(false)}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Comments Modal */}
            {showCommentsModal && (
                <div className="modal-overlay">
                    <div className="modal modal-large">
                        <div className="modal-header">
                            <h2>Comments</h2>
                            <button 
                                className="modal-close"
                                onClick={() => setShowCommentsModal(false)}
                                title="Close"
                            >
                            aria-label="Close"
                                <X size={16} aria-hidden="true" />
                            </button>
                        </div>
                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}
                        {/* Fills the modal body so the comment list scrolls
                            inside it rather than the whole modal. */}
                        <div className="modal-content modal-content-fill">
                            {selectedWorkspace && (
                                <div className="comments-container">
                                    <h3>{projectName || 'Workspace'} - Comments</h3>
                                    
                                    <div className="comments-layout">
                                        {/* Video Preview Section */}
                                        <div className="video-preview-section">
                                            <div className="video-preview-container">
                                                {isLoadingCommentVideo ? (
                                                    <div className="loading-placeholder">
                                                        <div className="loading">Loading video...</div>
                                                    </div>
                                                ) : commentVideoUrl ? (
                                                    <div className="video-with-controls">
                                                        <video
                                                            ref={setCommentVideoRef}
                                                            src={commentVideoUrl}
                                                            controls
                                                            preload="metadata"
                                                            onTimeUpdate={handleCommentVideoTimeUpdate}
                                                            onLoadedMetadata={handleCommentVideoLoadedMetadata}
                                                            onPlay={handleCommentVideoPlay}
                                                            onPause={handleCommentVideoPause}
                                                            onError={handleCommentVideoError}
                                                            onCanPlay={handleCommentVideoCanPlay}
                                                            style={{ width: '100%', maxHeight: '300px' }}
                                                        />
                                                        
                                                        {/* Video Timeline with Comment Markers */}
                                                        <div className="video-timeline">
                                                            <div 
                                                                className="timeline-bar"
                                                                ref={timelineRef}
                                                                onMouseDown={handleTimelineMouseDown}
                                                                onMouseMove={handleTimelineMouseMove}
                                                                onMouseUp={handleTimelineMouseUp}
                                                                onClick={handleTimelineClick}
                                                                style={{ cursor: editingComment ? 'not-allowed' : 'pointer' }}
                                                            >
                                                                <div 
                                                                    className="timeline-progress"
                                                                    style={{ 
                                                                        width: `${commentVideoDuration > 0 ? (commentVideoCurrentTime / commentVideoDuration) * 100 : 0}%` 
                                                                    }}
                                                                />
                                                                
                                                                {/* Timeline selection overlay */}
                                                                {renderTimelineSelection()}
                                                                
                                                                {/* Comment markers on timeline */}
                                                                {comments.map(comment => {
                                                                    const startPercent = commentVideoDuration > 0 ? (comment.VideoTimestamp / commentVideoDuration) * 100 : 0;
                                                                    const endPercent = comment.VideoEndTimestamp && comment.VideoEndTimestamp !== comment.VideoTimestamp 
                                                                        ? (comment.VideoEndTimestamp / commentVideoDuration) * 100 
                                                                        : startPercent + 0.5; // Small width for point comments
                                                                    const width = Math.max(0.5, endPercent - startPercent); // Minimum width for visibility
                                                                    
                                                                    return (
                                                                        <div
                                                                            key={comment.CommentId}
                                                                            className="comment-marker"
                                                                            style={{
                                                                                left: `${startPercent}%`,
                                                                                width: `${width}%`,
                                                                                minWidth: '2px'
                                                                            }}
                                                                            title={`v${comment.Version !== undefined ? comment.Version : 0} - Comment ${formatVideoTime(comment.VideoTimestamp)}${comment.VideoEndTimestamp && comment.VideoEndTimestamp !== comment.VideoTimestamp ? ` - ${formatVideoTime(comment.VideoEndTimestamp)}` : ''}: ${comment.Comment.substring(0, 50)}${comment.Comment.length > 50 ? '...' : ''}`}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                seekToCommentTime(comment.VideoTimestamp);
                                                                            }}
                                                                        />
                                                                    );
                                                                })}
                                                            </div>
                                                            <div className="timeline-info">
                                                                <span>{formatVideoTime(commentVideoCurrentTime)}</span>
                                                                <span>{formatVideoTime(commentVideoDuration)}</span>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="video-info">
                                                            <small>
                                                                Current time: {(commentVideoCurrentTime || 0).toFixed(1)}s
                                                                {!isCommentVideoPlaying && " (Paused - ready to add comment at this time)"}
                                                                {isCommentVideoPlaying && " (Playing - pause to capture timestamp)"}
                                                            </small>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="no-video-placeholder">
                                                        <p>No video available for comments</p>
                                                        <small>
                                                            {selectedWorkspace && selectedWorkspace.CurrentVersion > 0 
                                                                ? "Loading video..." 
                                                                : "Process a video first to enable commenting"
                                                            }
                                                        </small>
                                                        {error && (
                                                            <div className="error-message" style={{ marginTop: '8px', color: 'var(--sb-marker-text)' }}>
                                                                {error}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* The compose form sits with the video it
                                                timestamps, so the comment list gets the
                                                whole right column instead of the sliver
                                                left under a ~470px form. */}
                                            <div className="comment-form">
                                                <h4>{editingComment ? 'Edit Comment' : 'Add New Comment'}</h4>
                                                {!editingComment && commentVideoUrl && (
                                                    <div className="form-instruction">
                                                        <small><strong>Tip:</strong> Pause the video at any moment to automatically capture that timestamp for your comment</small>
                                                    </div>
                                                )}
                                                <div className="form-group">
                                                    <label>Comment:</label>
                                                    <textarea
                                                        value={newComment.comment}
                                                        onChange={(e) => setNewComment({
                                                            ...newComment,
                                                            comment: e.target.value
                                                        })}
                                                        placeholder="Enter your comment here..."
                                                        rows="3"
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Timestamps:</label>
                                                    {/* Timeline Selection Display */}
                                                    <div className="timeline-selection-info" style={{ 
                                                        background: 'var(--sb-g2)', 
                                                        padding: '12px', 
                                                        borderRadius: 'var(--sb-corner)', 
                                                        marginBottom: '16px',
                                                        border: '1px solid #dee2e6'
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                            <strong style={{ color: 'var(--sb-ink-muted)' }}>Comment Time Range</strong>
                                                            {(timelineSelection.start !== null || newComment.videoTimestamp > 0 || newComment.videoEndTimestamp > 0) && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-secondary"
                                                                    onClick={clearTimelineSelection}
                                                                    title="Clear selection and reset times"
                                                                >
                                                                    Clear
                                                                </button>
                                                            )}
                                                        </div>
                                                        
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                                            <div>
                                                                <span style={{ fontSize: '0.9em', color: 'var(--sb-ink-muted)' }}>Start:</span>
                                                                <strong style={{ marginLeft: '6px', color: 'var(--sb-blue)' }}>
                                                                    {formatVideoTime(newComment.videoTimestamp || 0)}
                                                                </strong>
                                                                <span style={{ marginLeft: '4px', fontSize: '0.8em', color: 'var(--sb-ink-muted)' }}>
                                                                    ({(newComment.videoTimestamp || 0).toFixed(1)}s)
                                                                </span>
                                                            </div>
                                                            
                                                            <div>
                                                                <span style={{ fontSize: '0.9em', color: 'var(--sb-ink-muted)' }}>End:</span>
                                                                <strong style={{ marginLeft: '6px', color: 'var(--sb-blue)' }}>
                                                                    {formatVideoTime(newComment.videoEndTimestamp || 0)}
                                                                </strong>
                                                                <span style={{ marginLeft: '4px', fontSize: '0.8em', color: 'var(--sb-ink-muted)' }}>
                                                                    ({(newComment.videoEndTimestamp || 0).toFixed(1)}s)
                                                                </span>
                                                            </div>
                                                            
                                                            <div>
                                                                <span style={{ fontSize: '0.9em', color: 'var(--sb-ink-muted)' }}>Duration:</span>
                                                                <strong style={{ marginLeft: '6px', color: 'var(--sb-good)' }}>
                                                                    {formatVideoTime((newComment.videoEndTimestamp || 0) - (newComment.videoTimestamp || 0))}
                                                                </strong>
                                                                <span style={{ marginLeft: '4px', fontSize: '0.8em', color: 'var(--sb-ink-muted)' }}>
                                                                    ({((newComment.videoEndTimestamp || 0) - (newComment.videoTimestamp || 0)).toFixed(1)}s)
                                                                </span>
                                                            </div>
                                                        </div>
                                                        
                                                        {newComment.videoEndTimestamp < newComment.videoTimestamp && (
                                                            <div style={{ marginTop: '8px', color: 'var(--sb-marker-text)', fontSize: '0.9em' }}>
                                                                End time must be after start time
                                                            </div>
                                                        )}
                                                        
                                                        <div style={{ marginTop: '8px', fontSize: '0.85em', color: 'var(--sb-ink-muted)' }}>
                                                            Drag on the timeline above to select a time range for your comment
                                                        </div>
                                                    </div>

                                                    {/* Manual timestamp adjustment (collapsed by default) */}
                                                    <details style={{ marginBottom: '16px' }}>
                                                        <summary style={{ cursor: 'pointer', fontSize: '0.9em', color: 'var(--sb-ink-muted)', marginBottom: '8px' }}>
                                                            Manual Time Adjustment
                                                        </summary>
                                                        <div className="timestamp-input-group">
                                                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                                                <label style={{ fontSize: '0.9em', marginBottom: '4px' }}>Start Time (MM:SS.XX):</label>
                                                                <input
                                                                    type="text"
                                                                    value={timeInputFormat.start}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setTimeInputFormat(prev => ({ ...prev, start: value }));
                                                                        if (validateTimeFormat(value)) {
                                                                            const seconds = timeFormatToSeconds(value);
                                                                            setNewComment(prev => ({
                                                                                ...prev,
                                                                                videoTimestamp: seconds
                                                                            }));
                                                                        }
                                                                    }}
                                                                    onBlur={(e) => {
                                                                        if (!validateTimeFormat(e.target.value)) {
                                                                            setTimeInputFormat(prev => ({
                                                                                ...prev,
                                                                                start: secondsToTimeFormat(newComment.videoTimestamp)
                                                                            }));
                                                                        }
                                                                    }}
                                                                    placeholder="0:00.00"
                                                                    style={{ 
                                                                        borderColor: validateTimeFormat(timeInputFormat.start) ? '#ced4da' : '#dc3545',
                                                                        backgroundColor: validateTimeFormat(timeInputFormat.start) ? 'white' : '#fff5f5'
                                                                    }}
                                                                />
                                                                {!validateTimeFormat(timeInputFormat.start) && (
                                                                    <small style={{ color: 'var(--sb-marker-text)', fontSize: '0.8em' }}>
                                                                        Format: MM:SS.XX (e.g., 1:23.45)
                                                                    </small>
                                                                )}
                                                            </div>
                                                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                                                <label style={{ fontSize: '0.9em', marginBottom: '4px' }}>End Time (MM:SS.XX):</label>
                                                                <input
                                                                    type="text"
                                                                    value={timeInputFormat.end}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setTimeInputFormat(prev => ({ ...prev, end: value }));
                                                                        if (validateTimeFormat(value)) {
                                                                            const seconds = timeFormatToSeconds(value);
                                                                            setNewComment(prev => ({
                                                                                ...prev,
                                                                                videoEndTimestamp: seconds
                                                                            }));
                                                                        }
                                                                    }}
                                                                    onBlur={(e) => {
                                                                        if (!validateTimeFormat(e.target.value)) {
                                                                            setTimeInputFormat(prev => ({
                                                                                ...prev,
                                                                                end: secondsToTimeFormat(newComment.videoEndTimestamp)
                                                                            }));
                                                                        }
                                                                    }}
                                                                    placeholder="0:00.00"
                                                                    style={{ 
                                                                        borderColor: validateTimeFormat(timeInputFormat.end) ? '#ced4da' : '#dc3545',
                                                                        backgroundColor: validateTimeFormat(timeInputFormat.end) ? 'white' : '#fff5f5'
                                                                    }}
                                                                />
                                                                {!validateTimeFormat(timeInputFormat.end) && (
                                                                    <small style={{ color: 'var(--sb-marker-text)', fontSize: '0.8em' }}>
                                                                        Format: MM:SS.XX (e.g., 1:23.45)
                                                                    </small>
                                                                )}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-secondary"
                                                                onClick={() => {
                                                                    if (commentVideoRef && commentVideoRef.currentTime !== undefined) {
                                                                        const currentTime = parseFloat(commentVideoRef.currentTime.toFixed(1));
                                                                        setNewComment(prev => ({
                                                                            ...prev,
                                                                            videoTimestamp: currentTime,
                                                                            videoEndTimestamp: Math.max(currentTime, prev.videoEndTimestamp)
                                                                        }));
                                                                        setTimeInputFormat(prev => ({
                                                                            ...prev,
                                                                            start: secondsToTimeFormat(currentTime),
                                                                            end: secondsToTimeFormat(Math.max(currentTime, newComment.videoEndTimestamp))
                                                                        }));
                                                                    }
                                                                }}
                                                                disabled={!commentVideoRef}
                                                                title="Set start time to current video position"
                                                            >
                                                                Start
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-secondary"
                                                                onClick={() => {
                                                                    if (commentVideoRef && commentVideoRef.currentTime !== undefined) {
                                                                        const currentTime = parseFloat(commentVideoRef.currentTime.toFixed(1));
                                                                        setNewComment(prev => ({
                                                                            ...prev,
                                                                            videoEndTimestamp: currentTime
                                                                        }));
                                                                        setTimeInputFormat(prev => ({
                                                                            ...prev,
                                                                            end: secondsToTimeFormat(currentTime)
                                                                        }));
                                                                    }
                                                                }}
                                                                disabled={!commentVideoRef}
                                                                title="Set end time to current video position"
                                                            >
                                                                End
                                                            </button>
                                                        </div>
                                                    </details>
                                                </div>
                                                <div className="form-actions">
                                                    <button 
                                                        className="btn btn-primary"
                                                        onClick={handleAddComment}
                                                        disabled={!newComment.comment.trim() || newComment.videoEndTimestamp < newComment.videoTimestamp}
                                                    >
                                                        {editingComment ? 'Update Comment' : 'Add Comment'}
                                                    </button>
                                                    {editingComment && (
                                                        <button 
                                                            className="btn btn-secondary"
                                                            onClick={handleCancelEditComment}
                                                        >
                                                            Cancel
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Comments List */}
                                        <div className="comments-section">
                                            <div className="comments-list">
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                                    <h4 style={{ margin: 0 }}>Comments ({comments.length})</h4>
                                                    {comments.length > 0 && (
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={handleCopyCommentsToClipboard}
                                                            title="Copy all comments to clipboard"
                                                        >
                                                            Copy All
                                                        </button>
                                                    )}
                                                </div>
                                                {isLoadingComments ? (
                                                    <div className="loading">Loading comments...</div>
                                                ) : comments.length === 0 ? (
                                                    <div className="no-comments">No comments yet. Pause the video and add your first comment!</div>
                                                ) : (
                                                    <div className="comments">
                                                        {comments
                                                            .sort((a, b) => a.VideoTimestamp - b.VideoTimestamp)
                                                            .map(comment => (
                                                            <div key={comment.CommentId} className="comment-item">
                                                                <div className="comment-header">
                                                                    <div className="comment-user-info">
                                                                        {comment.PhotoUrl ? (
                                                                            <img 
                                                                                src={comment.PhotoUrl} 
                                                                                alt={comment.UserName || 'User'} 
                                                                                className="comment-user-photo"
                                                                                onError={(e) => {
                                                                                    e.target.style.display = 'none';
                                                                                    e.target.nextElementSibling.style.display = 'flex';
                                                                                }}
                                                                            />
                                                                        ) : null}
                                                                        <div 
                                                                            className="comment-user-avatar-fallback"
                                                                            style={{ display: comment.PhotoUrl ? 'none' : 'flex' }}
                                                                        >
                                                                            {(comment.UserName || 'A').charAt(0).toUpperCase()}
                                                                        </div>
                                                                        <div className="comment-user-details">
                                                                            <span className="comment-username">
                                                                                {comment.UserName || 'Anonymous User'}
                                                                            </span>
                                                                            <button
                                                                                className="comment-timestamp-btn"
                                                                                onClick={() => seekToCommentTime(comment.VideoTimestamp)}
                                                                                disabled={!commentVideoRef}
                                                                                title="Jump to start time in video"
                                                                            >
                                                                                {formatVideoTime(comment.VideoTimestamp)}
                                                                                {comment.VideoEndTimestamp && comment.VideoEndTimestamp !== comment.VideoTimestamp && (
                                                                                    <span> - {formatVideoTime(comment.VideoEndTimestamp)}</span>
                                                                                )}
                                                                            </button>
                                                                            {comment.VideoEndTimestamp && comment.VideoEndTimestamp !== comment.VideoTimestamp && (
                                                                                <button
                                                                                    className="comment-timestamp-btn"
                                                                                    onClick={() => seekToCommentTime(comment.VideoEndTimestamp)}
                                                                                    disabled={!commentVideoRef}
                                                                                    title="Jump to end time in video"
                                                                                    style={{ marginLeft: '4px', fontSize: '0.75em' }}
                                                                                >
                                                                                    End
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="comment-meta-right">
                                                                        <span className="comment-version" style={{ 
                                                                            fontSize: '0.8em', 
                                                                            backgroundColor: 'var(--sb-g3)', 
                                                                            color: 'var(--sb-ink-muted)', 
                                                                            padding: '2px 6px', 
                                                                            borderRadius: 'var(--sb-corner)', 
                                                                            marginRight: '8px',
                                                                            fontWeight: '500'
                                                                        }}>
                                                                            v{comment.Version !== undefined ? comment.Version : 0}
                                                                        </span>
                                                                        <span className={`comment-state ${comment.State?.toLowerCase()}`}>
                                                                            {comment.State || 'Open'}
                                                                        </span>
                                                                        <span className="comment-date">
                                                                            {comment.CreatedDateTime && new Date(comment.CreatedDateTime).toLocaleDateString()}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="comment-content">
                                                                    {comment.Comment}
                                                                </div>
                                                                <div className="comment-actions">
                                                                    <button 
                                                                        className="btn btn-sm btn-secondary"
                                                                        onClick={() => handleEditComment(comment)}
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                    <button 
                                                                        className="btn btn-sm btn-danger"
                                                                        onClick={() => handleDeleteComment(comment.CommentId)}
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-actions">
                            <button 
                                className="btn btn-secondary"
                                onClick={() => setShowCommentsModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// buildScriptFromTimeline is exported for its unit test: it is the single point
// where a hand-made timeline becomes something the executor will run, so its
// output has to stay valid against the DSL compiler.
export { VideoWorkspace, buildScriptFromTimeline, createManualTimelineGraph };
